import {ItemDefinition, makeOptions, makeOptionsFromArray} from "./utils.js";
import {COUNTRIES} from "../lists/countries.js";
import {PROCESSING_OPTIONS} from "../lists/misc";
import {
    anySUPRMU,
    buildSafeGuardsOptions,
    computeHomogeneousFate,
    computeHomogeneousKeepData,
    computeHomogeneousSampleFate,
    computeHomogeneousVariable,
    DATASET_VARIABLES,
    getDatasetName,
    getNamesForTransfersLoop,
    getNumberOfDatasets,
    getNumberOfParties,
    getPartyName,
    GLOBAL_VARIABLES_DEFAULT_FALSE,
    isRespNotInPRSU,
    listOfTransferNotProcessing10Parties,
    listOfTransferProcessing101112Parties,
    PARTY_VARIABLES,
    THREE_DIMENSIONAL_VARIABLES,
    TWO_DIMENSIONAL_VARIABLES,
    uniqueDatatypes,
    uniqueDatatypesForParty,
    uniqueDataTypesForPartyText,
} from "./gdpr_flow_utils.js";
import {MAP_ROLES, uniqueDatatypesReceivedByParty} from "./gdpr_flow_utils";
import dish_template from "../../static/resources/Dawid_ANNEX_DISH_2020-09-15.docx";
import use_conditions_template from "../../static/resources/Dawid_ANNEX_Use-conditions_v1_Aug2021.pdf";
import service_template from "../../static/resources/Dawid_ANNEX_Service_2021-04-22.docx"

const pop = new ItemDefinition({
    id: 'pop',
    title: "Scenario",
    group: 'Purpose',
    fields: [
        {
            label: "What is the purpose of the processing?",
            explanation: "Choose from the dropdown menu the scenario relevant for the data sharing",
            type: "dropdown",
            options: [
                {key: "pop1", text: "Defined joint research project", value: "JP"},
                {
                    key: "pop2",
                    text: "Open collaboration framework, i.e. covering several projects",
                    value: "CA"
                },
                {
                    key: "pop3",
                    text: "Data user data access or download from data hub",
                    value: "AP"
                },
                {
                    key: "pop4",
                    text: "Data sharing with independent data user for his project (no collaboration)",
                    value: "OP"
                },
                {
                    key: "pop5",
                    text: "Making data available for secondary use through data hub",
                    value: "DH"
                },
                {
                    key: "pop6",
                    text: "Service provision only",
                    value: "SP"
                },
            ]
        }
    ],
    chooseNext: function (states, globals) {
        const {values} = states[this.id];
        const pop = values[0];
        globals['POP'] = pop;
        // if pop equals DH or SP we don't need to ask the project name
        // if pop equals DH we can infer number of parties = 2
        if (pop === 'DH') {
            states[numberOfParties.id] = {values: ["2"]};
            init_variables(globals, 2);
            return partyDetails;
        }
        if (pop === 'SP') {
            return numberOfParties;
        }
        return projectName;
    }
});
const projectName = new ItemDefinition({
    id: 'projectName',
    title: "Project",
    group: 'Purpose',
    fields: [
        {
            label: "What is the title of the project?",
            type: "text"
        },
        {
            label: "What is the acronym (or short name) of the project?",
            type: "text",
            optional: true
        }
    ],
    chooseNext: function (states, globals) {
        for (let i = 0; i < GLOBAL_VARIABLES_DEFAULT_FALSE.length; i++) {
            globals[GLOBAL_VARIABLES_DEFAULT_FALSE[i]] = false
        }
        return numberOfParties;
    }
});

function init_variables(globals, nbParties) {
    for (let i = 0; i < PARTY_VARIABLES.length; i++) {
        const variableName = PARTY_VARIABLES[i];
        if (globals[variableName] === undefined || globals[variableName] === null) {
            // the variable doesn't exist, we create it
            globals[variableName] = Array(nbParties);
        } else {
            // if we increased the number of parties, we extend the arrays
            for (let l = globals[variableName].length; l < nbParties; l++) {
                globals[variableName].push();
            }
        }
    }
}

export const numberOfParties = new ItemDefinition({
    id: 'numberOfParties',
    title: "Parties",
    group: 'Parties',
    fields: [
        {
            label: "How many parties are involved?",
            type: "number",
            min: 2,
            allowPreliminary: true
        }
    ],
    chooseNext: function (states, globals) {
        const nbParties = getNumberOfParties(states);
        init_variables(globals, nbParties);
        return partyDetails;
    }
});
export const partyDetails = new ItemDefinition({
    id: 'partyDetails',
    loopName: "parties_info_loop",
    title: "Party details",
    group: 'Parties',
    fields: [
        {
            label: "Name of institution",
            type: "text",
            optional: false
        },
        {
            label: "Short name of institution",
            type: "text",
            optional: false
        },
        {
            label: "Organisational form",
            type: "dropdown",
            optional: false,
            options: [
                {key: "partyDetails-organisation-pu", text: "Public organisation", value: 'pu'},
                {key: "partyDetails-organisation-pnp", text: "Private non-profit organisation", value: 'pnp'},
                {key: "partyDetails-organisation-pfp", text: "Private for-profit organisation", value: 'pfp'},
                {key: "partyDetails-organisation-io", text: "International organisation", value: 'io'},
                {key: "partyDetails-organisation-other", text: "Other", value: 'oth'},
            ]
        },
        {
            label: "Trade register location (if applicable)",
            type: "text",
            optional: true, //FIXME
        },
        {
            label: "Trade register number (if applicable)",
            type: "text",
            optional: true, //FIXME
        },
        {
            label: "Address of institution",
            type: "text",
            optional: true, //FIXME
        },
        {
            label: "Country of institution",
            type: "dropdown",
            options: makeOptions("partyDetails", COUNTRIES),
        },
        {
            label: "Name of scientific responsible",
            type: "text",
            optional: true, //FIXME
        },
        {
            label: "Email of scientific responsible",
            type: "text",
            optional: true, //FIXME
        }, // TODO add optional additional contacts
        {
            label: "Name of legal responsible",
            type: "text",
            optional: true, //FIXME
        },
        {
            label: "Email of legal responsible",
            type: "text",
            optional: true, //FIXME
        },
        {
            label: "Position of legal responsible",
            type: "text",
            optional: true, //FIXME
        },
        {
            label: "DPO applicable?",
            type: "yesno",
            optional: false,
            defaultValue: 1
        }
    ],
    allowApplyAll: false,
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        // if (!globals['Recipients'][loopIndex - 1]) {
        //     globals['Recipients'][loopIndex - 1] = [];
        // }
        if (!globals['Processing'][loopIndex - 1]) {
            globals['Processing'][loopIndex - 1] = Array(PROCESSING_OPTIONS.length);
        }
        if (!globals['Processing_resp'][loopIndex - 1]) {
            globals['Processing_resp'][loopIndex - 1] = Array(PROCESSING_OPTIONS.length);
        }
        if (!globals['Processing_string'][loopIndex - 1]) {
            globals['Processing_string'][loopIndex - 1] = Array(PROCESSING_OPTIONS.length);
        }
        if (!globals['Resp'][loopIndex - 1]) {
            globals['Resp'][loopIndex - 1] = {};
        }
        if (!globals['HasRole'][loopIndex - 1]) {
            globals['HasRole'][loopIndex - 1] = {"G": false, "P": false, "R": false, "A": false};
        }
        const dpo = states[partyDetails.id].values[12] === 1;
        if (dpo) {
            return partyDPODetails;
        } else {
            return partyDetailsNextDecision;
        }
    },
});

const partyDPODetails = new ItemDefinition({
    id: 'partyDPODetails',
    loopName: "parties_info_loop",
    title: "Party DPO details",
    group: 'Parties',
    allowApplyAll: false,
    buildFields: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const partyName = getPartyName(loopIndex - 1, loops);
        return [
            {
                label: "Name of DPO for '" + partyName + "'",
                type: "text",
                optional: true,
            },
            {
                label: "Email of DPO for '" + partyName + "'",
                type: "text",
                optional: false,
            },
            {
                label: "Phone number of DPO for '" + partyName + "'",
                type: "text",
                optional: true,
            },
        ]
    },
    chooseNext: function () {
        return partyDetailsNextDecision;
    },
});
export const partyDetailsNextDecision = new ItemDefinition({
    id: 'partyDetailsNextDecision',
    loopName: "parties_info_loop",
    title: "partyDetailsNextDecision",
    group: 'Parties',
    isDecisionNode: true,
    chooseNext: function (states, globals, loops) {
        const organization = states[partyDetails.id].values[2];
        if (organization === 'oth') {
            return partyOrganisationOther;
        } else {
            return partyDecisionAPOPDH;
        }
    },
});
export const partyRolesNextDecision = new ItemDefinition({
    id: 'partyRolesNextDecision',
    loopName: "parties_info_loop",
    title: "partyRolesNextDecision",
    group: 'Parties',
    isDecisionNode: true,
    chooseNext: function (states, globals, loops) {
        const organization = states[partyDetails.id].values[2];
        const internationalOrganisation = organization === 'io';
        if (internationalOrganisation === true) {
            return partyIOAD;
        } else {
            return partyEuCountry;
        }
    },
});

const partyOrganisationOther = new ItemDefinition({
    id: 'partyOrganisationOther',
    loopName: 'parties_info_loop',
    title: 'Organisation form - other',
    group: 'Parties',
    fields: [
        {
            label: "Please specify the organisation form",
            type: 'text'
        }
    ],
    chooseNext: function () {
        return partyDecisionAPOPDH;
    }
});

const partyIOAD = new ItemDefinition({
    id: 'partyIOAD',
    loopName: "parties_info_loop",
    title: "International Organisation - AD",
    isDecisionNode: true,
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const countryCode = states[partyDetails.id].values[6];
        const isAD = COUNTRIES[countryCode].AD;
        globals['IC'][loopIndex - 1] = isAD ? 'AD' : 'OT';
        return partyDecisionRoleRorGNotEU;
    }
});
const partyEuCountry = new ItemDefinition({
    id: 'partyEuCountry',
    loopName: "parties_info_loop",
    title: "EEA",
    group: 'Parties',
    isDecisionNode: true,
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const countryCode = states[partyDetails.id].values[6];
        const isEu = COUNTRIES[countryCode].EEA;
        if (isEu) {
            const isPrivate = states[partyDetails.id].values[2] === 'pfp' || states[partyDetails.id].values[2] === 'pnp';
            globals['IC'][loopIndex - 1] = 'EEA';
            if (isPrivate) {
                return partyEuSubs;
            } else {
                return partyDecisionRoleRorGNotEU;
            }
        }
        return partyADCountry;
    }
});
const partyEuSubs = new ItemDefinition({
    id: 'partyEuSubs',
    loopName: "parties_info_loop",
    title: "EEA Subsidiaries",
    group: 'Parties',
    buildFields: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const partyName = getPartyName(loopIndex - 1, loops);
        return [
            {
                label: "Does '" + partyName + "' have subsidiaries outside the EEA that will be" +
                    " involved in the processing?",
                type: "yesno"
            }
        ]
    },
    chooseNext: function (states) {
        const euSubsidiaries = states[partyEuSubs.id].values[0];
        if (euSubsidiaries === 1) {
            return partyEuSubsCountriesList;

        } else {
            return partyDecisionRoleRorGNotEU;
        }
    }
});
const partyEuSubsCountriesList = new ItemDefinition({
    id: 'partyEuSubsCountriesList',
    loopName: "parties_info_loop",
    title: "EEA Subsidiaries List",
    group: 'Parties',
    fields: [
        {
            label: "Select all countries outside of the EEA with subsidiaries involved in the processing",
            type: "dropdown",
            options: makeOptions("partyEuSubsCountriesList", COUNTRIES, (country) => (!country.EEA)),
            multiple: true
        }
    ],
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const subsidiariesCountries = states[partyEuSubsCountriesList.id].values[0];
        globals['SubIC'][loopIndex - 1] = Array(subsidiariesCountries.length);


        let allAD = true;
        let allBCR = true;
        for (let i = 0; i < subsidiariesCountries.length; i++) {
            const countryCode = subsidiariesCountries[i];
            if (COUNTRIES[countryCode].AD) {
                globals['SubIC'][loopIndex - 1][i] = 'AD';
                continue;
            }
            allAD = false;
            if (COUNTRIES[countryCode].BCR) {
                globals['SubIC'][loopIndex - 1][i] = 'CB';
                continue;
            }
            allBCR = false;
            globals['SubIC'][loopIndex - 1][i] = 'OT';
        }

        return partyDecisionRoleRorGNotEU;
    }
});
const partyADCountry = new ItemDefinition({
    id: 'partyADCountry',
    loopName: "parties_info_loop",
    title: "AD",
    group: 'Parties',
    isDecisionNode: true,
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const countryCode = states[partyDetails.id].values[6];
        let isAD = COUNTRIES[countryCode].AD;
        // if country is JAPAN, we have AD only for private for profit
        if (countryCode === 'JP') {
            const organization = states[partyDetails.id].values[2];
            isAD = organization === 'pfp';
        } else if (countryCode === 'CA') {
            if (states[partyDetails.id].values[2] === 'pfp') {
                return partyCAPipeda;
            } else {
                isAD = false;
            }
        }
        if (isAD) {
            globals['IC'][loopIndex - 1] = 'AD';
        } else {
            globals['IC'][loopIndex - 1] = 'OT';
        }
        return partyDecisionRoleRorGNotEU;
    }
});
const partyCAPipeda = new ItemDefinition({
    id: 'partyCAPipeda',
    loopName: "parties_info_loop",
    title: "PIPEDA",
    group: 'Parties',
    fields: [
        {
            type: 'yesno',
            label: 'Does the company adheres to "PIPEDA"?'
        }
    ],
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const isPipeda = states[partyCAPipeda.id].values[0] === 1;
        if (isPipeda) {
            globals['IC'][loopIndex - 1] = 'AD';
        } else {
            globals['IC'][loopIndex - 1] = 'OT';
        }
        return partyDecisionRoleRorGNotEU;
    }
});
const partyDecisionAPOPDH = new ItemDefinition({
    id: 'partyDecisionAPOPDH',
    title: "partyDecisionAPOPDH",
    group: 'Parties',
    loopName: "parties_info_loop",
    isDecisionNode: true,
    chooseNext: function (states, globals) {
        const pop = globals['POP'];
        if (['DH', 'AP', 'OP'].indexOf(pop) > -1) {
            return partyRolePR;
        } else {
            return partyRolePGR;
        }
    }
});

function validateDataProviderRule(globals, states, loopIndex, hasDataProviderRole, hasAnotherRole) {
    // we always need to have 1 party as data provider and one party as something else #11
    const nbParties = getNumberOfParties(states);
    if (loopIndex !== nbParties) {
        return [];
    }
    let hasAtLeastOneDataProvider = false;
    let hasAtLeastOneWithAnotherRole = false;
    for (let i = 0; i < nbParties - 1; i++) {
        const hasPRole = globals['HasRole'][i]['P'];
        hasAtLeastOneDataProvider = hasAtLeastOneDataProvider || hasPRole;
        const hasAnotherRole = globals['HasRole'][i]['G'] || globals['HasRole'][i]['R'] || globals['HasRole'][i]['A'];
        hasAtLeastOneWithAnotherRole = hasAtLeastOneWithAnotherRole || hasAnotherRole;
        if (hasAtLeastOneDataProvider && hasAtLeastOneWithAnotherRole) {
            return [];
        }
    }
    hasAtLeastOneDataProvider = hasAtLeastOneDataProvider || hasDataProviderRole;
    hasAtLeastOneWithAnotherRole = hasAtLeastOneWithAnotherRole || hasAnotherRole;
    if (hasAtLeastOneDataProvider && hasAtLeastOneWithAnotherRole) {
        return []
    } else {
        return [{
            field: 0,
            text: 'We need to have at least one party' +
                ' as data provider and one party with a different role.'
        }]
    }
}

function validateProcessorRule(globals, states, loopIndex) {
    // in SP, we always need to have 1 party as processor and one party as non-processor #11
    if (globals['POP'] !== 'SP') {
        return [];
    }
    const nbParties = getNumberOfParties(states);
    if (loopIndex !== nbParties) {
        return [];
    }
    let hasAtLeastOneProcessor = false;
    let hasAtLeastOneWithAnotherResponsibility = false;
    for (let i = 0; i < nbParties; i++) {
        const hasPRResponsibility = globals['Resp'][i]['P'] === 'PR' || globals['Resp'][i]['G'] === 'PR'
            || globals['Resp'][i]['A'] === 'PR' || globals['Resp'][i]['R'] === 'PR';
        hasAtLeastOneProcessor = hasAtLeastOneProcessor || hasPRResponsibility;
        const hasAnotherResponsibility = (globals['Resp'][i]['P'] && globals['Resp'][i]['P'] !== 'PR')
            || (globals['Resp'][i]['G'] && globals['Resp'][i]['G'] !== 'PR')
            || (globals['Resp'][i]['A'] && globals['Resp'][i]['A'] !== 'PR')
            || (globals['Resp'][i]['R'] && globals['Resp'][i]['R'] !== 'PR');
        hasAtLeastOneWithAnotherResponsibility = hasAtLeastOneWithAnotherResponsibility || hasAnotherResponsibility;
        if (hasAtLeastOneProcessor && hasAtLeastOneWithAnotherResponsibility) {
            return [];
        }

    }
    return [{
        text: 'If purpose of processing is "Service provision only", we need to have at least one party' +
            ' with a processor responsibility and one party with a different responsibility.'
    }]
}

const partyRolePR = new ItemDefinition({
    id: 'partyRolePR',
    loopName: "parties_info_loop",
    title: "Role",
    group: 'Parties',
    allowApplyAll: false,
    buildFields: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const partyName = getPartyName(loopIndex - 1, loops);
        const options = [
            {key: "rolePR-p", text: "Data provider", value: 'P'},
            {key: "rolePR-r", text: "Data recipient", value: 'R'}
        ];
        // see #102, data access option should not appear if pop = DH
        if (globals['POP'] !== 'DH') {
            options.push({key: "rolePR-a", text: "Access data without downloading", value: 'A'});
        }
        return [
            {
                label: "What is the role of '" + partyName + "'?",
                type: "radio",
                options
            }
        ]
    },
    validate: function (states, globals, loops) {
        const hasDataProviderRole = states[partyRolePR.id].values[0] === 'P';
        const hasAnotherRole = !hasDataProviderRole;
        const loopIndex = this.getLoopIndex(loops);
        this.errors = validateDataProviderRule(globals, states, loopIndex, hasDataProviderRole, hasAnotherRole);
    },
    chooseNext: function (states, globals, loops) { //TODO change flow.md
        const loopIndex = this.getLoopIndex(loops);
        globals['HasRole'][loopIndex - 1] = {"G": false, "P": false, "R": false, "A": false};
        globals['Resp'][loopIndex - 1] = {};
        const {values} = states[partyRolePR.id];
        const role = values[0];
        const pop = globals['POP'];
        globals['HasRole'][loopIndex - 1][role] = true;
        if (pop !== 'DH' || role === 'P') {
            globals['Resp'][loopIndex - 1] = {[role]: 'CO'};
            return partyRolesNextDecision;
        } else {
            return partyRoleRorA;
        }
    }
});
const partyRoleRorA = new ItemDefinition({
    id: 'partyRoleRorA',
    loopName: "parties_info_loop",
    title: "Responsibility",
    group: 'Parties',
    allowApplyAll: false,
    buildFields: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const partyName = getPartyName(loopIndex - 1, loops);
        return [
            {
                label: "What is the responsibility of '" + partyName + "'?",
                type: "radio",
                options: [
                    {key: "roleRorA-jc", text: "Joint controller", value: 'JC'},
                    {key: "roleRorA-pr", text: "Processor", value: 'PR'},
                    {key: "roleRorA-mu", text: "In multiple roles", value: 'MU'},
                ]
            }
        ]
    },
    chooseNext: function (states, globals, loops) { //TODO change flow.md
        const loopIndex = this.getLoopIndex(loops);
        const {values} = states[partyRoleRorA.id];
        const role = states[partyRolePR.id].values[0];
        const resp = values[0];
        globals['Resp'][loopIndex - 1] = {[role]: resp};
        return partyRolesNextDecision;
    }
});
const partyDecisionRoleRorGNotEU = new ItemDefinition({
    id: 'partyDecisionRoleRorGNotEU',
    title: "partyDecisionRoleRorGNotEEA",
    group: 'Parties',
    loopName: "parties_info_loop",
    isDecisionNode: true,
    buildErrors: function (states, globals, loops) {
        return validateProcessorRule(globals, states, this.getLoopIndex(loops));

    },
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        if (globals['HasRole'][loopIndex - 1]['R'] === true || globals['HasRole'][loopIndex - 1]['G'] === true || globals['HasRole'][loopIndex - 1]['A'] === true) {
            if (globals['IC'][loopIndex - 1] !== 'EEA') {
                if (!('InatShare' in globals)) {
                    globals['InatShare'] = {};
                    globals['InatShare'][globals['IC'][loopIndex - 1]] = true;
                }
            }
            if (globals['IC'][loopIndex - 1] === 'OT' || globals['SubIC'][loopIndex - 1] === 'OT') {
                return partyDecisionSafeguards;
            }
        }
        return partyProcessingDecision;
    }
});

const partyRolePGR = new ItemDefinition({
    id: 'partyRolePGR',
    loopName: "parties_info_loop",
    title: "Role",
    group: 'Parties',
    allowApplyAll: false,
    buildFields: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const partyName = getPartyName(loopIndex - 1, loops);
        let options = [
            {key: "rolePR-p", text: "Data provider", value: 'P'},
            {key: "rolePR-g", text: "Data generator", value: 'G'},
            {key: "rolePR-r", text: "Data recipient", value: 'R'},
            {key: "rolePR-a", text: "Access data without downloading", value: 'A'},
        ];

        return [

            {
                label: "What are the roles of '" + partyName + "'?",
                type: "dropdown",
                multiple: true,
                options: options,
                allowPreliminary: true
            }
        ]
    },
    validate: function (states, globals, loops) {
        const values = states[partyRolePGR.id].values;
        const hasDataProviderRole = values[0].indexOf('P') !== -1;
        const hasAnotherRole = !hasDataProviderRole || values[0].length > 1;
        const loopIndex = this.getLoopIndex(loops);
        this.errors = validateDataProviderRule(globals, states, loopIndex, hasDataProviderRole, hasAnotherRole);
    },
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        globals['HasRole'][loopIndex - 1] = {"G": false, "P": false, "R": false, "A": false};
        globals['Resp'][loopIndex - 1] = {};
        const {values} = states[partyRolePGR.id];
        if (values[0].indexOf('P') > -1) {
            globals['HasRole'][loopIndex - 1]['P'] = true;
            return partyRolePGRRespP;
        }
        return partyRolePGRR2;
    }
});

const partyRolePGRRespP = new ItemDefinition({
    id: 'partyRolePGRRespP',
    loopName: "parties_info_loop",
    title: "Responsibility as provider",
    group: 'Parties',
    allowApplyAll: false,
    buildFields: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const partyName = getPartyName(loopIndex - 1, loops);
        const pop = globals['POP'];
        const options = [
            {key: "partyRolePGRRespP-co", text: "Controller", value: 'CO'},
            {key: "partyRolePGRRespP-jc", text: "Joint controller", value: 'JC'},
            {key: "partyRolePGRRespP-pr", text: "Processor", value: 'PR'},
        ];
        if (pop === 'SP') {
            options.push({key: "partyRolePGRRespP-prs", text: "Processor issuing subprocessing", value: 'PRS'});
            options.push({key: "partyRolePGRRespP-su", text: "Sub-processor", value: 'SU'});
        } else {
            options.push({key: "partyRolePGRRespP-mu", text: "Multiple Roles", value: 'MU'});
        }
        return [

            {
                label: "What is the responsibility of '" + partyName + "' as a provider?",
                type: "radio",
                options,
                allowPreliminary: true
            }
        ]
    },
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {values} = states[partyRolePGRRespP.id];
        let resp = values[0];
        if (resp === 'PRS') {
            resp = 'PR';
        }
        globals['Resp'][loopIndex - 1]['P'] = resp;
        return partyRolePGRR2;
    },
});
const partyRolePGRR2 = new ItemDefinition({
    id: 'partyRolePGRR2',
    loopName: "parties_info_loop",
    title: "Generator?",
    group: 'Parties',
    isDecisionNode: true,
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {values} = states[partyRolePGR.id];
        if (values[0].indexOf('G') > -1) {
            globals['HasRole'][loopIndex - 1]['G'] = true;
            return partyRolePGRRespG;
        }
        return partyRolePGRR3;
    }
});
const partyRolePGRRespG = new ItemDefinition({
    id: 'partyRolePGRRespG',
    loopName: "parties_info_loop",
    title: "Responsibility as generator",
    group: 'Parties',
    allowApplyAll: false,
    buildFields: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const partyName = getPartyName(loopIndex - 1, loops);
        const pop = globals['POP'];
        const options = [
            {key: "partyRolePGRRespG-co", text: "Controller", value: 'CO'},
            {key: "partyRolePGRRespG-jc", text: "Joint controller", value: 'JC'},
            {key: "partyRolePGRRespG-pr", text: "Processor", value: 'PR'},
        ];
        if (pop === 'SP') {
            options.push({key: "partyRolePGRRespG-prs", text: "Processor issuing subprocessing", value: 'PRS'});
            options.push({key: "partyRolePGRRespG-su", text: "Sub-processor", value: 'SU'});
        } else {
            options.push({key: "partyRolePGRRespG-mu", text: "Multiple Roles", value: 'MU'});
        }
        return [

            {
                label: "What is the responsibility of '" + partyName + "' as a generator?",
                type: "radio",
                options
            }
        ]
    },
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {values} = states[partyRolePGRRespG.id];
        let resp = values[0];
        if (resp === 'PRS') {
            resp = 'PR';
        }
        globals['Resp'][loopIndex - 1]['G'] = resp;
        return partyRolePGRR3;
    }
});
const partyRolePGRR3 = new ItemDefinition({
    id: 'partyRolePGRR3',
    loopName: "parties_info_loop",
    title: "Access only?",
    group: 'Parties',
    isDecisionNode: true,
    allowApplyAll: false,
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {values} = states[partyRolePGR.id];
        if (values[0].indexOf('A') > -1) {
            globals['HasRole'][loopIndex - 1]['A'] = true;
            return partyRolePGRRespA;
        }
        return partyRolePGRR4;
    }
});
const partyRolePGRRespA = new ItemDefinition({
    id: 'partyRolePGRRespA',
    loopName: "parties_info_loop",
    title: "Responsibility for data access",
    group: 'Parties',
    allowApplyAll: false,
    buildFields: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const partyName = getPartyName(loopIndex - 1, loops);
        const pop = globals['POP'];
        const options = [
            {key: "partyRolePGRRespA-co", text: "Controller", value: 'CO'},
            {key: "partyRolePGRRespA-jc", text: "Joint controller", value: 'JC'},
            {key: "partyRolePGRRespA-pr", text: "Processor", value: 'PR'},
        ];
        if (pop === 'SP') {
            options.push({key: "partyRolePGRRespA-prs", text: "Processor issuing subprocessing", value: 'PRS'});
            options.push({key: "partyRolePGRRespA-su", text: "Sub-processor", value: 'SU'});
        } else {
            options.push({key: "partyRolePGRRespA-mu", text: "Multiple Roles", value: 'MU'});
        }
        return [
            {
                label: "What is the responsibility of '" + partyName + "' for its access to data?",
                type: "radio",
                options
            }
        ]
    },
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {values} = states[partyRolePGRRespA.id];
        let resp = values[0];
        if (resp === 'PRS') {
            resp = 'PR';
        }
        globals['Resp'][loopIndex - 1]['A'] = resp;
        return partyRolePGRR4;
    }
});
const partyRolePGRR4 = new ItemDefinition({
    id: 'partyRolePGRR4',
    loopName: "parties_info_loop",
    title: "Recipient?",
    group: 'Parties',
    allowApplyAll: false,
    isDecisionNode: true,
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {values} = states[partyRolePGR.id];
        if (values[0].indexOf('R') > -1) {
            globals['HasRole'][loopIndex - 1]['R'] = true;
            return partyRolePGRRespR;
        }
        return partyRolesNextDecision;
    }
});
const partyRolePGRRespR = new ItemDefinition({
    id: 'partyRolePGRRespR',
    loopName: "parties_info_loop",
    title: "Responsibility as recipient",
    group: 'Parties',
    allowApplyAll: false,
    buildFields: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const partyName = getPartyName(loopIndex - 1, loops);
        const pop = globals['POP'];
        const options = [
            {key: "partyRolePGRRespR-co", text: "Controller", value: 'CO'},
            {key: "partyRolePGRRespR-jc", text: "Joint controller", value: 'JC'},
            {key: "partyRolePGRRespR-pr", text: "Processor", value: 'PR'},
        ];
        if (pop === 'SP') {
            options.push({key: "partyRolePGRRespR-prs", text: "Processor issuing subprocessing", value: 'PRS'});
            options.push({key: "partyRolePGRRespR-su", text: "Sub-processor", value: 'SU'});
        } else {
            options.push({key: "partyRolePGRRespR-mu", text: "Multiple Roles", value: 'MU'});
        }
        return [
            {
                label: "What is the responsibility of '" + partyName + "' as a recipient?",
                type: "radio",
                options
            }
        ]
    },
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {values} = states[partyRolePGRRespR.id];
        let resp = values[0];
        if (resp === 'PRS') {
            resp = 'PR';
        }
        globals['Resp'][loopIndex - 1]['R'] = resp;
        return partyRolesNextDecision;
    },
});
const partyDecisionSafeguards = new ItemDefinition({
    id: 'partyDecisionSafeguards',
    loopName: "parties_info_loop",
    title: "Safeguards",
    group: 'Parties',

    fields: [
        {
            label: "What safeguard or derogation according to Art. 49 is used for international transfer (transmission or access)?",
            type: "dropdown",
            options: [
                {
                    key: "partyDecisionSafeguards-cc",
                    text: "Standard contractual clauses by European Commission",
                    value: 'CC'
                },
                {key: "partyDecisionSafeguards-co", text: "Based on Consent", value: 'CO'},
                {
                    key: "partyDecisionSafeguards-ii",
                    text: "Based on important reasons of public interest",
                    value: 'II'
                },
                {
                    key: "partyDecisionSafeguards-sa",
                    text: "Exceptional transfer with information of the Supervisory Authority",
                    value: 'SA'
                },
            ]
        }
    ],
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {values} = states[partyDecisionSafeguards.id];
        globals['SG'][loopIndex - 1] = {};
        globals['SG'][loopIndex - 1][values[0]] = true;
        return partyProcessingDecision;
    }
});

const partyProcessingDecision = new ItemDefinition({
    id: 'partyProcessingDecision',
    loopName: "parties_info_loop",
    title: "ProcessingDecision",
    group: 'Parties',
    isDecisionNode: true,
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const pop = globals['POP'];
        const checkRoles = ['MU', 'SU', 'PR'];
        if (
            pop === 'JP'
            || checkRoles.indexOf(globals['Resp'][loopIndex - 1]['P']) !== -1
            || checkRoles.indexOf(globals['Resp'][loopIndex - 1]['R']) !== -1
            || checkRoles.indexOf(globals['Resp'][loopIndex - 1]['G']) !== -1
        ) {
            return partyProcessingList;
        }
        if (pop === 'CA' && globals['HasRole'][loopIndex - 1]['R'] === true) {
            return partyShareOutside;
        }
        return partySecondaryDecision;
    }
});

const partyProcessingList = new ItemDefinition({
    id: 'partyProcessingList',
    title: "Scope of processing / role of party for processing",
    group: 'Parties',
    loopName: "parties_info_loop",
    allowApplyAll: false,
    buildFields: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        let options;
        const nbOfParties = getNumberOfParties(states);
        if (globals['POP'] === 'DH') {
            options = [
                {"key": partyProcessingList + "-" + 2, "value": 2, "text": 'Data hosting'},
                {"key": partyProcessingList + "-" + 4, "value": 4, "text": "Data compute services"},
                {"key": partyProcessingList + "-" + 5, "value": 5, "text": "Data curation"},
                {"key": partyProcessingList + "-" + 6, "value": 6, "text": "(Re-) Pseudonymisation"},
                {
                    "key": partyProcessingList + "-" + 10,
                    "value": 10,
                    "text": "Secondary sharing with external parties as part of the processing / project"
                },
                {"key": partyProcessingList + "-" + 11, "value": 11, "text": "Other"}
            ];
        } else {
            options = makeOptionsFromArray("partyProcessingList", PROCESSING_OPTIONS);
            if (globals['POP'] === 'JP' && nbOfParties === 2) {
                options.splice(9, 1);
            }
        }
        const partyName = getPartyName(loopIndex - 1, loops);
        return [
            {
                label: "Choose all relevant processing types for '" + partyName + "' from the list below",
                type: "dropdown",
                multiple: true,
                options,
                allowPreliminary: true
            }
        ];
    },
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        // reset first all values to false
        for (let i = 0; i < PROCESSING_OPTIONS.length; i++) {
            globals['Processing'][loopIndex - 1][i] = false;
        }
        const selectedProcessingList = states[partyProcessingList.id].values[0];
        for (let i = 0; i < selectedProcessingList.length; i++) {
            const selectedProcessing = selectedProcessingList[i];
            globals['Processing'][loopIndex - 1][selectedProcessing] = true;
        }
        if (globals['Processing'][loopIndex - 1][11] === true) {
            return partyProcessingListSpecify;
        } else {
            return partyProcessingListDecision;
        }
    }
});

const partyProcessingListDecision = new ItemDefinition({
    id: 'partyProcessingListDecision',
    loopName: "parties_info_loop",
    title: "ProcessingDecision2",
    group: 'Parties',
    isDecisionNode: true,
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);

        const respP = globals['Resp'][loopIndex - 1]['P'];
        const respG = globals['Resp'][loopIndex - 1]['G'];
        const respR = globals['Resp'][loopIndex - 1]['R'];
        const respA = globals['Resp'][loopIndex - 1]['A'];
        const uniqueResponsibilities = [...new Set([respP, respG, respR, respA])].filter(Boolean);
        // .filter to remove undefined values
        if (uniqueResponsibilities.length > 1 || uniqueResponsibilities[0] === 'MU'
        ) {
            return partyProcessingListResp;
        } else {
            const selectedProcessingList = states[partyProcessingList.id].values[0];
            globals['Processing_string'][loopIndex - 1] = Array(PROCESSING_OPTIONS.length);
            globals['Processing_resp'][loopIndex - 1] = Array(PROCESSING_OPTIONS.length);
            for (let i = 0; i < selectedProcessingList.length; i++) {
                const selectedProcessing = selectedProcessingList[i];
                globals['Processing_string'][loopIndex - 1][selectedProcessing] = PROCESSING_OPTIONS[selectedProcessing] + ' as ' + MAP_ROLES[uniqueResponsibilities[0]];
                globals['Processing_resp'][loopIndex - 1][selectedProcessing] = uniqueResponsibilities[0];
            }
            return partyEndLoop;
        }
    }
});

const partyProcessingListSpecify = new ItemDefinition({
    id: 'partyProcessingListSpecify',
    title: "Processing - other",
    group: 'Parties',
    loopName: "parties_info_loop",
    fields: [
        {
            label: "Please specify the 'other processing'",
            type: "text"
        }
    ],
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        globals['ProcessingOther'][loopIndex - 1] = states[partyProcessingListSpecify.id].values[0];
        return partyProcessingListDecision;
    }
});

const partyProcessingListResp = new ItemDefinition({
    id: 'partyProcessingListResp',
    title: "Processing responsibilities",
    group: 'Parties',
    loopName: "parties_info_loop",
    allowApplyAll: false,
    buildFields: function (states) {
        const fields = [];
        const selectedProcessingList = states[partyProcessingList.id].values[0];
        for (let i = 0; i < selectedProcessingList.length; i++) {
            const selectedProcessing = selectedProcessingList[i];
            const label = PROCESSING_OPTIONS[selectedProcessing];
            fields.push({
                label,
                type: "dropdown",
                options: [
                    {
                        key: "partyProcessingListResp" + i + "CO",
                        value: "controller",
                        text: "controller"
                    },
                    {
                        key: "partyProcessingListResp" + i + "JC",
                        value: "joint controller",
                        text: "joint controller"
                    },
                    {
                        key: "partyProcessingListResp" + i + "PR",
                        value: "processor",
                        text: "processor"
                    },
                    {
                        key: "partyProcessingListResp" + i + "SU",
                        value: "sub-processor",
                        text: "sub-processor"
                    }
                ],
                allowPreliminary: true
            });
        }
        return fields;
    },
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const selectedProcessingList = states[partyProcessingList.id].values[0];
        const {values} = states[partyProcessingListResp.id];
        const processing10 = globals['Processing'][loopIndex - 1][9];
        globals['SecondSharingInternalProcessor'][loopIndex - 1] = false;
        globals['SecondSharingExternalProcessor'][loopIndex - 1] = false;
        globals['Processing_string'][loopIndex - 1] = Array(PROCESSING_OPTIONS.length);
        globals['Processing_resp'][loopIndex - 1] = Array(PROCESSING_OPTIONS.length);
        for (let i = 0; i < selectedProcessingList.length; i++) {
            const selectedProcessing = selectedProcessingList[i];
            globals['Processing_string'][loopIndex - 1][selectedProcessing] = PROCESSING_OPTIONS[selectedProcessing] + ' as ' + values[i].toUpperCase();
            globals['Processing_resp'][loopIndex - 1][selectedProcessing] = values[i];
            if (processing10 && values[i] === 'processor') {
                globals['SecondSharingInternalProcessor'][loopIndex - 1] = true;
                globals['SecondSharingExternalProcessor'][loopIndex - 1] = true;
            }
        }
        return partyEndLoop;
    }
});
const partyShareOutside = new ItemDefinition({
    id: 'partyShareOutside',
    loopName: "parties_info_loop",
    title: "Share outside",
    group: 'Parties',
    buildFields: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const partyName = getPartyName(loopIndex - 1, loops);
        return [
            {
                label: "Will '" + partyName + "' share data with third parties outside this agreement?",
                type: "yesno"
            }
        ]
    },
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {values} = states[partyShareOutside.id];
        const shareOutside = values[0] === 1;
        if (shareOutside) {
            globals['Processing'][loopIndex - 1][10] = true;
        }
        return partySecondaryDecision;
    }
});
const partySecondaryDecision = new ItemDefinition({
    id: 'partySecondaryDecision',
    isDecisionNode: true,
    loopName: "parties_info_loop",
    title: "partySecondaryDecision",
    group: 'Parties',
    chooseNext(states, globals, loops) {
        const nbParties = getNumberOfParties(states);
        const loopIndex = this.getLoopIndex(loops);
        const pop = globals['POP'];
        if (pop === 'CA' && nbParties > 2 && globals['HasRole'][loopIndex - 1]['R'] === true) {
            return partySecondaryWithin;
        } else {
            return partyEndLoop;
        }
    }

});
const partySecondaryWithin = new ItemDefinition({
    id: 'partySecondaryWithin',
    title: "Secondary within",
    group: 'Parties',
    loopName: "parties_info_loop",
    buildFields: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const partyName = getPartyName(loopIndex - 1, loops);
        return [
            {
                label: "Will '" + partyName + "' do secondary sharing of data with other partners within the consortium?",
                type: "yesno"
            }
        ]
    },
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {values} = states[partySecondaryWithin.id];
        const secondaryWithin = values[0] === 1;
        if (secondaryWithin) {
            globals['Processing'][loopIndex - 1][9] = true;
        }
        return partyEndLoop;
    }
});


const partyEndLoop = new ItemDefinition({
    id: 'partyEndLoop',
    loopName: "parties_info_loop",
    title: "EndOfLoop",
    group: 'Parties',
    isDecisionNode: true,
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const nbParties = getNumberOfParties(states);
        if (loopIndex === nbParties) {
            return numberOfDatasets;
        } else {
            return this.nextLoopIteration(loops);
        }
    }
});//
//   DATASETS
//

export const numberOfDatasets = new ItemDefinition({
    id: 'numberOfDatasets',
    title: "Datasets",
    group: 'Cohort Definition',
    fields: [
        {
            label: "How many cohorts are involved?",
            type: "number",
            min: 1,
            allowPreliminary: true
        }
    ],
    chooseNext: function (states, globals, loops) {
        // resetLoopIndex(loops, 'dataset_info_loop');
        const nbDatasets = getNumberOfDatasets(states);
        for (let i = 0; i < DATASET_VARIABLES.length; i++) {
            const variableName = DATASET_VARIABLES[i];
            if (globals[variableName] === undefined || globals[variableName] === null) {
                // the variable doesn't exist, we create it
                globals[variableName] = Array(nbDatasets);
            } else {
                // if we increased the number of dataset, we extend the arrays
                for (let l = globals[variableName].length; l < nbDatasets; l++) {
                    globals[variableName].push();
                }
            }
        }
        for (let i = 0; i < TWO_DIMENSIONAL_VARIABLES.length; i++) {
            const variableName = TWO_DIMENSIONAL_VARIABLES[i];
            if (globals[variableName] === undefined || globals[variableName] === null) {
                // the variable doesn't exist, we create it
                globals[variableName] = Array(nbDatasets);
            } else {
                // if we increased the number of dataset, we extend the arrays
                for (let l = globals[variableName].length; l < nbDatasets; l++) {
                    globals[variableName].push();
                }
            }
        }
        for (let i = 0; i < THREE_DIMENSIONAL_VARIABLES.length; i++) {
            const variableName = THREE_DIMENSIONAL_VARIABLES[i];
            if (globals[variableName] === undefined || globals[variableName] === null) {
                // the variable doesn't exist, we create it
                globals[variableName] = Array(nbDatasets);
            } else {
                // if we increased the number of dataset, we extend the arrays
                for (let l = globals[variableName].length; l < nbDatasets; l++) {
                    globals[variableName].push();
                }
            }
        }
        return datasetDetails;
    }
});
export const datasetDetails = new ItemDefinition({
    id: 'datasetDetails',
    loopName: "dataset_info_loop",
    title: "Dataset details",
    group: 'Cohort Definition',
    allowApplyAll: false,
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {values} = states[datasetDetails.id];
        const nbParties = getNumberOfParties(states);
        const nbDatasets = getNumberOfDatasets(states);
        const pop = globals['POP'];
        for (let i = 0; i < TWO_DIMENSIONAL_VARIABLES.length; i++) {
            const variableName = TWO_DIMENSIONAL_VARIABLES[i];
            if (globals[variableName][loopIndex - 1] === undefined || globals[variableName][loopIndex - 1] === null) {
                // the variable doesn't exist, we create it
                globals[variableName][loopIndex - 1] = Array(nbParties);
            } else {
                // if we increased the number of parties, we extend the arrays
                for (let l = globals[variableName][loopIndex - 1].length; l < nbParties; l++) {
                    globals[variableName][loopIndex - 1].push();
                }
            }
        }
        for (let i = 0; i < THREE_DIMENSIONAL_VARIABLES.length; i++) {
            const variableName = THREE_DIMENSIONAL_VARIABLES[i];
            if (globals[variableName] === undefined || globals[variableName] === null) {
                // the variable doesn't exist, we create it
                globals[variableName] = Array(nbDatasets);
            }
            if (globals[variableName][loopIndex - 1] === undefined || globals[variableName][loopIndex - 1] === null) {
                // the variable doesn't exist, we create it
                globals[variableName][loopIndex - 1] = Array(nbParties);
            } else {
                // if we increased the number of parties, we extend the arrays
                for (let l = globals[variableName][loopIndex - 1].length; l < nbParties; l++) {
                    globals[variableName][loopIndex - 1].push();
                }
            }
            for (let j = 0; j < nbParties; j++) {
                if (globals[variableName][loopIndex - 1][j] === undefined || globals[variableName][loopIndex - 1][j] === null) {
                    // the variable doesn't exist, we create it
                    globals[variableName][loopIndex - 1][j] = Array(nbParties);
                } else {
                    // if we increased the number of parties, we extend the arrays
                    for (let l = globals[variableName][loopIndex - 1][j].length; l < nbParties; l++) {
                        globals[variableName][loopIndex - 1][j].push();
                    }
                }
            }
        }
        let cohortOwner = values[1];
        globals['CohortOwner'][loopIndex - 1] = cohortOwner;
        globals['CohortOwner_External'][loopIndex - 1] = false;
        if (cohortOwner === 'OT') { // Other
            globals['CohortOwner_External'][loopIndex - 1] = true;
            globals['Recruitment'][loopIndex - 1] = false;
            return datasetResponsiblePiAndInstitution;
        } else {
            globals['CohortProvider'][loopIndex - 1] = cohortOwner;
            if (['DH', 'AP', 'OP'].indexOf(pop) !== -1) {
                globals['Recruitment'][loopIndex - 1] = false;
                return datasetDatatypes;
            }
            return datasetParticipantRecruitedUnderAgreement;
        }
    },
    buildFields: function (states, globals, loops) {
        const options = [];
        const nbParties = getNumberOfParties(states);
        const pop = globals['POP'];
        // we get the names of the parties as entered in partyDetails
        options.push({key: "d-details-1", text: "not a party to this agreement", value: 'OT'});
        for (let i = 0; i < nbParties; i++) {
            // list should not contain recipients in case of OP or AP or DH, should not contain processors in SA or SU
            if (['OP', 'AP', 'DH'].indexOf(pop) !== -1 && globals['HasRole'][i]['R'] === true) {
                continue;
            }
            const partyName = getPartyName(i, loops);
            options.push({key: "d-details" + i, text: partyName, value: i});
        }
        return [
            {
                label: "Name of cohort",
                type: "text",
            },
            {
                label: "Contact point and responsible towards the data subjects for cohort",
                type: "dropdown",
                options: options
            },
        ];
    },

});

const datasetResponsiblePiAndInstitution = new ItemDefinition({
    id: 'datasetResponsiblePiAndInstitution',
    title: "Responsible contact person and institution responsible for the cohort",
    group: 'Cohort Definition',
    loopName: "dataset_info_loop",
    buildFields: function (states, globals, loops) {
        const options = [];
        const loopIndex = this.getLoopIndex(loops);
        const nbParties = getNumberOfParties(states);
        const cohortName = getDatasetName(loopIndex - 1, loops);
        const pop = globals['POP'];
        // we get the names of the parties as entered in partyDetails
        let fields = [
            {
                label: `Full name of the responsible contact person for cohort ${cohortName}`,
                type: "text"
            },
            {
                label: `Institution responsible for the cohort ${cohortName}`,
                type: "text"
            }
        ];
        if (pop !== 'DH') {
            for (let i = 0; i < nbParties; i++) {
                const partyName = getPartyName(i, loops);
                options.push({key: "datasetResponsiblePiAndInstitution" + i, text: partyName, value: i});
            }
            fields.push({
                label: `For cohort ${cohortName}, which partner brings in the data and keeps contact with the cohort owner?`,
                type: "dropdown",
                options: options
            });
        }
        return fields;
    },
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {values} = states[datasetResponsiblePiAndInstitution.id];
        const pop = globals['POP'];
        globals['CohortPI'][loopIndex - 1] = values[0];
        globals['CohortInstitution'][loopIndex - 1] = values[1];
        if (pop !== 'DH') {
            globals['CohortProvider'][loopIndex - 1] = values[2];
        } else {
            globals['CohortProvider'][loopIndex - 1] = globals['HasRole'][0]['P'] === true ? 0 : 1;
        }
        return datasetDatatypes;
    }
});
const datasetParticipantRecruitedUnderAgreement = new ItemDefinition({
        id: 'datasetParticipantRecruitedUnderAgreement',
        loopName: "dataset_info_loop",
        title: "Participants recruitment",
        group: 'Cohort Definition',
        buildFields(states, globals, loops) {
            const loopIndex = this.getLoopIndex(loops);
            const cohortName = getDatasetName(loopIndex - 1, loops);
            return [
                {
                    label: "Are the subjects of cohort '" + cohortName + "' recruited under the agreement?",
                    type: "yesno",
                    allowPreliminary: true
                }
            ]
        },
        chooseNext: function (states, globals, loops) {
            const loopIndex = this.getLoopIndex(loops);
            const recruitment = states[datasetParticipantRecruitedUnderAgreement.id].values[0];
            let isRecruitment = recruitment === 1;
            globals['Recruitment'][loopIndex - 1] = isRecruitment;
            if (isRecruitment) {
                return datasetRecruitmentBy;
            } else {
                return datasetDatatypes;
            }
        }
    }
);

const datasetRecruitmentBy = new ItemDefinition({
        id: 'datasetRecruitmentBy',
        title: "Recruitment",
        group: 'Cohort Definition',
        loopName: "dataset_info_loop",
        buildFields: function (states, globals, loops) {
            const options = [];
            const nbParties = getNumberOfParties(states);
            const loopIndex = this.getLoopIndex(loops);
            const cohortName = getDatasetName(loopIndex - 1, loops);
            // we get the names of the parties as entered in partyDetails
            for (let i = 0; i < nbParties; i++) {
                const isGenerator = globals['HasRole'][i]['G'];
                if (!isGenerator) {
                    continue
                }
                const partyName = getPartyName(i, loops);
                options.push({key: "datasetRecruitmentBy" + i, text: partyName, value: i});
            }
            if (options.length === 0) {
                // we should have least one option available
                // if empty it means that no partner had generator role
                this.errors = [{text: "No partner with generator role has been defined!"}];
                return [];
            }
            return [
                {
                    label: "Which partner(s) recruits the data subject of cohort '" + cohortName + "'?",
                    type: "dropdown",
                    options: options,
                    multiple: true
                }
            ];
        },
        chooseNext: function (states, globals, loops) {
            const loopIndex = this.getLoopIndex(loops);
            const nbDatasets = getNumberOfDatasets(states);
            const {values} = states[datasetRecruitmentBy.id];
            let hasMu = false;
            const numberOfRecruiters = values[0].length;
            for (let i = 0; i < numberOfRecruiters; i++) {
                const partyIndex = values[0][i];
                if (globals['Recruit'][partyIndex] === null || globals['Recruit'][partyIndex] === undefined) {
                    globals['Recruit'][partyIndex] = Array(nbDatasets);
                }
                globals['Recruit'][partyIndex][loopIndex - 1] = true;
                let recruitRespArrayForParty = globals['RecruitResp'][partyIndex];
                if (recruitRespArrayForParty === undefined || recruitRespArrayForParty === null) {
                    recruitRespArrayForParty = Array(nbDatasets);
                }
                if (globals['CohortOwner'][loopIndex - 1] === partyIndex) {
                    recruitRespArrayForParty[loopIndex - 1] = 'CO';
                } else {
                    let respG = globals['Resp'][partyIndex]['G'];
                    if (respG === 'MU') {
                        hasMu = true
                    } else {
                        recruitRespArrayForParty[loopIndex - 1] = respG;
                    }
                }
                globals['RecruitResp'][partyIndex] = recruitRespArrayForParty;
            }
            if (hasMu) {
                return datasetRecruitMU;
            } else {
                if (numberOfRecruiters > 1) {
                    return datasetDatatypesRecruitersSame;
                } else {
                    return datasetDatatypes;
                }
            }
        }
    })
;

const datasetRecruitMU = new ItemDefinition({
    id: 'datasetRecruitMU',
    title: "Recruitment responsibilities",
    group: 'Cohort Definition',
    loopName: "dataset_info_loop",
    buildFields: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {values} = states[datasetRecruitmentBy.id];
        const fields = [];
        const numberOfRecruiters = values[0].length;
        for (let i = 0; i < numberOfRecruiters; i++) {
            const partyIndex = values[0][i];
            if (globals['CohortOwner'][loopIndex - 1] !== partyIndex) {
                let respG = globals['Resp'][partyIndex]['G'];
                if (respG === 'MU') {
                    // we get the names of the parties as entered in partyDetails
                    const partyName = getPartyName(partyIndex, loops);
                    fields.push(
                        {
                            label: partyName,
                            type: "dropdown",
                            options: [
                                {key: "datasetRecruitMU_" + i + "PR", text: "processor", value: "PR"},
                                {key: "datasetRecruitMU_" + i + "SU", text: "sub-processor", value: "SU"},
                                {key: "datasetRecruitMU_" + i + "JC", text: "joint-controller", value: "JC"},
                            ],
                        }
                    );
                }
            }
        }
        return fields;
    },
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {values} = states[datasetRecruitmentBy.id];
        const valuesResp = states[datasetRecruitMU.id].values;
        const numberOfRecruiters = values[0].length;
        for (let i = 0; i < values[0].length; i++) {
            const partyIndex = values[0][i];
            if (globals['CohortOwner'][loopIndex - 1] !== partyIndex) {
                let respG = globals['Resp'][partyIndex]['G'];
                if (respG === 'MU') {
                    globals['RecruitResp'][partyIndex][loopIndex - 1] = valuesResp[i];
                }
            }
        }
        if (numberOfRecruiters > 1) {
            return datasetDatatypesRecruitersSame;
        } else {
            return datasetDatatypes;
        }
    }
});

const datasetDatatypesRecruitersSame = new ItemDefinition({
    id: 'datasetDatatypesRecruitersSame',
    title: "Same Datatypes",
    group: 'Cohort Definition',
    loopName: "dataset_info_loop",
    buildFields: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const cohortName = getDatasetName(loopIndex - 1, loops);
        return [{
            label: "For cohort '" + cohortName + "', are all recruiting partners recruiting study participants " +
                "collecting the same data types of these participants?",
            type: "yesno"
        }];
    },
    chooseNext: function () {
        return datasetDatatypes;

    }
});

function getAllUniqueDatatypes(globals) {
    const allDatatypes = new Set();
    for (let i = 0; i < globals['CohortData'].length; i++) {
        getAllUniqueDatatypesForCohort(globals, i, allDatatypes);
    }
    return allDatatypes;
}

function getAllUniqueDatatypesForCohort(globals, cohortIndex, allDatatypes) {
    if (globals['CohortData'][cohortIndex]) {
        globals['CohortData'][cohortIndex].forEach(item => allDatatypes.add(item));
    }
    if (globals['DataGenerated'][cohortIndex]) {
        globals['DataGenerated'][cohortIndex].forEach(partyList => partyList && partyList.forEach(item => allDatatypes.add(item)));
    }
    if (globals['DataProvided'][cohortIndex]) {
        globals['DataProvided'][cohortIndex].forEach(partyList => partyList && partyList.forEach(item => allDatatypes.add(item)));
    }
}

const datasetDatatypes = new ItemDefinition({
    id: 'datasetDatatypes',
    title: "Cohort's Datatypes",
    group: 'Cohort Data',
    loopName: "dataset_info_loop",
    buildFields: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const hasRecruitment = globals['Recruitment'][loopIndex - 1];
        const cohortName = getDatasetName(loopIndex - 1, loops);
        const fields = [];
        const allDatatypes = getAllUniqueDatatypes(globals);
        const options = Array.from(allDatatypes).map((dataType, index) => {
            return {
                key: "datasetDatatypes-" + index, value: dataType, text: dataType
            }
        });

        if (hasRecruitment) {
            const numberOfParties = getNumberOfParties(states);
            const recruiters = [];
            const recruitersNames = [];
            for (let i = 0; i < numberOfParties; i++) {
                if (globals['Recruit'][i] !== null && globals['Recruit'][i] !== undefined && globals['Recruit'][i][loopIndex - 1]) {
                    recruiters.push(i);
                    recruitersNames.push(getPartyName(i, loops))
                }
            }
            const sameDatatypes = recruiters.length === 1 || states[datasetDatatypesRecruitersSame.id].values[0] === 1;
            if (sameDatatypes) {
                const label = "For the cohort '" + cohortName + "', specify all datatypes " +
                    "generated by " + recruitersNames.join(', ');
                fields.push({
                    label,
                    type: "dropdown",
                    multiple: true,
                    allowAdditions: true,
                    options,
                    allowPreliminary: true
                });
            } else {
                for (let i = 0; i < recruiters.length; i++) {
                    const partyIndex = recruiters[i];
                    const label = "For the cohort '" + cohortName + "', specify all datatypes " +
                        "generated by " + recruitersNames[partyIndex];
                    fields.push({
                        label,
                        type: "dropdown",
                        multiple: true,
                        allowAdditions: true,
                        options,
                        allowPreliminary: true
                    });
                }
            }
        } else {
            const cohortProviderName = getPartyName(globals['CohortProvider'][loopIndex - 1], loops);
            fields.push({
                label: "For the cohort '" + cohortName + "', specify all existing datatypes provided by '" + cohortProviderName + "'",
                type: "dropdown",
                multiple: true,
                allowAdditions: true,
                options,
                allowPreliminary: true
            });
        }

        return fields;
    },
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {values} = states[datasetDatatypes.id];
        const numberOfParties = getNumberOfParties(states);
        const nbDatasets = getNumberOfDatasets(states);
        const hasRecruitment = globals['Recruitment'][loopIndex - 1];
        if (globals['CohortDataPerParty'] === undefined || globals['CohortDataPerParty'] === null) {
            globals['CohortDataPerParty'] = Array(numberOfParties);
        }

        if (hasRecruitment) {
            const recruiters = [];
            for (let i = 0; i < numberOfParties; i++) {
                if (globals['Recruit'][i] !== null && globals['Recruit'][i] !== undefined && globals['Recruit'][i][loopIndex - 1]) {
                    recruiters.push(i);
                    if (globals['CohortDataPerParty'][i] === undefined || globals['CohortDataPerParty'][i] === null) {
                        globals['CohortDataPerParty'][i] = Array(nbDatasets);
                    }
                }
            }
            const sameDatatypes = recruiters.length === 1 || states[datasetDatatypesRecruitersSame.id].values[0] === 1;
            if (sameDatatypes) {
                globals['CohortData'][loopIndex - 1] = values[0];
                const datatypes = values[0];
                for (let i = 0; i < recruiters.length; i++) {
                    const partyIndex = recruiters[i];
                    globals['CohortDataPerParty'][partyIndex][loopIndex - 1] = datatypes;
                }
            } else {
                const allDatatypes = [];
                for (let i = 0; i < recruiters.length; i++) {
                    const partyIndex = recruiters[i];
                    globals['CohortDataPerParty'][partyIndex][loopIndex - 1] = values[i];
                    allDatatypes.push(...values[i]);
                }
                globals['CohortData'][loopIndex - 1] = [...new Set([...allDatatypes])];
            }
        } else {
            const cohortProvider = globals['CohortProvider'][loopIndex - 1];
            const datatypes = values[0];
            globals['CohortData'][loopIndex - 1] = datatypes;
            if (globals['CohortDataPerParty'][cohortProvider] === undefined || globals['CohortDataPerParty'][cohortProvider] === null) {
                globals['CohortDataPerParty'][cohortProvider] = Array(nbDatasets);
            }
            globals['CohortDataPerParty'][cohortProvider][loopIndex - 1] = datatypes;
        }
        return datasetSamples;
    }
});
// 20
const datasetSamples = new ItemDefinition({
    id: 'datasetSamples',
    title: "Samples",
    group: 'Cohort Samples',
    loopName: "dataset_info_loop",
    buildFields: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const cohortName = getDatasetName(loopIndex - 1, loops);
        return [
            {
                label: "Are bio samples of the data subjects of cohort '" + cohortName + "' transferred within the scope of this agreement?",
                type: "yesno",
                allowPreliminary: true
            }
        ]
    },
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {values} = states[datasetSamples.id];
        const samples = values[0] === 1;
        globals['Samples'][loopIndex - 1] = samples;
        if (samples) {
            return datasetSamplesAgreement;
        } else {
            return datasetSampleFateTrigger;
        }
    }
});

const datasetSamplesAgreement = new ItemDefinition({
    id: 'datasetSamplesAgreement',
    title: "Samples agreement",
    group: 'Cohort Samples',
    loopName: "dataset_info_loop",
    fields: [
        {
            label: "The transfer of samples is covered by?",
            type: "radio",
            options: [
                {key: "datasetSamplesAgreement-mta", text: "Material Transfer Agreement", value: "mta"},
                {key: "datasetSamplesAgreement-oth", text: "Other agreement", value: "oth"},
            ],
        },
    ],
    chooseNext: function (states, globals) {
        globals['SamplesInvolved'] = true;
        const {values} = states[datasetSamplesAgreement.id];
        if (values[0] === 'oth') {
            return datasetSamplesAgreementSpecify;
        } else {
            return datasetSamplesProvidersDec;
        }
    }
});

const datasetSamplesAgreementSpecify = new ItemDefinition({
    id: 'datasetSamplesAgreementSpecify',
    title: "Samples agreement",
    group: 'Cohort Samples',
    loopName: "dataset_info_loop",
    fields: [
        {
            label: "Please specify the 'other agreement'",
            type: "text"
        }
    ],
    chooseNext: function () {
        return datasetSamplesProvidersDec;
    }
});

const datasetSamplesProvidersDec = new ItemDefinition({
    id: 'datasetSamplesProvidersDec',
    title: "Samples Providers Decision",
    group: 'Cohort Samples',
    loopName: "dataset_info_loop",
    isDecisionNode: true,
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const nbParties = getNumberOfParties(states);
        const nbDatasets = getNumberOfDatasets(states);
        if (nbDatasets === 1 && nbParties === 2 && ['OP', 'AP', 'DH'].indexOf(globals['POP']) !== -1) {
            // see #7
            globals['SamplesProviders'][loopIndex - 1] = Array(nbParties);
            globals['SampleProvResp'][loopIndex - 1] = Array(nbParties);
            globals['SamplesReceivers'][loopIndex - 1] = Array(nbParties);
            if (globals['HasRole'][0]['P'] && globals['HasRole'][1]['R'] &&
                !globals['HasRole'][0]['R'] && !globals['HasRole'][1]['P']) {
                // data transfer from 0 to 1
                globals['SamplesProviders'][loopIndex - 1][0] = true;
                globals['SamplesReceivers'][loopIndex - 1][0] = false;
                globals['SamplesProviders'][loopIndex - 1][1] = false;
                globals['SamplesReceivers'][loopIndex - 1][1] = true;
                globals['SampleProvResp'][loopIndex - 1][0] = globals['Resp'][0]['P'];
                globals['SampleRecResp'][loopIndex - 1][1] = globals['Resp'][1]['R'];
                return datasetSampleFateTrigger;
            }
            if (globals['HasRole'][1]['P'] && globals['HasRole'][1]['R'] &&
                !globals['HasRole'][1]['R'] && !globals['HasRole'][0]['P']) {
                // data transfer from 1 to 0
                globals['SamplesProviders'][loopIndex - 1][1] = true;
                globals['SamplesReceivers'][loopIndex - 1][1] = false;
                globals['SamplesProviders'][loopIndex - 1][0] = false;
                globals['SamplesReceivers'][loopIndex - 1][0] = true;
                globals['SampleProvResp'][loopIndex - 1][1] = globals['Resp'][1]['P'];
                globals['SampleRecResp'][loopIndex - 1][0] = globals['Resp'][0]['R'];
                return datasetSampleFateTrigger;
            }
            return datasetSamplesProviders;
        } else {
            return datasetSamplesProviders;
        }
    }
});


const datasetSamplesProviders = new ItemDefinition({
    id: 'datasetSamplesProviders',
    title: "Samples Providers",
    group: 'Cohort Samples',
    loopName: "dataset_info_loop",
    allowApplyAll: false,
    buildFields: function (states, globals, loops) {
        const options = [];
        const nbParties = getNumberOfParties(states);
        const pop = globals['POP'];
        // we get the names of the parties as entered in partyDetails
        for (let i = 0; i < nbParties; i++) {
            if (['OP', 'AP', 'DH'].indexOf(pop) !== -1 && globals['HasRole'][i]['R']) {
                // list should not contain recipients in case of OP or AP or DH
                continue
            }
            const partyName = getPartyName(i, loops);
            options.push({key: "datasetSamplesProviders" + i, text: partyName, value: i});
        }
        if (options.length === 0) {
            // we should have at least one option available
            // if empty it means that no partner a role other than recipients
            this.errors = [{text: "No partner with a role other than recipient has been defined!"}];
            return [];
        }
        return [
            {
                label: "Which partner provides the samples?",
                type: "dropdown",
                multiple: true,
                options: options
            },
        ];
    },
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {values} = states[datasetSamplesProviders.id];
        const nbParties = getNumberOfParties(states);
        globals['SamplesProviders'][loopIndex - 1] = Array(nbParties);
        let next = datasetSamplesReceivers;
        for (let i = 0; i < nbParties; i++) {
            const isSamplesProvider = values[0].indexOf(i) > -1;
            globals['SamplesProviders'][loopIndex - 1][i] = isSamplesProvider;
            if (isSamplesProvider) {
                const respP = globals['Resp'][i]['P'];
                const respG = globals['Resp'][i]['G'];
                const respR = globals['Resp'][i]['R'];
                const uniqueResponsibilities = [...new Set([respP, respG, respR])].filter(Boolean);
                // if same responsibility in all roles, we can infer samples responsibility #12
                if (uniqueResponsibilities.length === 1 && uniqueResponsibilities[0] !== 'MU') {
                    globals['SampleProvResp'][loopIndex - 1][i] = uniqueResponsibilities[0];
                } else {
                    next = datasetSamplesProvidersRoles;
                }
            }
        }
        return next;
    }
});

const datasetSamplesProvidersRoles = new ItemDefinition({
    id: 'datasetSamplesProvidersRoles',
    title: "Samples providers roles",
    group: 'Cohort Samples',
    loopName: "dataset_info_loop",
    allowApplyAll: false,
    buildFields: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const nbParties = getNumberOfParties(states);
        const fields = [];
        for (let i = 0; i < nbParties; i++) {
            if (globals['SamplesProviders'][loopIndex - 1][i]) {
                const respP = globals['Resp'][i]['P'];
                const respG = globals['Resp'][i]['G'];
                const respR = globals['Resp'][i]['R'];
                const uniqueResponsibilities = [...new Set([respP, respG, respR])].filter(Boolean);
                if (uniqueResponsibilities.length === 1 && uniqueResponsibilities[0] !== 'MU') {
                    // if same responsibility in all roles, we already know responsibility #12
                    continue
                }
                const partyName = getPartyName(i, loops);
                fields.push({
                    label: "Role of '" + partyName + "' ",
                    type: "dropdown",
                    options: [
                        {
                            key: "datasetSamplesProvidersRolesPR-" + i,
                            value: "PR",
                            text: "service provider (= processor)"
                        },
                        {
                            key: "datasetSamplesProvidersRolesCO-" + i,
                            value: "CO",
                            text: "controller"
                        },
                        {
                            key: "datasetSamplesProvidersRolesJC-" + i,
                            value: "JC",
                            text: "joint-controller"
                        }
                    ],
                    allowPreliminary: true
                });
            }
        }
        return fields;
    },
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {values} = states[datasetSamplesProviders.id];
        const valuesRoles = states[datasetSamplesProvidersRoles.id].values;
        let countWithoutResp = 0;
        for (let i = 0; i < values[0].length; i++) {
            const partyIndex = values[0][i];
            const respP = globals['Resp'][partyIndex]['P'];
            const respG = globals['Resp'][partyIndex]['G'];
            const respR = globals['Resp'][partyIndex]['R'];
            const uniqueResponsibilities = [...new Set([respP, respG, respR])].filter(Boolean);
            if (uniqueResponsibilities.length === 1 && uniqueResponsibilities[0] !== 'MU') {
                // if same responsibility in all roles, we already know responsibility #12
                continue
            }
            globals['SampleProvResp'][loopIndex - 1][partyIndex] = valuesRoles[countWithoutResp];
            countWithoutResp++;
        }
        return datasetSamplesReceivers;
    }
});
const datasetSamplesReceivers = new ItemDefinition({
    id: 'datasetSamplesReceivers',
    title: "Samples Recipients",
    group: 'Cohort Samples',
    loopName: "dataset_info_loop",
    buildFields: function (states, globals, loops) {
        const options = [];
        const nbParties = getNumberOfParties(states);
        const loopIndex = this.getLoopIndex(loops);
        const cohortName = getDatasetName(loopIndex - 1, loops);
        const nbProviders = states[datasetSamplesProviders.id].values[0].length;
        // we get the names of the parties as entered in partyDetails
        for (let i = 0; i < nbParties; i++) {
            const partyName = getPartyName(i, loops);
            if (nbProviders === 1 && globals['SamplesProviders'][loopIndex - 1][i] === true) {
                continue;
            }
            options.push({key: "datasetSamplesReceivers" + loopIndex + "-" + i, text: partyName, value: i});
        }
        return [
            {
                label: "Which partner(s) receive samples of cohort '" + cohortName + "'?",
                type: "dropdown",
                multiple: true,
                options: options,
                allowPreliminary: true
            },
        ];
    },
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {values} = states[datasetSamplesReceivers.id];
        const nbParties = getNumberOfParties(states);
        let hasSamplesReceivers = false;
        let mustCheckRoles = false;
        for (let i = 0; i < nbParties; i++) {
            const isSamplesReceiver = values[0].indexOf(i) > -1;
            globals['SamplesReceivers'][loopIndex - 1][i] = isSamplesReceiver;
            if (isSamplesReceiver) {
                hasSamplesReceivers = true;
                const respP = globals['Resp'][i]['P'];
                const respG = globals['Resp'][i]['G'];
                const respR = globals['Resp'][i]['R'];
                const uniqueResponsibilities = [...new Set([respP, respG, respR])].filter(Boolean);
                // if same responsibility in all roles, we can infer samples responsibility #12
                if (uniqueResponsibilities.length === 1 && uniqueResponsibilities[0] !== 'MU') {
                    globals['SampleRecResp'][loopIndex - 1][i] = uniqueResponsibilities[0];
                } else {
                    mustCheckRoles = true;
                }
            }
        }
        if (hasSamplesReceivers && mustCheckRoles) {
            return datasetSamplesReceiversRoles;
        } else {
            return datasetSampleFateTrigger;
        }
    }
});
const datasetSamplesReceiversRoles = new ItemDefinition({
    id: 'datasetSamplesReceiversRoles',
    title: "Samples recipients roles",
    group: 'Cohort Samples',
    loopName: "dataset_info_loop",
    buildFields: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const nbParties = getNumberOfParties(states);
        const fields = [];
        for (let i = 0; i < nbParties; i++) {
            if (globals['SamplesReceivers'][loopIndex - 1][i]) {
                const respP = globals['Resp'][i]['P'];
                const respG = globals['Resp'][i]['G'];
                const respR = globals['Resp'][i]['R'];
                const uniqueResponsibilities = [...new Set([respP, respG, respR])].filter(Boolean);
                // if same responsibility in all roles, we can infer samples responsibility #12
                if (uniqueResponsibilities.length === 1 && uniqueResponsibilities[0] !== 'MU') {
                    continue
                }
                const partyName = getPartyName(i, loops);
                fields.push({
                    label: "Role of '" + partyName + "' ",
                    type: "dropdown",
                    options: [
                        {
                            key: "datasetSamplesReceiversRolesPR-" + i,
                            value: "PR",
                            text: "service provider (= processor)"
                        },
                        {
                            key: "datasetSamplesReceiversRolesCO-" + i,
                            value: "CO",
                            text: "controller"
                        },
                        {
                            key: "datasetSamplesReceiversRolesJC-" + i,
                            value: "JC",
                            text: "joint-controller"
                        }
                    ]
                });
            }
        }
        return fields;
    },
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {values} = states[datasetSamplesReceivers.id];
        const valuesRoles = states[datasetSamplesReceiversRoles.id].values;
        for (let i = 0; i < values[0].length; i++) {
            const partyIndex = values[0][i];
            const respP = globals['Resp'][partyIndex]['P'];
            const respG = globals['Resp'][partyIndex]['G'];
            const respR = globals['Resp'][partyIndex]['R'];
            const uniqueResponsibilities = [...new Set([respP, respG, respR])].filter(Boolean);
            // if same responsibility in all roles, we can infer samples responsibility #12
            if (uniqueResponsibilities.length === 1 && uniqueResponsibilities[0] !== 'MU') {
                continue
            }
            globals['SampleRecResp'][loopIndex - 1][partyIndex] = valuesRoles[i];
        }
        return datasetSampleFateTrigger;
    }
});

const datasetSampleFateTrigger = new ItemDefinition({
    id: 'datasetSampleFateTrigger',
    title: "datasetSampleFateTrigger",
    group: 'Cohort Samples',
    buildLoopName: function (states, globals, loops) {
        return "dataset_info_loop#dataset_samples_use$" + loops['dataset_info_loop'].index;
    },
    isDecisionNode: true,
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const datasetIndex = loops['dataset_info_loop'].index - 1;
        const partyIndexI = loopIndex - 1;
        const nbParties = getNumberOfParties(states);
        let samplesReceiversCount = 0;
        for (let i = 0; i < nbParties; i++) {
            const isSamplesReceivers = globals['SamplesReceivers'][datasetIndex][i];
            if (isSamplesReceivers === true) {
                if (samplesReceiversCount === partyIndexI) {
                    const samplesRecResp = globals['SampleRecResp'][datasetIndex][i];
                    const sampleFateTrigger = isSamplesReceivers && (samplesRecResp === 'PR' || samplesRecResp === 'SU');
                    globals['SampleFateTrigger'][datasetIndex][i] = sampleFateTrigger;
                    if (sampleFateTrigger) {
                        return datasetPkSampleFate;
                    } else {
                        for (let k = 0; k < nbParties; k++) {
                            if (k !== i) {
                                const isSamplesProvider = globals['SamplesProviders'][datasetIndex][k];
                                const sampleProvResp = globals['SampleProvResp'][datasetIndex][k];
                                const sampleFateTrigger = isSamplesProvider && (sampleProvResp === 'CO' || sampleProvResp === 'JC');
                                globals['SampleFateTrigger'][datasetIndex][i] = sampleFateTrigger;
                                if (sampleFateTrigger) {
                                    return datasetPkSampleFate;
                                }
                            }
                        }
                    }
                }
                samplesReceiversCount++;
            }
        }
        return datasetSamplesUsePartiesEndLoop;
    }
});

const datasetPkSampleFate = new ItemDefinition({
    id: 'datasetPkSampleFate',
    buildLoopName: function (states, globals, loops) {
        return "dataset_info_loop#dataset_samples_use$" + loops['dataset_info_loop'].index;
    },
    title: "Samples Fate",
    group: 'Cohort Samples',
    buildFields: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const datasetIndex = loops['dataset_info_loop'].index - 1;
        const partyIndexI = loopIndex - 1;
        let samplesReceiversCount = 0;
        const nbParties = getNumberOfParties(states);
        const cohortName = getDatasetName(datasetIndex, loops);
        for (let i = 0; i < nbParties; i++) {
            const isSamplesReceivers = globals['SamplesReceivers'][datasetIndex][i];
            if (isSamplesReceivers === true) {
                if (samplesReceiversCount === partyIndexI) {
                    const partyNameI = getPartyName(i, loops);
                    return [{
                        label: "For '" + partyNameI + "' and for cohort '" + cohortName + "', what should happen with the samples" +
                            " at the end of contract?",
                        type: "radio",
                        options: [
                            {key: "datasetPkSampleFate-ded", text: "Destroy remaining samples", value: "DES"},
                            {key: "datasetPkSampleFate-and", text: "Anonymise remaining samples", value: "ANS"},
                            {key: "datasetPkSampleFate-red", text: "Return remaining samples", value: "RES"},
                            {key: "datasetPkSampleFate-kes", text: "Keep remaining samples", value: "KES"},
                        ],
                    }];
                }
                samplesReceiversCount++;
            }
        }
    },
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const valuesFate = states[datasetPkSampleFate.id].values;
        const datasetIndex = loops['dataset_info_loop'].index - 1;
        const sampleFate = valuesFate[0];

        const partyIndexI = loopIndex - 1;
        let samplesReceiversCount = 0;
        const nbParties = getNumberOfParties(states);
        for (let i = 0; i < nbParties; i++) {
            const isSamplesReceivers = globals['SamplesReceivers'][datasetIndex][i];
            if (isSamplesReceivers === true) {
                if (samplesReceiversCount === partyIndexI) {
                    globals['SampleFate'][datasetIndex][i] = sampleFate;
                    if (sampleFate === 'KES') {
                        return datasetSampleTerminate;
                    } else {
                        return datasetSamplesUsePartiesEndLoop;
                    }
                }
                samplesReceiversCount++;
            }
        }


    }
});
const datasetSampleTerminate = new ItemDefinition({
    id: 'datasetSampleTerminate',
    title: "TerminateSampleKeep",
    group: 'Cohort Samples',
    buildLoopName: function (states, globals, loops) {
        return "dataset_info_loop#dataset_samples_use$" + loops['dataset_info_loop'].index;
    },
    buildFields: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const datasetIndex = loops['dataset_info_loop'].index - 1;
        const cohortName = getDatasetName(datasetIndex, loops);
        const projectOrHosting = globals['POP'] === 'DH' ? 'hosting' : 'project';
        const partyIndexI = loopIndex - 1;
        let samplesReceiversCount = 0;
        const nbParties = getNumberOfParties(states);
        for (let i = 0; i < nbParties; i++) {
            const isSamplesReceivers = globals['SamplesReceivers'][datasetIndex][i];
            if (isSamplesReceivers === true) {
                if (samplesReceiversCount === partyIndexI) {
                    const partyNameI = getPartyName(i, loops);
                    return [{
                        label: `If the ${projectOrHosting} is being terminated due to one of the partner’s fault, should
                         '${partyNameI}' keep the samples of cohort '${cohortName}' nevertheless?`,
                        type: "yesno"
                    }]
                }
                samplesReceiversCount++;
            }
        }


    },
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const datasetIndex = loops['dataset_info_loop'].index - 1;
        const terminateSampleKeep = states[datasetSampleTerminate.id].values[0] === 1;
        const partyIndexI = loopIndex - 1;
        let samplesReceiversCount = 0;
        const nbParties = getNumberOfParties(states);
        for (let i = 0; i < nbParties; i++) {
            const isSamplesReceivers = globals['SamplesReceivers'][datasetIndex][i];
            if (isSamplesReceivers === true) {
                if (samplesReceiversCount === partyIndexI) {
                    globals['TerminateSampleKeep'][datasetIndex][i] = terminateSampleKeep;
                    if (terminateSampleKeep) {
                        globals['SampleTerminateFate'][datasetIndex][i] = 'KES';
                        return datasetSamplesUsePartiesEndLoop;
                    } else {
                        return datasetSampleTerminateFate;
                    }
                }
                samplesReceiversCount++;
            }
        }

    }
});

const datasetSampleTerminateFate = new ItemDefinition({
    id: 'datasetSampleTerminateFate',
    buildLoopName: function (states, globals, loops) {
        return "dataset_info_loop#dataset_samples_use$" + loops['dataset_info_loop'].index;
    },
    title: "Terminate Samples Fate",
    group: 'Cohort Samples',
    buildFields: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const datasetIndex = loops['dataset_info_loop'].index - 1;
        const cohortName = getDatasetName(datasetIndex, loops);
        const partyIndexI = loopIndex - 1;
        let samplesReceiversCount = 0;
        const nbParties = getNumberOfParties(states);
        const projectOrHosting = globals['POP'] === 'DH' ? 'hosting' : 'project';
        for (let i = 0; i < nbParties; i++) {
            const isSamplesReceivers = globals['SamplesReceivers'][datasetIndex][i];
            if (isSamplesReceivers === true) {
                if (samplesReceiversCount === partyIndexI) {
                    const partyNameI = getPartyName(i, loops);
                    return [{
                        label: `If the ${projectOrHosting} is being terminated due to one of the partner's fault, what should '${partyNameI}' do with the samples of cohort '${cohortName}'`,
                        type: "radio",
                        options: [
                            {key: "datasetSampleTerminateFate-ded", text: "Destroy remaining samples", value: "DES"},
                            {key: "datasetSampleTerminateFate-red", text: "Return remaining samples", value: "RES"},
                        ],
                    }];
                }
                samplesReceiversCount++;
            }
        }

    },
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const samplesTerminateFate = states[datasetSampleTerminateFate.id].values[0] === 1;
        const datasetIndex = loops['dataset_info_loop'].index - 1;
        const partyIndexI = loopIndex - 1;
        let samplesReceiversCount = 0;
        const nbParties = getNumberOfParties(states);
        for (let i = 0; i < nbParties; i++) {
            const isSamplesReceivers = globals['SamplesReceivers'][datasetIndex][i];
            if (isSamplesReceivers === true) {
                if (samplesReceiversCount === partyIndexI) {
                    globals['SampleTerminateFate'][datasetIndex][i] = samplesTerminateFate;

                }
                samplesReceiversCount++;
            }
        }
        return datasetSamplesUsePartiesEndLoop;
    }
});


const datasetSamplesUsePartiesEndLoop = new ItemDefinition({
    id: 'datasetSamplesUsePartiesEndLoop',
    title: "datasetSamplesUsePartiesEndLoop",
    group: 'Cohort Samples',
    buildLoopName: function (states, globals, loops) {
        return "dataset_info_loop#dataset_samples_use$" + loops['dataset_info_loop'].index;
    },
    isDecisionNode: true,
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        // count number of parties with samples receivers
        let samplesReceiversCount = 0;
        const nbParties = getNumberOfParties(states);
        const datasetIndex = loops['dataset_info_loop'].index - 1;

        for (let i = 0; i < nbParties; i++) {
            if (globals['SamplesReceivers'][datasetIndex][i] === true) {
                samplesReceiversCount++;
            }
        }
        if (samplesReceiversCount === 0 || loopIndex >= samplesReceiversCount) {
            return datasetProvDataDecision;
        } else {
            return this.nextLoopIteration(loops);
        }
    }
});

const datasetProvDataDecision = new ItemDefinition({
    id: 'datasetProvDataDecision',
    title: 'datasetProvDataDecision',
    group: 'Additional Data',
    loopName: "dataset_info_loop",
    isDecisionNode: true,
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const nbParties = getNumberOfParties(states);
        if (globals['Recruitment'][loopIndex - 1] === true || nbParties === 2) {
            globals['ProvData'][loopIndex - 1] = false;
            return datasetGenerationDecision;
        }
        return datasetProvData;
    }
});
// 19
const datasetProvData = new ItemDefinition({
    id: 'datasetProvData',
    title: "Already existing data",
    group: 'Additional Data',
    loopName: "dataset_info_loop",
    buildFields: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const cohortName = getDatasetName(loopIndex - 1, loops);
        return [
            {
                label: "Is additional existing data of cohort '" + cohortName + "' provided by another party to the agreement?",
                type: "yesno",
                allowPreliminary: true
            }
        ]
    },
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {values} = states[datasetProvData.id];
        const hasProvData = values[0] === 1;
        globals['ProvData'][loopIndex - 1] = hasProvData;
        if (hasProvData) {
            return datasetProvider;
        } else {
            return datasetGenerationDecision;
        }
    }
});

const datasetProvider = new ItemDefinition({
    id: 'datasetProvider',
    title: "Provider",
    group: 'Additional Data',
    loopName: "dataset_info_loop",
    buildFields: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const cohortName = getDatasetName(loopIndex - 1, loops);
        const nbParties = getNumberOfParties(states);
        // we get the names of the parties as entered in partyDetails
        const options = [];
        for (let i = 0; i < nbParties; i++) {
            const isProvider = globals['HasRole'][i]['P'];
            if (isProvider && globals['CohortOwner'][loopIndex - 1] !== i) {
                const partyName = getPartyName(i, loops);
                options.push({key: "datasetProvider" + i, text: partyName, value: i});
            }
        }
        if (options.length === 0) {
            // we should have least one option available
            // if empty it means that no partner had provider role
            this.errors = [{text: "No partner with provider role (excluding the cohort provider) has been defined!"}];
            return [];
        }
        return [{
            type: "dropdown",
            options: options,
            label: "Which partner provides additional existing data for cohort '" + cohortName + "'?",
            multiple: true,
            allowPreliminary: true
        }]
    },
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const nbParties = getNumberOfParties(states);
        const {values} = states[datasetProvider.id];
        const selectedProviders = values[0];
        let hasMu = false;
        for (let i = 0; i < nbParties; i++) {
            const isASelectedProvider = selectedProviders.indexOf(i) > -1;
            globals['Provider'][loopIndex - 1][i] = isASelectedProvider;
            if (!isASelectedProvider) {
                continue
            }
            let respP = globals['Resp'][i]['P'];
            if (respP === 'MU') {
                hasMu = true
            } else {
                globals['ProvResp'][loopIndex - 1][i] = respP;
            }
        }
        if (hasMu) {
            return datasetProvRespMU;
        } else {
            return datasetProvTypes;
        }
    }
});

const datasetProvRespMU = new ItemDefinition({
    id: 'datasetProvRespMU',
    title: "Provider responsibilities",
    group: 'Additional Data',
    loopName: "dataset_info_loop",
    buildFields: function (states, globals, loops) {
        const {values} = states[datasetProvider.id];
        const fields = [];
        for (let i = 0; i < values[0].length; i++) {
            const partyIndex = values[0][i];
            let respP = globals['Resp'][partyIndex]['P'];
            if (respP === 'MU') {
                // we get the names of the parties as entered in partyDetails
                const partyName = getPartyName(partyIndex, loops);
                fields.push(
                    {
                        label: partyName,
                        type: "dropdown",
                        options: [
                            {key: "datasetProvRespMU" + i + "PR", text: "processor", value: "PR"},
                            {key: "datasetProvRespMU" + i + "SU", text: "sub-processor", value: "SU"},
                            {key: "datasetProvRespMU" + i + "JC", text: "joint-controller", value: "JC"},
                        ],
                    }
                );
            }
        }
        return fields;
    },
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {values} = states[datasetProvider.id];
        const valuesResp = states[datasetProvRespMU.id].values;
        const nbParties = getNumberOfParties(states);
        globals['ProvResp'][loopIndex - 1] = Array(nbParties);
        for (let i = 0; i < values[0].length; i++) {
            const partyIndex = values[0][i];
            let respP = globals['Resp'][partyIndex]['P'];
            if (respP === 'MU') {
                globals['ProvResp'][loopIndex - 1][partyIndex] = valuesResp[i];
            }
        }
        return datasetProvTypes;
    }
});

const datasetProvTypes = new ItemDefinition({
    id: 'datasetProvTypes',
    title: "Type of data provided",
    group: 'Additional Data',
    loopName: "dataset_info_loop",
    buildFields: function (states, globals, loops) {
        const {values} = states[datasetProvider.id];
        const fields = [];
        const allDatatypes = getAllUniqueDatatypes(globals);
        const options = Array.from(allDatatypes).map((dataType, index) => {
            return {
                key: "datasetProvTypes-" + index, value: dataType, text: dataType
            }
        });
        for (let i = 0; i < values[0].length; i++) {
            const partyIndex = values[0][i];
            // we get the names of the parties as entered in partyDetails
            const partyName = getPartyName(partyIndex, loops);
            fields.push(
                {
                    label: "Specify datatypes provided by '" + partyName + "'",
                    type: "dropdown",
                    multiple: true,
                    allowAdditions: true,
                    options,
                    allowPreliminary: true
                }
            );
        }
        return fields;
    },
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const valuesTypes = states[datasetProvTypes.id].values;
        const {values} = states[datasetProvider.id];
        for (let i = 0; i < values[0].length; i++) {
            const partyIndex = values[0][i];
            globals['DataProvided'][loopIndex - 1][partyIndex] = valuesTypes[i];
        }
        return datasetGenerationDecision;
    }
});

const datasetGenerationDecision = new ItemDefinition({
    id: 'datasetGenerationDecision',
    title: "datasetGenerationDecision",
    group: 'Additional Data',
    loopName: "dataset_info_loop",
    isDecisionNode: true,
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        if (globals['POP'] === 'DH') {
            globals['Generation'][loopIndex - 1] = false;
            return datasetTransferAccessDecision;
        } else {
            return datasetGeneration;
        }
    }
});

const datasetGeneration = new ItemDefinition({
    id: 'datasetGeneration',
    title: "New generation",
    group: 'Additional Data',
    loopName: "dataset_info_loop",
    buildFields: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const cohortName = getDatasetName(loopIndex - 1, loops);
        return [
            {
                label: "Are additional datatypes generated around cohort '" + cohortName + "' by a partner under this agreement?",
                type: "yesno",
                allowPreliminary: true
            }
        ]
    },
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {values} = states[datasetGeneration.id];
        const generation = values[0] === 1;
        globals['Generation'][loopIndex - 1] = generation;
        if (generation) {
            return datasetGenerator;
        } else {
            return datasetTransferAccessDecision;
        }
    }
});

const datasetGenerator = new ItemDefinition({
    id: 'datasetGenerator',
    title: "Generator",
    group: 'Additional Data',
    loopName: "dataset_info_loop",
    buildFields: function (states, globals, loops) {
        const nbParties = getNumberOfParties(states);
        // we get the names of the parties as entered in partyDetails
        const options = [];
        for (let i = 0; i < nbParties; i++) {
            const isGenerator = globals['HasRole'][i]['G'];
            if (isGenerator) {
                const partyName = getPartyName(i, loops);
                options.push({key: "datasetGenerator" + i, text: partyName, value: i});
            }
        }
        if (options.length === 0) {
            // we should have least one option available
            // if empty it means that no partner had generator role
            this.errors = [{text: "No partner with generator role has been defined!"}];
            return [];
        }
        return [{
            type: "dropdown",
            options: options,
            label: "Which partner generates the additional data?",
            multiple: true
        }]
    },
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        globals['Generator'][loopIndex - 1] = Array();
        globals['GenResp'][loopIndex - 1] = Array();
        const nbParties = getNumberOfParties(states);
        const {values} = states[datasetGenerator.id];
        const selectedGenerators = values[0];
        let hasMu = false;
        for (let i = 0; i < nbParties; i++) {
            const isASelectedGenerator = selectedGenerators.indexOf(i) > -1;
            globals['Generator'][loopIndex - 1][i] = isASelectedGenerator;
            if (!isASelectedGenerator) {
                continue
            }
            if (globals['CohortOwner'][loopIndex - 1] === i) {
                globals['GenResp'][loopIndex - 1][i] = 'CO';
                continue;
            }
            let respG = globals['Resp'][i]['G'];
            if (respG === 'MU') {
                hasMu = true
            } else {
                globals['GenResp'][loopIndex - 1][i] = respG;
            }
        }
        if (hasMu) {
            return datasetGenRespMU;
        } else {
            return datasetGenTypes;
        }
    }
});

const datasetGenRespMU = new ItemDefinition({
    id: 'datasetGenRespMU',
    title: "Generator responsibilities",
    group: 'Additional Data',
    loopName: "dataset_info_loop",
    buildFields: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {values} = states[datasetGenerator.id];
        const fields = [];
        for (let i = 0; i < values[0].length; i++) {
            const partyIndex = values[0][i];
            if (globals['CohortOwner'][loopIndex - 1] === partyIndex) {
                continue;
            }
            let respG = globals['Resp'][partyIndex]['G'];
            if (respG === 'MU') {
                // we get the names of the parties as entered in partyDetails
                const partyName = getPartyName(partyIndex, loops);
                fields.push(
                    {
                        label: partyName,
                        type: "dropdown",
                        options: [
                            {key: "datasetGenRespMU" + i + "PR", text: "processor", value: "PR"},
                            {key: "datasetGenRespMU" + i + "SU", text: "sub-processor", value: "SU"},
                            {key: "datasetGenRespMU" + i + "JC", text: "joint-controller", value: "JC"},
                        ],
                    }
                );
            }
        }
        return fields;
    },
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {values} = states[datasetGenerator.id];
        const valuesResp = states[datasetGenRespMU.id].values;
        const nbParties = getNumberOfParties(states);
        globals['GenResp'][loopIndex - 1] = Array(nbParties);
        for (let i = 0; i < values[0].length; i++) {
            const partyIndex = values[0][i];
            if (globals['CohortOwner'][loopIndex - 1] === partyIndex) {
                continue;
            }
            let respG = globals['Resp'][partyIndex]['G'];
            if (respG === 'MU') {
                globals['GenResp'][loopIndex - 1][partyIndex] = valuesResp[i];
            }
        }
        return datasetGenTypes;
    }
});

const datasetGenTypes = new ItemDefinition({
    id: 'datasetGenTypes',
    title: "Type of data generated",
    group: 'Additional Data',
    loopName: "dataset_info_loop",
    buildFields: function (states, globals, loops) {
        const {values} = states[datasetGenerator.id];
        const fields = [];
        const allDatatypes = getAllUniqueDatatypes(globals);
        const options = Array.from(allDatatypes).map((dataType, index) => {
            return {
                key: "datasetGenTypes-" + index, value: dataType, text: dataType
            }
        });
        for (let i = 0; i < values[0].length; i++) {
            const partyIndex = values[0][i];
            // we get the names of the parties as entered in partyDetails
            const partyName = getPartyName(partyIndex, loops);
            fields.push(
                {
                    label: "Specify datatypes generated by '" + partyName + "'",
                    type: "dropdown",
                    multiple: true,
                    allowAdditions: true,
                    options,
                    allowPreliminary: true
                }
            );
        }
        return fields;
    },
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const valuesTypes = states[datasetGenTypes.id].values;
        const {values} = states[datasetGenerator.id];
        for (let i = 0; i < values[0].length; i++) {
            const partyIndex = values[0][i];
            globals['DataGenerated'][loopIndex - 1][partyIndex] = valuesTypes[i];
        }
        return datasetTransferAccessDecision;
    }
});

const datasetTransferAccessDecision = new ItemDefinition({
    id: 'datasetTransferAccessDecision',
    title: '',
    isDecisionNode: true,
    loopName: "dataset_info_loop",
    chooseNext: function (states, globals, loops) {
        const datasetIndex = this.getLoopIndex(loops) - 1;
        const parties = listOfTransferNotProcessing10Parties(globals, states, datasetIndex);
        if (parties.length > 0) {
            const pop = globals['POP'];
            if (pop === 'DH') {
                globals['WhereEarlyTermination'] = 'dataset_transfer_not_processing10';
                globals['WhereEarlyTerminationInner'] = 'transfer_not_processing10_to';
                const provider = globals['HasRole'][0]['P'] === true ? 0 : 1;
                const receiver = (provider + 1) % 2;
                globals['Sharing'][datasetIndex][provider][receiver] = true;
                globals['Transfers'][datasetIndex][provider] = true;
                const selectedValues = uniqueDataTypesForPartyText(globals, datasetIndex, provider, false);
                globals['DataTypesShared'][datasetIndex][provider][receiver] = selectedValues;
                globals['Sharing'][datasetIndex][provider][receiver] = true;
                globals['KeepData'][datasetIndex][receiver][provider] = true;
                globals['BroadUse'][datasetIndex][receiver][provider] = true;
                return datasetTransfersNotProcessing10EndLoop;
            }
            return datasetTransfersIntro;
        } else {
            return datasetAccessDecision;
        }
    }
});

const datasetTransfersIntro = new ItemDefinition({
    id: 'datasetTransfersIntro',
    title: "Data Sharing Introduction",
    group: "Cohort Primary Shares",
    buildLoopName: function (states, globals, loops) {
        return "dataset_info_loop#dataset_transfer_not_processing10$" + loops['dataset_info_loop'].index;
    },
    buildFields: function (states, globals, loops) {
        const datasetIndex = loops['dataset_info_loop'].index - 1;
        // the samples loops has to be reset
        loops['dataset_info_loop#dataset_samples_use$' + loops['dataset_info_loop'].index].index = 0;
        const cohortName = getDatasetName(datasetIndex, loops);
        const loopIndex = this.getLoopIndex(loops);
        const parties = listOfTransferNotProcessing10Parties(globals, states, datasetIndex);
        const partyIndex = parties[loopIndex - 1];
        const partyName = getPartyName(partyIndex, loops);
        return [{
            type: 'noInput',
            label: "The next group of questions is about data sharing from '" + partyName + "' of cohort '" + cohortName + "'."
        }]
    },
    chooseNext: function () {
        return datasetTransfersDec;
    }
});

const datasetTransfersDec = new ItemDefinition({
    id: 'datasetTransfersDec',
    title: "Data Sharing Decision",
    isDecisionNode: true,
    buildLoopName: function (states, globals, loops) {
        return "dataset_info_loop#dataset_transfer_not_processing10$" + loops['dataset_info_loop'].index;
    },
    chooseNext: function (states, globals, loops) {
        const datasetIndex = loops['dataset_info_loop'].index - 1;
        const nbDatasets = getNumberOfDatasets(states);
        const nbParties = getNumberOfParties(states);
        if (nbDatasets === 1 && nbParties === 2) {
            globals['WhereEarlyTermination'] = 'dataset_transfer_not_processing10';
            globals['WhereEarlyTerminationInner'] = 'transfer_not_processing10_to';
            let foundSimpleCase = false;
            let provider, receiver;
            if (globals['HasRole'][0]['P'] && globals['HasRole'][1]['R'] &&
                !globals['HasRole'][0]['R'] && !globals['HasRole'][1]['P']) {
                // data transfer from 0 to 1
                provider = 0;
                receiver = 1;
                foundSimpleCase = true;
            }
            if (globals['HasRole'][1]['P'] && globals['HasRole'][1]['R'] &&
                !globals['HasRole'][1]['R'] && !globals['HasRole'][0]['P']) {
                // data transfer from 1 to 0
                provider = 1;
                receiver = 0;
                foundSimpleCase = true;
            }
            if (foundSimpleCase) {
                globals['Sharing'][datasetIndex][provider][receiver] = true;
                globals['Transfers'][datasetIndex][provider] = true;
                const pop = globals['POP'];
                if (pop === 'DH') {
                    const selectedValues = uniqueDataTypesForPartyText(globals, datasetIndex, provider, false);
                    globals['DataTypesShared'][datasetIndex][provider][receiver] = selectedValues;
                    globals['Sharing'][datasetIndex][provider][receiver] = true;
                    globals['KeepData'][datasetIndex][receiver][provider] = true;
                    globals['BroadUse'][datasetIndex][receiver][provider] = true;
                    return afterEarlyTerminationDecision;
                } else {
                    return datasetsTransferredDatatypes;
                }
            }
        }
        const parties = listOfTransferNotProcessing10Parties(globals, states, datasetIndex);
        if (parties.length > 0) {
            return datasetTransferNotProcessing10;
        } else {
            return datasetAccessDecision;
        }
    }
});

const datasetTransferNotProcessing10 = new ItemDefinition({
    id: 'datasetTransferNotProcessing10',
    title: "Transmissions",
    group: "Cohort Primary Shares",

    buildLoopName: function (states, globals, loops) {
        return "dataset_info_loop#dataset_transfer_not_processing10$" + loops['dataset_info_loop'].index;
    },
    buildFields: function (states, globals, loops) {
        const datasetIndex = loops['dataset_info_loop'].index - 1;
        const cohortName = getDatasetName(datasetIndex, loops);
        const loopIndex = this.getLoopIndex(loops);
        // first iteration, we ask about cohortProvider
        const parties = listOfTransferNotProcessing10Parties(globals, states, datasetIndex);
        const partyIndex = parties[loopIndex - 1];
        const partyName = getPartyName(partyIndex, loops);
        return [
            {
                label: "Is data of cohort '" + cohortName + "' transmitted by '" + partyName + "' to other partners?",
                type: "yesno"
            }
        ];
    },
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const datasetIndex = loops['dataset_info_loop'].index - 1;
        const parties = listOfTransferNotProcessing10Parties(globals, states, datasetIndex);
        const partyIndex = parties[loopIndex - 1];
        const transfer = states[datasetTransferNotProcessing10.id].values[0] === 1;
        globals['Transfers'][datasetIndex][partyIndex] = transfer;
        if (transfer) {
            const nbParties = getNumberOfParties(states);
            if (nbParties === 2 && globals['HasRole'][partyIndex]['P'] === true) {
                // in this case we can skip the question about the recipient.
                const otherPartyIndex = (partyIndex + 1) % 2 // 0 -> 1, 1 -> 0
                globals['Sharing'][datasetIndex][partyIndex][otherPartyIndex] = true;
                globals['WhereEarlyTermination'] = 'dataset_transfer_not_processing10';
                globals['WhereEarlyTerminationInner'] = 'transfer_not_processing10_to';
                return datasetsTransferredDatatypes;
            }
            return datasetsSharingTo;
        } else {
            return datasetTransfersNotProcessing10EndLoop;
        }
    }
});

const datasetsSharingTo = new ItemDefinition({
    id: 'datasetsSharingTo',
    buildLoopName: function (states, globals, loops) {
        return "dataset_info_loop#dataset_transfer_not_processing10$" + loops['dataset_info_loop'].index;
    },
    title: "Define data shares",
    group: "Cohort Primary Shares",
    buildFields: function (states, globals, loops) {
        const options = [];
        const loopIndex = this.getLoopIndex(loops);
        const nbParties = getNumberOfParties(states);
        const datasetIndex = loops['dataset_info_loop'].index - 1;
        const parties = listOfTransferNotProcessing10Parties(globals, states, datasetIndex);
        const partyIndex = parties[loopIndex - 1];
        const partyName = getPartyName(partyIndex, loops);
        const cohortName = getDatasetName(datasetIndex, loops);
        if (!globals['HasRole'][partyIndex]['P'] === true) {
            this.errors = [{text: "Partner '" + partyName + "' should have role Provider as it is sharing data!"}];
            return [];
        }

        // we get the names of the parties as entered in partyDetails
        for (let i = 0; i < nbParties; i++) {
            if (i === partyIndex) {
                continue;
            }
            if (globals['HasRole'][i]['R'] === true) {
                const partyName = getPartyName(i, loops);
                options.push({key: "datasetsSharingTo" + i, text: partyName, value: i});
            }
        }
        if (options.length === 0) {
            // we should have least one option available
            // if empty it means that no partner had receiver role
            this.errors = [{text: "No partner with recipient role has been defined!"}];
            return [];
        }
        return [
            {
                label: "To which partners are the data of " +
                    "cohort '" + cohortName + "' transmitted by '" + partyName + "'?",
                type: "dropdown",
                options,
                multiple: true,
                allowPreliminary: true
            }
        ];
    },
    chooseNext: function (states, globals, loops) {
        const datasetIndex = loops['dataset_info_loop'].index - 1;
        const loopIndex = this.getLoopIndex(loops);
        const parties = listOfTransferNotProcessing10Parties(globals, states, datasetIndex);
        const partyIndex = parties[loopIndex - 1];
        const values = states[datasetsSharingTo.id].values[0];
        for (let j = 0; j < values.length; j++) {
            globals['Sharing'][datasetIndex][partyIndex][values[j]] = true;
        }
        globals['WhereEarlyTermination'] = 'dataset_transfer_not_processing10';
        globals['WhereEarlyTerminationInner'] = 'transfer_not_processing10_to';
        return datasetsTransferredDatatypes;
    }
});

const datasetsTransferredDatatypes = new ItemDefinition({
    id: 'datasetsTransferredDatatypes',
    title: "Transmitted Datatypes",
    group: "Cohort Primary Shares",
    buildLoopName: function (states, globals, loops) {
        const processing10loopName = "dataset_info_loop#" + globals['WhereEarlyTermination'] + "$" + loops['dataset_info_loop'].index;
        return processing10loopName + "#" + globals['WhereEarlyTerminationInner'] + "$" + loops[processing10loopName].index;
    },
    buildFields: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {
            cohortName,
            partyNameI,
            partyIndexI,
            partyName,
            datasetIndex
        } = getNamesForTransfersLoop(loopIndex, states, globals, loops);

        let options = uniqueDatatypesForParty(globals, datasetIndex, partyIndexI, "datasetsTransferredDatatypes", true);
        const label = "For cohort '" + cohortName + "', which data types are transmitted to '" + partyName + "' by '" + partyNameI + "'";
        return [
            {
                label,
                type: "dropdown",
                multiple: true,
                options
            }];
    },
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {datasetIndex, partyIndexI, partnerIndex} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        const transferredDatatypes = states[datasetsTransferredDatatypes.id].values;
        // let next = datasetTransfersNext;
        let selectedValues = transferredDatatypes[0];
        if (selectedValues.includes("all datatypes")) {
            selectedValues = uniqueDataTypesForPartyText(globals, datasetIndex, partyIndexI, false);
        }
        globals['DataTypesShared'][datasetIndex][partyIndexI][partnerIndex] = selectedValues;
        globals['Sharing'][datasetIndex][partyIndexI][partnerIndex] = true;
        if (globals['CohortOwner'][datasetIndex] === partnerIndex) {
            globals['KeepData'][datasetIndex][partyIndexI][partnerIndex] = true;
            globals['BroadUse'][datasetIndex][partyIndexI][partnerIndex] = true;
            return afterEarlyTerminationDecision;
        }
        return datasetScopeUseDecision;
    },
});

// SCOPE USE
const datasetScopeUseDecision = new ItemDefinition({
    id: 'datasetScopeUseDecision',
    isDecisionNode: true,
    buildLoopName: function (states, globals, loops) {
        const processing10loopName = "dataset_info_loop#" + globals['WhereEarlyTermination'] + "$" + loops['dataset_info_loop'].index;
        return processing10loopName + "#" + globals['WhereEarlyTerminationInner'] + "$" + loops[processing10loopName].index;
    },
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {partnerIndex} = getNamesForTransfersLoop(loopIndex, states, globals, loops);

        if (globals['Resp'][partnerIndex]['R'] === 'CO' || globals['Resp'][partnerIndex]['R'] === 'JC') {
            return datasetSharingToLimited;
        }
        if (globals['Resp'][partnerIndex]['R'] === 'MU') {
            return datasetSharingController;
        }
        return datasetTransfersNext;
    }
});

const buildGroupForTransfersLoop = function (states, globals, loops) {
    if (globals['WhereEarlyTermination'] === 'dataset_transfer_not_processing10') {
        return 'Cohort Primary Shares';
    } else {
        return 'Cohort Secondary Shares';
    }
};
const datasetSharingToLimited = new ItemDefinition({
        id: 'datasetSharingToLimited',
        buildLoopName: function (states, globals, loops) {
            const processing10loopName = "dataset_info_loop#" + globals['WhereEarlyTermination'] + "$" + loops['dataset_info_loop'].index;
            return processing10loopName + "#" + globals['WhereEarlyTerminationInner'] + "$" + loops[processing10loopName].index;
        },
        title: "Use limitation",
        buildGroup: buildGroupForTransfersLoop,
        buildFields: function (states, globals, loops) {
            const loopIndex = this.getLoopIndex(loops);
            const {cohortName, partyNameI, partyName} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
            const label = "Is use of cohort '" + cohortName + "' data received by '" + partyName + "' from " +
                "'" + partyNameI + "' limited to the scope of this agreement?";
            return [
                {
                    label,
                    type: "yesno"
                }
            ];
        },
        chooseNext: function (states, globals, loops) {
            const loopIndex = this.getLoopIndex(loops);
            const {datasetIndex, partyIndexI, partnerIndex} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
            const selectedShareToLimited = states[datasetSharingToLimited.id].values[0];
            globals['BroadUse'][datasetIndex][partyIndexI][partnerIndex] = selectedShareToLimited === 0;
            return datasetTransfersNext;
        }
    })
;

const datasetSharingController = new ItemDefinition({
    id: 'datasetSharingController',
    buildLoopName: function (states, globals, loops) {
        const processing10loopName = "dataset_info_loop#" + globals['WhereEarlyTermination'] + "$" + loops['dataset_info_loop'].index;
        return processing10loopName + "#" + globals['WhereEarlyTerminationInner'] + "$" + loops[processing10loopName].index;
    },
    title: "Identifying if data is received as controller",
    processPreliminaries: function (states, globals, loops) {
        let preliminary;
        try {
            preliminary = states[datasetSharingController.id].preliminaries[0];
        } catch (e) {
            preliminary = false;
        }
        const loopIndex = this.getLoopIndex(loops);
        const {
            datasetIndex,
            partyIndexI,
            partnerIndex
        } = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        globals['BroadUsePreliminaries'][datasetIndex][partyIndexI][partnerIndex] = preliminary;
    },
    buildGroup: buildGroupForTransfersLoop,
    buildFields: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {cohortName, partyNameI, partyName} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        const label = "Is some data of the cohort '" + cohortName + "' received by '" + partyName + "' from '" + partyNameI + "' received as controller or joint controller only and not in multiple roles or as processor?";
        return [{
            label,
            type: "yesno",
            allowPreliminary: true
        }];
    },
    chooseNext: function (states) {
        const selectedShareToController = states[datasetSharingController.id].values[0];
        if (selectedShareToController === 1) {
            return datasetSharingToControllerLimited;
        } else {
            return datasetTransfersNext;
        }
    }
});

const datasetSharingToControllerLimited = new ItemDefinition({
    id: 'datasetSharingToControllerLimited',
    buildLoopName: function (states, globals, loops) {
        const processing10loopName = "dataset_info_loop#" + globals['WhereEarlyTermination'] + "$" + loops['dataset_info_loop'].index;
        return processing10loopName + "#" + globals['WhereEarlyTerminationInner'] + "$" + loops[processing10loopName].index;
    },
    title: "Limited",
    buildGroup: buildGroupForTransfersLoop,
    buildFields: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {cohortName, partyNameI, partyName} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        const label = "Is use of cohort '" + cohortName + "' data received by '" + partyName + "' from " +
            "'" + partyNameI + "' limited to the scope of this agreement?";
        return [{
            label: label,
            type: "yesno"
        }];
    },
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {partyIndexI, partnerIndex, datasetIndex} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        const selectedShareToControllerLimited = states[datasetSharingToControllerLimited.id].values[0];
        globals['BroadUse'][datasetIndex][partyIndexI][partnerIndex] = selectedShareToControllerLimited === 0;
        return datasetTransfersNext;
    }
});

const datasetSharingToControllerKeep = new ItemDefinition({
    id: 'datasetSharingToControllerKeep',
    buildLoopName: function (states, globals, loops) {
        const processing10loopName = "dataset_info_loop#" + globals['WhereEarlyTermination'] + "$" + loops['dataset_info_loop'].index;
        return processing10loopName + "#" + globals['WhereEarlyTerminationInner'] + "$" + loops[processing10loopName].index;
    },
    title: "KeepData",
    buildGroup: buildGroupForTransfersLoop,
    buildFields: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {cohortName, partyNameI, partyName} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        const label = "Is '" + partyName + "' allowed to keep the cohort '" + cohortName + "' data received " +
            "from '" + partyNameI + "' after the termination of the contract?";
        return [{
            label: label,
            type: "yesno"
        }];
    },
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {datasetIndex, partyIndexI, partnerIndex} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        const selectedDatasetSharingToControllerKeep = states[datasetSharingToControllerKeep.id].values[0];
        globals['KeepData'][datasetIndex][partyIndexI][partnerIndex] = selectedDatasetSharingToControllerKeep === 1;
        return datasetKeepDataDecision;
    }
});

const datasetTransfersNext = new ItemDefinition({
    id: 'datasetTransfersNext',
    buildLoopName: function (states, globals, loops) {
        const processing10loopName = "dataset_info_loop#" + globals['WhereEarlyTermination'] + "$" + loops['dataset_info_loop'].index;
        return processing10loopName + "#" + globals['WhereEarlyTerminationInner'] + "$" + loops[processing10loopName].index;
    },
    title: "datasetTransfersNext",
    isDecisionNode: true,
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {datasetIndex, partyIndexI, partnerIndex} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        const broadUse = globals['BroadUse'][datasetIndex][partyIndexI][partnerIndex];
        if (broadUse === true) {
            if (isRespNotInPRSU(globals, partyIndexI, loops)) {
                return datasetPkUseConditions;
            }
        }
        // in PRSU or no broadUse
        // return datasetKeepDataDecision;
        return datasetAllowedKeepDecision;
    }
});

const datasetAllowedKeepDecision = new ItemDefinition({
    id: 'datasetAllowedKeepDecision',
    buildLoopName: function (states, globals, loops) {
        const processing10loopName = "dataset_info_loop#" + globals['WhereEarlyTermination'] + "$" + loops['dataset_info_loop'].index;
        return processing10loopName + "#" + globals['WhereEarlyTerminationInner'] + "$" + loops[processing10loopName].index;
    },
    title: "datasetAllowedKeepDecision",
    isDecisionNode: true,
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {partnerIndex} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        if (globals['Resp'][partnerIndex]['R'] === 'CO' || globals['Resp'][partnerIndex]['R'] === 'JC') {
            return datasetSharingToKeep;
        }
        if (globals['Resp'][partnerIndex]['R'] === 'MU') {
            const selectedShareToController = states[datasetSharingController.id].values[0] === 1;
            if (selectedShareToController) {
                return datasetSharingToControllerKeep;
            }
        }
        return datasetKeepDataDecision;
    }
});

const datasetSharingToKeep = new ItemDefinition({
    id: 'datasetSharingToKeep',
    buildLoopName: function (states, globals, loops) {
        const processing10loopName = "dataset_info_loop#" + globals['WhereEarlyTermination'] + "$" + loops['dataset_info_loop'].index;
        return processing10loopName + "#" + globals['WhereEarlyTerminationInner'] + "$" + loops[processing10loopName].index;
    },
    title: "Retention beyond contract duration",
    buildGroup: buildGroupForTransfersLoop,
    buildFields: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {cohortName, partyNameI, partyName} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        const label = "Is '" + partyName + "' allowed to keep the cohort '" + cohortName + "' data received " +
            "from '" + partyNameI + "' after the termination of the contract?";
        return [{
            label: label,
            type: "yesno"
        }];
    },
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {datasetIndex, partyIndexI, partnerIndex} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        const selectedShareToKeep = states[datasetSharingToKeep.id].values[0];
        globals['KeepData'][datasetIndex][partyIndexI][partnerIndex] = selectedShareToKeep === 1;
        return datasetKeepDataDecision;
    }
});
const datasetPkUseConditions = new ItemDefinition({
    id: 'datasetPkUseConditions',
    title: 'Use Conditions',
    buildGroup: buildGroupForTransfersLoop,
    buildLoopName: function (states, globals, loops) {
        const processing10loopName = "dataset_info_loop#" + globals['WhereEarlyTermination'] + "$" + loops['dataset_info_loop'].index;
        return processing10loopName + "#" + globals['WhereEarlyTerminationInner'] + "$" + loops[processing10loopName].index;
    },
    buildFields: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {cohortName, partyNameI, partyName} = getNamesForTransfersLoop(loopIndex, states, globals, loops);

        const label = partyName + " is allowed to use the data of cohort '" + cohortName + "' from party '" + partyNameI + "' also outside the activities of " +
            "this agreement (independent use). Please provide the general use conditions for such use as required " +
            "in the Use Conditions Annex.";
        return [{
            label,
            type: "noInput"
        }];
    },
    chooseNext: function () {
        return datasetPkIndependentSharing;
    }
});

const datasetPkIndependentSharing = new ItemDefinition({
    id: 'datasetPkIndependentSharing',
    title: 'Independent Sharing',
    buildGroup: buildGroupForTransfersLoop,
    buildLoopName: function (states, globals, loops) {
        const processing10loopName = "dataset_info_loop#" + globals['WhereEarlyTermination'] + "$" + loops['dataset_info_loop'].index;
        return processing10loopName + "#" + globals['WhereEarlyTerminationInner'] + "$" + loops[processing10loopName].index;
    },
    buildFields: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {cohortName, partyNameI, partyName} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        const label = "Does the use of cohort '" + cohortName + "' data from '" + partyNameI + "' outside" +
            " the agreement by '" + partyName + "' include independent secondary data sharing of the data?";
        return [{
            label: label,
            type: "yesno"
        }];
    },
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {values} = states[datasetPkIndependentSharing.id];
        const {datasetIndex, partyIndexI, partnerIndex} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        globals['IndependentSharing'][datasetIndex][partyIndexI][partnerIndex] = values[0] === 1;
        return datasetPkAdditionalConditional;
    }
});

const datasetPkAdditionalConditional = new ItemDefinition({
    id: 'datasetPkAdditionalConditional',
    title: 'Additional Conditions',
    buildGroup: buildGroupForTransfersLoop,
    buildLoopName: function (states, globals, loops) {
        const processing10loopName = "dataset_info_loop#" + globals['WhereEarlyTermination'] + "$" + loops['dataset_info_loop'].index;
        return processing10loopName + "#" + globals['WhereEarlyTerminationInner'] + "$" + loops[processing10loopName].index;
    },
    buildFields: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {cohortName, partyNameI, partyName} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        const label = "Do any additional conditions specific for the independent use by partner '" + partyName + "' of " +
            "cohort '" + cohortName + "' data from '" + partyNameI + "' apply?";
        return [{
            label,
            type: "yesno"
        }];
    },
    chooseNext: function (states) {
        const {values} = states[datasetPkAdditionalConditional.id];
        if (values[0] === 1) {
            return datasetPkAdditionalConditionalSpecify;
        } else {
            return datasetAllowedKeepDecision;
        }
    }
});

const datasetPkAdditionalConditionalSpecify = new ItemDefinition({
    id: 'datasetPkAdditionalConditionalSpecify',
    title: 'Specify additional conditions',
    buildGroup: buildGroupForTransfersLoop,
    buildLoopName: function (states, globals, loops) {
        const processing10loopName = "dataset_info_loop#" + globals['WhereEarlyTermination'] + "$" + loops['dataset_info_loop'].index;
        return processing10loopName + "#" + globals['WhereEarlyTerminationInner'] + "$" + loops[processing10loopName].index;
    },
    buildFields: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {cohortName, partyNameI, partyName} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        const label = "Specify the additional conditions specific for the independent use by partner '" + partyName + "' of" +
            " cohort '" + cohortName + "' data from '" + partyNameI + "'";
        return [{
            label,
            type: "textarea",
        }];
    },
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {datasetIndex, partyIndexI, partnerIndex} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        globals['SpecificUseCond'][datasetIndex][partyIndexI][partnerIndex] = states[datasetPkAdditionalConditionalSpecify.id].values[0];
        return datasetAllowedKeepDecision;
    }
});

// datasetPkPurposeKeep --- if at least one no keep --> datasetGeneratorAndSharingDecision
// datasetPkKeepMinimum --- if at least no generator or no sharing --> datasetPkFate
const datasetKeepDataDecision = new ItemDefinition({
    id: 'datasetKeepDataDecision',
    buildLoopName: function (states, globals, loops) {
        const processing10loopName = "dataset_info_loop#" + globals['WhereEarlyTermination'] + "$" + loops['dataset_info_loop'].index;
        return processing10loopName + "#" + globals['WhereEarlyTerminationInner'] + "$" + loops[processing10loopName].index;
    },
    title: "datasetKeepDataDecision",
    buildGroup: buildGroupForTransfersLoop,
    isDecisionNode: true,
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {datasetIndex, partyIndexI, partnerIndex} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        const keepData = globals['KeepData'][datasetIndex][partyIndexI][partnerIndex];
        if (keepData === true) {
            if (isRespNotInPRSU(globals, loopIndex - 1, loops)) {
                return datasetPkPurposeKeep;
            }
        }
        return datasetGeneratorAndSharingDecision;
    }
});


const datasetPkPurposeKeep = new ItemDefinition({
    id: 'datasetPkPurposeKeep',
    title: "PurposeKeep",
    buildGroup: buildGroupForTransfersLoop,
    buildLoopName: function (states, globals, loops) {
        const processing10loopName = "dataset_info_loop#" + globals['WhereEarlyTermination'] + "$" + loops['dataset_info_loop'].index;
        return processing10loopName + "#" + globals['WhereEarlyTerminationInner'] + "$" + loops[processing10loopName].index;
    },
    buildFields: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {cohortName, partyNameI, partyName} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        const label = "'" + partyName + "' is allowed to keep the cohort '" + cohortName + "' data from '" + partyNameI + "' after the termination" +
            " of this agreement. Please specify the scope of continued data processing.";
        return [{
            label,
            type: "dropdown",
            multiple: true,
            options: [
                {key: "datasetPkPurposeKeep-res", text: "Own research", value: "RES"},
                {key: "datasetPkPurposeKeep-sds", text: "Independent secondary data sharing", value: "SDS"},
                {key: "datasetPkPurposeKeep-arc", text: "Archiving / documentation", value: "ARC"},
                {key: "datasetPkPurposeKeep-met", text: "Method development", value: "MET"},
                {key: "datasetPkPurposeKeep-oth", text: "Other", value: "OTH"},
            ]
        }];
    },
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {datasetIndex, partyIndexI, partnerIndex} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        const {values} = states[datasetPkPurposeKeep.id];
        globals['PurposeKeep'][datasetIndex][partyIndexI][partnerIndex] = values[0];
        const isOthers = values[0].indexOf('OTH') !== -1;
        if (isOthers) {
            return datasetPkPurposeKeepSpecify;
        } else {
            return datasetPkPurposeKeepConditions;
        }
    }
});

const datasetPkPurposeKeepSpecify = new ItemDefinition({
    id: 'datasetPkPurposeKeepSpecify',
    title: "Specify PurposeKeep",
    buildGroup: buildGroupForTransfersLoop,
    buildLoopName: function (states, globals, loops) {
        const processing10loopName = "dataset_info_loop#" + globals['WhereEarlyTermination'] + "$" + loops['dataset_info_loop'].index;
        return processing10loopName + "#" + globals['WhereEarlyTerminationInner'] + "$" + loops[processing10loopName].index;
    },
    buildFields: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {cohortName, partyNameI, partyName} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        const label = "Specify the scope of continued data processing by partner '" + partyName + "' for " +
            "cohort '" + cohortName + "' data from '" + partyNameI + "'";
        return [{
            label,
            type: "textarea"
        }];
    },
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {datasetIndex, partyIndexI, partnerIndex} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        globals['OtherPurpose'][datasetIndex][partyIndexI][partnerIndex] = states[datasetPkPurposeKeepSpecify.id].values[0];
        return datasetPkPurposeKeepConditions;
    }
});

const datasetPkPurposeKeepConditions = new ItemDefinition({
    id: 'datasetPkPurposeKeepConditions',
    title: "PurposeKeep Conditions",
    buildGroup: buildGroupForTransfersLoop,
    buildLoopName: function (states, globals, loops) {
        const processing10loopName = "dataset_info_loop#" + globals['WhereEarlyTermination'] + "$" + loops['dataset_info_loop'].index;
        return processing10loopName + "#" + globals['WhereEarlyTerminationInner'] + "$" + loops[processing10loopName].index;
    },
    buildFields: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {cohortName, partyNameI, partyName} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        const label = "Do any additional conditions specific for the use by '" + partyName + "' of " +
            "cohort '" + cohortName + "' data from '" + partyNameI + "' apply?";
        return [{
            label,
            type: "yesno"
        }];
    },
    chooseNext: function (states) {
        const {values} = states[datasetPkPurposeKeepConditions.id];
        if (values[0] === 1) {
            return datasetPkPurposeKeepConditionsSpecify
        } else {
            return datasetGeneratorAndSharingDecision;
        }
    }
});
const datasetPkPurposeKeepConditionsSpecify = new ItemDefinition({
    id: 'datasetPkPurposeKeepConditionsSpecify',
    title: "PurposeKeep Conditions - Specify",
    buildGroup: buildGroupForTransfersLoop,
    buildLoopName: function (states, globals, loops) {
        const processing10loopName = "dataset_info_loop#" + globals['WhereEarlyTermination'] + "$" + loops['dataset_info_loop'].index;
        return processing10loopName + "#" + globals['WhereEarlyTerminationInner'] + "$" + loops[processing10loopName].index;
    },
    buildFields: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {cohortName, partyNameI, partyName} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        const label = "Please provide the general use conditions for such use in the Use Conditions Annex. " +
            "Specify the additional conditions specific for the use by partner" +
            " '" + partyName + "' of cohort '" + cohortName + "' data from '" + partyNameI + "'";
        return [{
            label,
            type: "textarea"
        }];
    },
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {datasetIndex, partyIndexI, partnerIndex} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        globals['SpecificKeepCond'][datasetIndex][partyIndexI][partnerIndex] = states[datasetPkPurposeKeepConditionsSpecify.id].values[0];
        return datasetGeneratorAndSharingDecision;
    }
});

const datasetGeneratorAndSharingDecision = new ItemDefinition({
    id: 'datasetGeneratorAndSharingDecision',
    buildLoopName: function (states, globals, loops) {
        const processing10loopName = "dataset_info_loop#" + globals['WhereEarlyTermination'] + "$" + loops['dataset_info_loop'].index;
        return processing10loopName + "#" + globals['WhereEarlyTerminationInner'] + "$" + loops[processing10loopName].index;
    },
    title: "datasetGeneratorAndSharingDecision",
    buildGroup: buildGroupForTransfersLoop,
    isDecisionNode: true,
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {datasetIndex, partyIndexI, partnerIndex} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        const keepData = globals['KeepData'][datasetIndex][partyIndexI][partnerIndex];
        const sharing = globals['Sharing'][datasetIndex][partyIndexI][partnerIndex];
        if (!keepData && sharing) {
            const generator = globals['Generator'][datasetIndex][partnerIndex];
            if (generator) {
                return datasetPkKeepMinimum;
            } else {
                return datasetPkFate;
            }
        } else {
            return backShareDecision;
        }
    }
});

const datasetPkKeepMinimum = new ItemDefinition({
    id: 'datasetPkKeepMinimum',
    buildLoopName: function (states, globals, loops) {
        const processing10loopName = "dataset_info_loop#" + globals['WhereEarlyTermination'] + "$" + loops['dataset_info_loop'].index;
        return processing10loopName + "#" + globals['WhereEarlyTerminationInner'] + "$" + loops[processing10loopName].index;
    },
    title: "KeepMinimum",
    buildGroup: buildGroupForTransfersLoop,
    buildFields: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {cohortName, partyNameI, partyName} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        const label = "Will '" + partyName + "' be able to keep minimum information / anonymised data" +
            " (as anonymised annotation of generated data set) for cohort" +
            " '" + cohortName + "' data from '" + partyNameI + "'?";
        return [{
            label: label,
            type: "yesno"
        }];
    },
    chooseNext: function (states) {
        const {values} = states[datasetPkKeepMinimum.id];
        if (values[0] === 1) {
            //at least one keep minimum
            return datasetPkReducedDataset;
        } else {
            return datasetPkNotGeneratorOrSharingFate;
        }
    },
});
const datasetPkNotGeneratorOrSharingFate = new ItemDefinition({
    id: 'datasetPkNotGeneratorOrSharingFate',
    buildLoopName: function (states, globals, loops) {
        const processing10loopName = "dataset_info_loop#" + globals['WhereEarlyTermination'] + "$" + loops['dataset_info_loop'].index;
        return processing10loopName + "#" + globals['WhereEarlyTerminationInner'] + "$" + loops[processing10loopName].index;
    },
    title: "Fate",
    buildGroup: buildGroupForTransfersLoop,
    buildFields: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {cohortName, partyNameI, partyName} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        const projectOrHosting = globals['POP'] === 'DH' ? 'hosting' : 'project';
        return [{
            label: `What should '${partyName}' do with the cohort '${cohortName}' data received from '${partyNameI}'
             at the end of the ${projectOrHosting}?`,
            type: "radio",
            options: [
                {key: "datasetPkNotGeneratorOrSharingFate-ded", text: "Destroy data", value: "DED"},
                {key: "datasetPkNotGeneratorOrSharingFate-red", text: "Return data", value: "RED"},
            ],
        }];
    },
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const valuesReduced = states[datasetPkNotGeneratorOrSharingFate.id].values[0];
        const {datasetIndex, partyIndexI, partnerIndex} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        globals['Fate'][datasetIndex][partyIndexI][partnerIndex] = valuesReduced;
        return backShareDecision;
    },
});
const datasetPkReducedDataset = new ItemDefinition({
    id: 'datasetPkReducedDataset',
    buildLoopName: function (states, globals, loops) {
        const processing10loopName = "dataset_info_loop#" + globals['WhereEarlyTermination'] + "$" + loops['dataset_info_loop'].index;
        return processing10loopName + "#" + globals['WhereEarlyTerminationInner'] + "$" + loops[processing10loopName].index;
    },
    title: "ReducedDataset",
    buildGroup: buildGroupForTransfersLoop,
    buildFields: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {datasetIndex, cohortName, partyNameI, partyName, partyIndexI, partnerIndex} =
            getNamesForTransfersLoop(loopIndex, states, globals, loops);
        const options = [];
        const datatypes = globals['DataTypesShared'][datasetIndex][partyIndexI][partnerIndex] || [];
        for (let d = 0; d < datatypes.length; d++) {
            const datatype = datatypes[d];
            options.push({
                key: 'datasetPkReducedDataset' + '-' + d,
                value: datatype,
                text: datatype
            });
        }
        return [{
            label: "For cohort '" + cohortName + "', data types from '" + partyNameI + "' to be kept by '" + partyName + "'",
            type: "dropdown",
            options,
            multiple: true,
            allowAdditions: true
        }];
    },
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const valuesReduced = states[datasetPkReducedDataset.id].values;
        const {datasetIndex, partyIndexI, partnerIndex} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        globals['ReducedDataset'][datasetIndex][partyIndexI][partnerIndex] = valuesReduced[0];
        globals['Fate'][datasetIndex][partyIndexI][partnerIndex] = 'AND';
        return backShareDecision;
    },
});

const datasetPkFate = new ItemDefinition({
    id: 'datasetPkFate',
    buildLoopName: function (states, globals, loops) {
        const processing10loopName = "dataset_info_loop#" + globals['WhereEarlyTermination'] + "$" + loops['dataset_info_loop'].index;
        return processing10loopName + "#" + globals['WhereEarlyTerminationInner'] + "$" + loops[processing10loopName].index;
    },
    title: "Fate",
    buildGroup: buildGroupForTransfersLoop,
    buildFields: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {cohortName, partyNameI, partyName} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        const projectOrHosting = globals['POP'] === 'DH' ? 'hosting' : 'project';
        return [{
            label: `What should '${partyName}' do with the cohort '${cohortName}' data received from '${partyNameI}' at
             the end of the '${projectOrHosting}'?`,
            type: "radio",
            options: [
                {key: "datasetPkNotGeneratorOrSharingFate-ded", text: "Destroy data", value: "DED"},
                {key: "datasetPkNotGeneratorOrSharingFate-and", text: "Anonymise data", value: "AND"},
                {key: "datasetPkNotGeneratorOrSharingFate-red", text: "Return data", value: "RED"},
                {key: "datasetPkNotGeneratorOrSharingFate-ard", text: "Archive data", value: "ARD"},
            ],
        }];
    },
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const valuesFate = states[datasetPkFate.id].values[0];
        const {datasetIndex, partyIndexI, partnerIndex} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        globals['Fate'][datasetIndex][partyIndexI][partnerIndex] = valuesFate;
        if (valuesFate === 'ARD') {
            return datasetPkFateArchiveDuration;
        } else {
            return backShareDecision;
        }
    }
});
const datasetPkFateArchiveDuration = new ItemDefinition({
    id: 'datasetPkFateArchiveDuration',
    buildLoopName: function (states, globals, loops) {
        const processing10loopName = "dataset_info_loop#" + globals['WhereEarlyTermination'] + "$" + loops['dataset_info_loop'].index;
        return processing10loopName + "#" + globals['WhereEarlyTerminationInner'] + "$" + loops[processing10loopName].index;
    },
    title: "Archive Duration",
    buildGroup: buildGroupForTransfersLoop,
    buildFields: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {cohortName, partyNameI, partyName} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        const projectOrHosting = globals['POP'] === 'DH' ? 'hosting' : 'project';
        return [{
            label: `For how long should '${partyName}' archive the cohort '${cohortName}' data received from
             '${partyNameI}' at the end of the ${projectOrHosting}?`,
            type: "text",
        }];
    },
    chooseNext: function (states, globals, loops) {
        const archiveDuration = states[datasetPkFateArchiveDuration.id].values[0];
        const loopIndex = this.getLoopIndex(loops);
        const {datasetIndex, partyIndexI, partnerIndex} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        globals['FateArchiveDuration'][datasetIndex][partyIndexI][partnerIndex] = archiveDuration;
        return backShareDecision;
    }
});
// END SCOPE USE

const backShareDecision = new ItemDefinition({
    id: 'backShareDecision',
    title: 'backShareDecision',
    buildLoopName: function (states, globals, loops) {
        const processing10loopName = "dataset_info_loop#" + globals['WhereEarlyTermination'] + "$" + loops['dataset_info_loop'].index;
        return processing10loopName + "#" + globals['WhereEarlyTerminationInner'] + "$" + loops[processing10loopName].index;
    },
    isDecisionNode: true,
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {datasetIndex, partyIndexI, partnerIndex} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        const generator = globals['Generator'][datasetIndex][partnerIndex] === true;
        const backSharing = globals['Sharing'][datasetIndex][partnerIndex][partyIndexI] === true;
        if (globals['CohortProvider'][datasetIndex] !== partyIndexI && generator && !backSharing) {
            return datasetPkBackShare;
        } else {
            // early termination
            return terminateKeepDecision;

        }
    }
});

// BACKSHARE
const datasetPkBackShare = new ItemDefinition({
    id: 'datasetPkBackShare',
    buildLoopName: function (states, globals, loops) {
        const processing10loopName = "dataset_info_loop#" + globals['WhereEarlyTermination'] + "$" + loops['dataset_info_loop'].index;
        return processing10loopName + "#" + globals['WhereEarlyTerminationInner'] + "$" + loops[processing10loopName].index;
    },
    title: "BackShare",
    buildGroup: buildGroupForTransfersLoop,
    buildFields: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {cohortName, partyNameI, partyName} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        const projectOrHosting = globals['POP'] === 'DH' ? 'hosting' : 'project';

        const label = `For cohort '${cohortName}', will '${partyName}' share its generated data with '${partyNameI}' 
        after the ${projectOrHosting}`;
        return [{
            label: label,
            type: "yesno"
        }];
    },
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {values} = states[datasetPkBackShare.id];
        const {datasetIndex, partyIndexI, partnerIndex} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        const backShare = values[0] === 1;
        globals['BackShare'][datasetIndex][partnerIndex][partyIndexI] = backShare;
        if (backShare) {
            return datasetPkConsentPurposeKeep;
        } else {
            return datasetPkConsentPurposeKeepConditions;
        }
    }
});
const datasetPkConsentPurposeKeep = new ItemDefinition({
    id: 'datasetPkConsentPurposeKeep',
    title: "PurposeKeep",
    buildGroup: buildGroupForTransfersLoop,
    buildLoopName: function (states, globals, loops) {
        const processing10loopName = "dataset_info_loop#" + globals['WhereEarlyTermination'] + "$" + loops['dataset_info_loop'].index;
        return processing10loopName + "#" + globals['WhereEarlyTerminationInner'] + "$" + loops[processing10loopName].index;
    },
    buildFields: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {cohortName, partyNameI} = getNamesForTransfersLoop(loopIndex, states, globals, loops);

        const label = "For cohort '" + cohortName + "', please specify the scope of the use" +
            " of this data by '" + partyNameI + "'";
        return [{
            label,
            type: "dropdown",
            multiple: true,
            options: [
                {key: "datasetPkConsentPurposeKeep-res", text: "Own research ", value: "RES"},
                {key: "datasetPkConsentPurposeKeep-sds", text: "Independent secondary data sharing", value: "SDS"},
                {key: "datasetPkConsentPurposeKeep-arc", text: "Archiving / documentation", value: "ARC"},
                {key: "datasetPkConsentPurposeKeep-met", text: "Method development", value: "MET"},
                {key: "datasetPkConsentPurposeKeep-oth", text: "Other", value: "OTH"},
            ]
        }];
    },
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {values} = states[datasetPkConsentPurposeKeep.id];
        const {datasetIndex, partyIndexI, partnerIndex} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        globals['PurposeKeep'][datasetIndex][partnerIndex][partyIndexI] = values[0];
        const hasOthers = 'OTH' in values[0];
        if (hasOthers) {
            return datasetPkConsentPurposeKeepSpecify;
        } else {
            return datasetPkConsentPurposeKeepConditions;
        }
    }
});
const datasetPkConsentPurposeKeepSpecify = new ItemDefinition({
    id: 'datasetPkConsentPurposeKeepSpecify',
    title: "PurposeKeep - Specify",
    buildGroup: buildGroupForTransfersLoop,
    buildLoopName: function (states, globals, loops) {
        const processing10loopName = "dataset_info_loop#" + globals['WhereEarlyTermination'] + "$" + loops['dataset_info_loop'].index;
        return processing10loopName + "#" + globals['WhereEarlyTerminationInner'] + "$" + loops[processing10loopName].index;
    },
    buildFields: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {cohortName, partyNameI} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        const label = "For cohort '" + cohortName + "', please specify the scope of the use of this data by '" + partyNameI + "'";
        return [{
            label,
            type: "textarea"
        }];
    },
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {values} = states[datasetPkConsentPurposeKeepSpecify.id];
        const {datasetIndex, partyIndexI, partnerIndex} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        globals['OtherPurpose'][datasetIndex][partnerIndex][partyIndexI] = values[0];
        return datasetPkConsentPurposeKeepConditions;
    }
});
const datasetPkConsentPurposeKeepConditions = new ItemDefinition({
    id: 'datasetPkConsentPurposeKeepConditions',
    title: "PurposeKeep Conditions",
    buildGroup: buildGroupForTransfersLoop,
    buildLoopName: function (states, globals, loops) {
        const processing10loopName = "dataset_info_loop#" + globals['WhereEarlyTermination'] + "$" + loops['dataset_info_loop'].index;
        return processing10loopName + "#" + globals['WhereEarlyTerminationInner'] + "$" + loops[processing10loopName].index;
    },
    buildFields: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {cohortName, partyNameI} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        const label = "For cohort '" + cohortName + "', do any additional conditions specific" +
            " for the use by '" + partyNameI + "' apply?";
        return [{
            label: label,
            type: "yesno"
        }];
    },
    chooseNext: function (states) {
        const {values} = states[datasetPkConsentPurposeKeepConditions.id];
        if (values[0] === 1) {
            return datasetPkConsentPurposeKeepConditionsSpecify;
        } else {
            return terminateKeep;
        }
    }
});
const datasetPkConsentPurposeKeepConditionsSpecify = new ItemDefinition({
    id: 'datasetPkConsentPurposeKeepConditionsSpecify',
    title: "PurposeKeep Conditions - Specify",
    buildGroup: buildGroupForTransfersLoop,
    buildLoopName: function (states, globals, loops) {
        const processing10loopName = "dataset_info_loop#" + globals['WhereEarlyTermination'] + "$" + loops['dataset_info_loop'].index;
        return processing10loopName + "#" + globals['WhereEarlyTerminationInner'] + "$" + loops[processing10loopName].index;
    },
    buildFields: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {cohortName, partyNameI} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        const label = "Please provide the general use conditions for such use in the Use Conditions Annex. " +
            "Specify the additional conditions specific for the use by" +
            " partner '" + partyNameI + "' for cohort '" + cohortName + "'";
        return [{
            label,
            type: "textarea"
        }]
    },
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {values} = states[datasetPkConsentPurposeKeepConditionsSpecify.id];
        const {datasetIndex, partyIndexI, partnerIndex} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        globals['SpecificKeepCond'][datasetIndex][partnerIndex][partyIndexI] = values[0];
        return terminateKeepDecision;
    }
});
// END BACKSHARE


// EARLY TERMINATION
const terminateKeepDecision = new ItemDefinition({
    id: 'terminateKeepDecision',
    title: "terminateKeepDecision",
    group: 'Term and Termination',
    isDecisionNode: true,
    buildLoopName: function (states, globals, loops) {
        const processing10loopName = "dataset_info_loop#" + globals['WhereEarlyTermination'] + "$" + loops['dataset_info_loop'].index;
        return processing10loopName + "#" + globals['WhereEarlyTerminationInner'] + "$" + loops[processing10loopName].index;
    },
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {datasetIndex, partyIndexI, partnerIndex} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        if (globals['KeepData'][datasetIndex][partyIndexI][partnerIndex] === true) {
            return terminateKeep;
        } else {
            return afterEarlyTerminationDecision;
        }
    }
});

const terminateKeep = new ItemDefinition({
    id: 'terminateKeep',
    title: "TerminateKeep",
    group: 'Term and Termination',
    buildLoopName: function (states, globals, loops) {
        const processing10loopName = "dataset_info_loop#" + globals['WhereEarlyTermination'] + "$" + loops['dataset_info_loop'].index;
        return processing10loopName + "#" + globals['WhereEarlyTerminationInner'] + "$" + loops[processing10loopName].index;
    },
    buildFields: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const projectOrHosting = globals['POP'] === 'DH' ? 'hosting' : 'project';
        const {cohortName, partyNameI, partyName} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        return [{
            label: `If the ${projectOrHosting} is being terminated prematurely for reasons of a defaulting partner,
             should '${partyName}' keep the data of cohort '${cohortName}' received from '${partyNameI}' nevertheless?`,
            type: "yesno"
        }];
    },
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {datasetIndex, partyIndexI, partnerIndex} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        const keepValues = states[terminateKeep.id].values[0];
        const terminateKeepValue = keepValues === 1;
        globals['TerminateKeep'][datasetIndex][partyIndexI][partnerIndex] = terminateKeepValue;
        if (terminateKeepValue) {
            return terminateFateTermination;
        } else {
            return terminateKeepMinimum;
        }
    }
});
const terminateFateTermination = new ItemDefinition({
    id: 'terminateFateTermination',
    buildLoopName: function (states, globals, loops) {
        const processing10loopName = "dataset_info_loop#" + globals['WhereEarlyTermination'] + "$" + loops['dataset_info_loop'].index;
        return processing10loopName + "#" + globals['WhereEarlyTerminationInner'] + "$" + loops[processing10loopName].index;
    },
    title: "FateTermination",
    group: 'Term and Termination',
    buildFields: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {cohortName, partyNameI, partyName} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        const projectOrHosting = globals['POP'] === 'DH' ? 'hosting' : 'project';
        return [{
            label: `What should '${partyName}' do with the data '${cohortName}' received from '${partyNameI}' at the end of the ${projectOrHosting}?`,
            type: "radio",
            options: [
                {key: "terminateFateTermination-ded", text: "Destroy data", value: "DED"},
                {key: "terminateFateTermination-and", text: "Anonymise data", value: "AND"},
                {key: "terminateFateTermination-red", text: "Return data", value: "RED"},
            ],
        }];
    },
    chooseNext: function (states, globals, loops) {
        const valuesFateTermination = states[terminateFateTermination.id].values[0];
        const loopIndex = this.getLoopIndex(loops);
        const {datasetIndex, partyIndexI, partnerIndex} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        globals['FateTermination'][datasetIndex][partyIndexI][partnerIndex] = valuesFateTermination;
        return afterEarlyTerminationDecision;
    }
});
const terminateKeepMinimum = new ItemDefinition({
    id: 'terminateKeepMinimum',
    buildLoopName: function (states, globals, loops) {
        const processing10loopName = "dataset_info_loop#" + globals['WhereEarlyTermination'] + "$" + loops['dataset_info_loop'].index;
        return processing10loopName + "#" + globals['WhereEarlyTerminationInner'] + "$" + loops[processing10loopName].index;
    },
    title: "KeepMinimum",
    group: 'Term and Termination',
    buildFields: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {cohortName, partyNameI, partyName} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        return [{
            label: "For cohort '" + cohortName + "', will '" + partyName + "' be able to keep minimum information / anonymised data (as anonymised annotation) of the generated dataset received from '" + partyNameI + "'?",
            type: "yesno",
        }];
    },
    chooseNext: function (states) {
        const valuesKeepMinimum = states[terminateKeepMinimum.id].values[0];
        if (valuesKeepMinimum === 1) {
            return terminateReducedDataset;
        } else {
            return terminateFateTermination2;
        }
    }
});

const terminateFateTermination2 = new ItemDefinition({
    id: 'terminateFateTermination2',
    buildLoopName: function (states, globals, loops) {
        const processing10loopName = "dataset_info_loop#" + globals['WhereEarlyTermination'] + "$" + loops['dataset_info_loop'].index;
        return processing10loopName + "#" + globals['WhereEarlyTerminationInner'] + "$" + loops[processing10loopName].index;
    },
    title: "FateTermination forced termination",
    group: 'Term and Termination',
    buildFields: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {cohortName, partyNameI, partyName} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        return [{
            label: "What should '" + partyName + "' do with the data of cohort'" + cohortName + "' received from '" + partyNameI + "' in the event of an earlier termination with a cause?",
            type: "radio",
            options: [
                {key: "terminateFateTermination2-ded", text: "Destroy data", value: "DED"},
                {key: "terminateFateTermination-red", text: "Return data", value: "RED"},
            ],
        }];
    },
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const valuesFateTermination = states[terminateFateTermination2.id].values[0];
        const {datasetIndex, partyIndexI, partnerIndex} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        globals['FateTermination'][datasetIndex][partyIndexI][partnerIndex] = valuesFateTermination;
        return afterEarlyTerminationDecision;
    }
});
const terminateReducedDataset = new ItemDefinition({
    id: 'terminateReducedDataset',
    buildLoopName: function (states, globals, loops) {
        const processing10loopName = "dataset_info_loop#" + globals['WhereEarlyTermination'] + "$" + loops['dataset_info_loop'].index;
        return processing10loopName + "#" + globals['WhereEarlyTerminationInner'] + "$" + loops[processing10loopName].index;
    },
    title: "ReducedDatasetTermination",
    group: 'Term and Termination',
    buildFields: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {
            cohortName,
            datasetIndex,
            partyIndexI,
            partnerIndex,
            partyNameI,
            partyName
        } = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        const options = [];
        const datatypes = globals['DataTypesShared'][datasetIndex][partyIndexI][partnerIndex] || [];
        for (let d = 0; d < datatypes.length; d++) {
            const datatype = datatypes[d];
            options.push({
                key: 'terminateReducedDataset' + '-' + d,
                value: datatype,
                text: datatype
            });
        }
        return [{
            label: "In case of earlier termination with a cause, specify datatypes received from '" + partyNameI + "' to" +
                " be kept by '" + partyName + "' for dataset '" + cohortName + "'?",
            type: "dropdown",
            multiple: true,
            allowAdditions: true,
            options: options
        }];
    },
    chooseNext: function (states, globals, loops) {
        const valuesReducedDataset = states[terminateReducedDataset.id].values[0];
        const loopIndex = this.getLoopIndex(loops);
        const {datasetIndex, partyIndexI, partnerIndex} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        globals['ReducedDatasetTermination'][datasetIndex][partyIndexI][partnerIndex] = valuesReducedDataset;
        globals['FateTermination'][datasetIndex][partyIndexI][partnerIndex] = 'AND';
        return afterEarlyTerminationDecision;
    }
});

// END EARLY TERMINATION

const afterEarlyTerminationDecision = new ItemDefinition({
    id: 'afterEarlyTerminationDecision',
    isDecisionNode: true,
    buildLoopName: function (states, globals, loops) {
        const processing10loopName = "dataset_info_loop#" + globals['WhereEarlyTermination'] + "$" + loops['dataset_info_loop'].index;
        return processing10loopName + "#" + globals['WhereEarlyTerminationInner'] + "$" + loops[processing10loopName].index;
    },
    title: "afterEarlyTerminationDecision",
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        // what comes next depends and where early termination was called
        if (globals['WhereEarlyTermination'] === 'dataset_transfer_not_processing10') {
            const {selectedShareTo} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
            if (loopIndex < selectedShareTo.length) {
                return this.nextLoopIteration(loops);
            } else {
                return datasetTransfersNotProcessing10EndLoop;
            }
        } else {
            const {selectedShareTo} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
            if (loopIndex < selectedShareTo.length) {
                return this.nextLoopIteration(loops);
            } else {
                return datasetTransferProcessing1112Decision;
            }
        }

    }
});
const datasetTransfersNotProcessing10EndLoop = new ItemDefinition({
    id: 'datasetTransfersNotProcessing10EndLoop',
    isDecisionNode: true,
    buildLoopName: function (states, globals, loops) {
        return "dataset_info_loop#dataset_transfer_not_processing10$" + loops['dataset_info_loop'].index;
    },
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const datasetIndex = loops['dataset_info_loop'].index - 1;
        const nbDatasets = getNumberOfDatasets(states);
        const nbParties = getNumberOfParties(states);
        let simpleCase = false;
        if (nbDatasets === 1 && nbParties === 2) {
            if (globals['HasRole'][0]['P'] && globals['HasRole'][1]['R'] &&
                !globals['HasRole'][0]['R'] && !globals['HasRole'][1]['P']) {
                // data transfer from 0 to 1
                simpleCase = true;
            }
            if (globals['HasRole'][1]['P'] && globals['HasRole'][1]['R'] &&
                !globals['HasRole'][1]['R'] && !globals['HasRole'][0]['P']) {
                // data transfer from 1 to 0
                simpleCase = true;
            }
        }
        if (!simpleCase && loopIndex < listOfTransferNotProcessing10Parties(globals, states, datasetIndex).length) {
            return this.nextLoopIteration(loops);
        } else {
            return datasetAccessDecision;
        }
    }
});
// 21
const datasetAccessDecision = new ItemDefinition({
    id: 'datasetAccessDecision',
    title: "datasetAccessDecision",
    group: 'Cohort access',
    loopName: "dataset_info_loop",
    isDecisionNode: true,
    chooseNext: function (states, globals) {
        const nbParties = getNumberOfParties(states);
        let noPartnerWithAccessRole = true;
        for (let i = 0; i < nbParties; i++) {
            if (globals['HasRole'][i]['A']) {
                noPartnerWithAccessRole = false;
                break;
            }
        }
        if (noPartnerWithAccessRole) {
            return datasetProcessing10Decision;
        }
        const nbDatasets = getNumberOfDatasets(states);
        if (nbDatasets === 1) {
            return datasetAccessNoDownloadPartnersTypes;
        }
        return datasetAccessNoDownload;
    }
});
const datasetAccessNoDownload = new ItemDefinition({
    id: 'datasetAccessNoDownload',
    title: 'Access data without downloading',
    group: 'Cohort access',
    buildFields: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const cohortName = getDatasetName(loopIndex - 1, loops);
        return [
            {
                type: "yesno",
                label: "Is data of cohort '" + cohortName + "' accessed by another partner without download?"
            }
        ]
    },
    loopName: "dataset_info_loop",
    chooseNext(states) {
        const isDataAccessedWithoutDownload = states[datasetAccessNoDownload.id].values[0] === 1;
        if (isDataAccessedWithoutDownload) {
            return datasetAccessNoDownloadPartners;
        } else {
            return datasetProcessing10Decision;
        }

    }
});

const datasetAccessNoDownloadPartners = new ItemDefinition({
    id: 'datasetAccessNoDownloadPartners',
    title: "Access data without downloading - partners",
    group: 'Cohort access',
    loopName: "dataset_info_loop",
    buildFields(states, globals, loops) {
        const nbParties = getNumberOfParties(states);
        const options = [];

        for (let i = 0; i < nbParties; i++) {
            if (globals['HasRole'][i]['A']) {
                const partyName = getPartyName(i, loops);
                options.push({
                    key: "datasetAccessNoDownloadPartners-" + i,
                    value: i,
                    text: partyName
                });
            }
        }
        if (options.length === 0) {
            // we should have least one option available
            // if empty it means that no partner had access role
            this.errors = [{text: "No partner with access role has been defined!"}];
            return [];
        }
        return [{
            label: "Specify partners having access to the data without download",
            type: "dropdown",
            options: options,
            multiple: true
        }]
    },
    chooseNext() {
        return datasetAccessNoDownloadPartnersTypes;
    }
});


const datasetAccessNoDownloadPartnersTypes = new ItemDefinition({
    id: 'datasetAccessNoDownloadPartnersTypes',
    title: "Datatypes accessed without download by each partner",
    group: 'Cohort access',
    loopName: "dataset_info_loop",
    buildFields(states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const nbParties = getNumberOfParties(states);
        const nbDatasets = getNumberOfDatasets(states);
        let selectedPartners;
        if (nbDatasets === 1) {
            // if we have only one datasets then the partners having accessed are necessarily the one with role access
            selectedPartners = [];
            for (let i = 0; i < nbParties; i++) {
                if (globals['HasRole'][i]['A']) {
                    selectedPartners.push(i);
                }
            }
        } else {
            selectedPartners = states[datasetAccessNoDownloadPartners.id].values[0];
        }

        const fields = [];
        for (let i = 0; i < nbParties; i++) {
            if (globals['HasRole'][i]['A'] && selectedPartners.indexOf(i) !== -1) {
                globals['Access'][loopIndex - 1][i] = true;
                const partyName = getPartyName(i, loops);
                const possibleDatatypesOptions = uniqueDatatypes(nbParties, globals, loopIndex - 1, "datasetAccessNoDownloadPartnersTypes-" + i);
                fields.push({
                    label: partyName,
                    type: "dropdown",
                    options: possibleDatatypesOptions,
                    multiple: true
                });
            }
        }
        return fields;
    },
    chooseNext(states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const nbParties = getNumberOfParties(states);
        const nbDatasets = getNumberOfDatasets(states);
        let selectedPartners;
        if (nbDatasets === 1) {
            // if we have only one datasets then the partners having accessed are necessarily the one with role access
            selectedPartners = [];
            for (let i = 0; i < nbParties; i++) {
                if (globals['HasRole'][i]['A']) {
                    selectedPartners.push(i);
                }
            }
        } else {
            selectedPartners = states[datasetAccessNoDownloadPartners.id].values[0];
        }
        const selectedDatatypes = states[datasetAccessNoDownloadPartnersTypes.id].values;
        let j = 0;
        for (let i = 0; i < nbParties; i++) {
            if (globals['HasRole'][i]['A'] && selectedPartners.indexOf(i) !== -1) {
                globals['DataTypesAccessed'][loopIndex - 1][i] = selectedDatatypes[j];
                j++;
            }
        }
        return datasetProcessing10Decision;
    }
});

const datasetProcessing10Decision = new ItemDefinition({
    id: 'datasetProcessing10Decision',
    title: "datasetProcessing10Decision",
    loopName: "dataset_info_loop",
    isDecisionNode: true,
    chooseNext: function (states, globals) {
        const parties = listOfTransferProcessing101112Parties(globals, states);
        if (parties.length > 0) {
            return datasetTransferProcessing101112;
        } else {
            return datasetEndLoop;
        }
    }
});

const datasetTransferProcessing101112 = new ItemDefinition({
    id: 'datasetTransferProcessing101112',
    title: "Secondary, External Sharing and International Transfers",
    group: 'Cohort Secondary Shares',
    buildLoopName: function (states, globals, loops) {
        return "dataset_info_loop#dataset_transfer_processing101112$" + loops['dataset_info_loop'].index;
    },
    buildFields: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        globals['WhereEarlyTermination'] = 'dataset_transfer_processing101112';
        globals['WhereEarlyTerminationInner'] = 'transfer_processing10_to';
        const {cohortName, partyNameI, partyIndexI} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        const kinds = [];
        if (globals['Processing'][partyIndexI][9]) {
            kinds.push('secondary')
        }
        if (globals['Processing'][partyIndexI][10]) {
            kinds.push('external')
        }
        if (globals['Processing'][partyIndexI][11]) {
            kinds.push('international')
        }
        let kind = kinds[0];
        if (kinds.length === 2) {
            kind += ' and ' + kinds[1];
        }
        if (kinds.length === 3) {
            kind += ', ' + kinds[1] + ' and ' + kinds[2];
        }
        return [{
            type: 'noInput',
            label: "The next group of questions is about " + kind + " data shares by '" + partyNameI + "' of cohort '" + cohortName + "'."
        }]
    },
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {partyIndexI} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        if (globals['Processing'][partyIndexI][9]) {
            return datasetSecondarySharingConditions;
        }
        return datasetTransferProcessing1112Decision;

    }
});

const datasetTransferProcessing1112Decision = new ItemDefinition({
    id: 'datasetTransferProcessing1112Decision',
    title: "Secondary, External and International Transmissions",
    isDecisionNode: true,
    buildLoopName: function (states, globals, loops) {
        return "dataset_info_loop#dataset_transfer_processing101112$" + loops['dataset_info_loop'].index;
    },
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {datasetIndex, partyIndexI} = getNamesForTransfersLoop(loopIndex, states, globals, loops);

        if (globals['Processing'][partyIndexI][10]) {
            globals['Safeguard'][datasetIndex][partyIndexI] = {};
            globals['ExtCountries'][datasetIndex][partyIndexI] = Array(4);
            globals['ExtShare'][partyIndexI] = true;
            return partyShareWithTypes;
        }
        if (globals['Processing'][partyIndexI][11]) {
            return partyInatShare;
        }
        return datasetTransfersProcessing101112EndLoop;
    }
});

// processing 10
const datasetSecondarySharingConditions = new ItemDefinition({
    id: 'datasetSecondarySharingConditions',
    title: "General conditions for secondary data sharing within the consortium",
    group: 'Cohort Secondary Shares',
    buildLoopName: function (states, globals, loops) {
        return "dataset_info_loop#dataset_transfer_processing101112$" + loops['dataset_info_loop'].index;
    },
    fields: [{
        type: "noInput",
        label: "General conditions for secondary data sharing / data use conditions to be specified in separate annex"
    }],
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {partyIndexI} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        if (globals['SecondSharingInternalProcessor'][partyIndexI]) {
            return datasetSoleController;
        } else {
            return datasetSecondarySharingDAC;
        }
    }
});
const datasetSoleController = new ItemDefinition({
    id: 'datasetSoleController',
    title: "Sole controller of secondary sharing",
    group: 'Cohort Secondary Shares',
    buildLoopName: function (states, globals, loops) {
        return "dataset_info_loop#dataset_transfer_processing101112$" + loops['dataset_info_loop'].index;
    },
    buildFields: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {cohortName, datasetIndex} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        const cohortProvider = globals['CohortProvider'][datasetIndex];
        const partyName = getPartyName(cohortProvider, loops);
        return [{
            type: "yesno",
            label: "Is " + partyName + " sole controller of the secondary sharing of the data of " + cohortName,
        }]
    },
    chooseNext(states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {datasetIndex, partyIndexI} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        const soleController = states[datasetSoleController.id].values[0] === 1;
        const cohortProvider = globals['CohortProvider'][datasetIndex];
        if (soleController) {
            globals["SecondSharing"][datasetIndex][partyIndexI] = [cohortProvider];
            return datasetSecondarySharingDAC;
        } else {
            return datasetControllersList;
        }
    }
})
const datasetControllersList = new ItemDefinition({
    id: 'datasetControllersList',
    title: 'List of controllers',
    group: 'Cohort Secondary Shares',
    buildLoopName: function (states, globals, loops) {
        return "dataset_info_loop#dataset_transfer_processing101112$" + loops['dataset_info_loop'].index;
    },
    buildFields: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const nbParties = getNumberOfParties(states);
        const options = [];
        for (let i = 0; i < nbParties; i++) {
            const partyName = getPartyName(i, loops);
            options.push({key: "datasetControllersList" + i, text: partyName, value: i});
        }
        return [{
            label: "Which partner(s) are controller for the secondary sharing?",
            type: "dropdown",
            options,
            multiple: true,
        }]
    },
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {datasetIndex, partyIndexI} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        const datasetControllersListValues = states[datasetControllersList.id].values[0];
        globals["SecondSharing"][datasetIndex][partyIndexI] = datasetControllersListValues;
        return datasetSecondarySharingDAC;
    }
});
const datasetSecondarySharingDAC = new ItemDefinition({
    id: 'datasetSecondarySharingDAC',
    title: "DAC",
    group: 'Cohort Secondary Shares',
    buildLoopName: function (states, globals, loops) {
        return "dataset_info_loop#dataset_transfer_processing101112$" + loops['dataset_info_loop'].index;
    },
    buildFields: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {cohortName, partyNameI} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        return [{
            label: "For secondary data sharing of data from cohort '" + cohortName + "' by '" + partyNameI + "'" +
                " within the consortium, will there be a data provider or data access committee (DAC) decision" +
                " needed before sharing?",
            type: "yesno",
            allowPreliminary: true
        }]
    },
    chooseNext: function (states, globals, loops) {
        const dacNeeded = states[datasetSecondarySharingDAC.id].values[0] === 1;
        const loopIndex = this.getLoopIndex(loops);
        const {datasetIndex, partyIndexI} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        globals['InternDAC'][datasetIndex][partyIndexI] = dacNeeded;
        return datasetTransferProcessing10Transfer;
    }
});

const datasetTransferProcessing10Transfer = new ItemDefinition({
    id: 'datasetTransferProcessing10Transfer',
    title: "Secondary sharing within the consortium?",
    group: 'Cohort Secondary Shares',
    buildLoopName: function (states, globals, loops) {
        return "dataset_info_loop#dataset_transfer_processing101112$" + loops['dataset_info_loop'].index;
    },
    buildFields: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {cohortName, partyNameI} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        return [
            {
                label: "For cohort '" + cohortName + "', will '" + partyNameI + "' as a data hub transmit data to other" +
                    " partners in the consortium?",
                type: "yesno"
            }
        ];
    },
    chooseNext: function (states) {
        const transfer = states[datasetTransferProcessing10Transfer.id].values[0] === 1;
        if (transfer) {
            return datasetTransferProcessing10;
        } else {
            return datasetTransferProcessing1112Decision;
        }
    }
});
export const datasetTransferProcessing10 = new ItemDefinition({
    id: 'datasetTransferProcessing10',
    title: "Secondary sharing within the consortium",
    group: 'Cohort Secondary Shares',
    buildLoopName: function (states, globals, loops) {
        return "dataset_info_loop#dataset_transfer_processing101112$" + loops['dataset_info_loop'].index;
    },
    buildFields: function (states, globals, loops) {
        const dacNeeded = states[datasetSecondarySharingDAC.id].values[0] === 1;
        const loopIndex = this.getLoopIndex(loops);
        const {cohortName, partyNameI, partyIndexI} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        const nbParties = getNumberOfParties(states);
        const options = [];
        for (let i = 0; i < nbParties; i++) {
            if (i === partyIndexI) {
                continue;
            }
            if (globals['HasRole'][i]['R'] === true || globals['HasRole'][i]['A'] === true) {
                const partyNameOption = getPartyName(i, loops);
                options.push({key: "datasetsSecondarySharingTo" + i, text: partyNameOption, value: i});
            }
        }
        if (options.length === 0) {
            // we should have least one option available
            // if empty it means that no partner had receiver role
            this.errors = [{text: "No partner with recipient or access only role has been defined!"}];
            return [];
        }
        const extraTextDAC = dacNeeded ? ' subject to DAC approval' : '';
        return [
            {
                label: "With which partner(s) does '" + partyNameI + "' share data of " +
                    "cohort '" + cohortName + "'" + extraTextDAC + "?",
                explanation: 'Secondary sharing within the consortium',
                type: 'dropdown',
                multiple: true,
                options,
                allowPreliminary: true
            }, {
                label: "Will all parties receive the same data sets?",
                type: "yesno",
                allowPreliminary: true
            }
        ];
    },
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {partyIndexI, datasetIndex} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        const transfer = states[datasetTransferProcessing10.id].values[0].length > 0;
        globals['Transfers'][datasetIndex][partyIndexI] = transfer;

        if (transfer) {
            const sameDatatypes = states[datasetTransferProcessing10.id].values[1] === 1;
            if (sameDatatypes) {
                return datasetsSecondarySharingDatatypes;
            } else {
                return datasetsSecondarySharingDatatypesEach;
            }
        } else {
            return datasetTransferProcessing1112Decision;
        }
    }
});

const datasetsSecondarySharingDatatypes = new ItemDefinition({
    id: 'datasetsSecondarySharingDatatypes',
    title: 'Datatypes in secondary data sharing within the consortium',
    group: 'Cohort Secondary Shares',
    buildLoopName: function (states, globals, loops) {
        return "dataset_info_loop#dataset_transfer_processing101112$" + loops['dataset_info_loop'].index;
    },
    buildFields: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {
            cohortName,
            partyNameI,
            partyIndexI,
            datasetIndex,
            selectedShareTo
        } = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        const nbParties = getNumberOfParties(states);
        const prefix = "datasetsSecondarySharingDatatypes";
        const options = uniqueDatatypesReceivedByParty(globals, datasetIndex, partyIndexI, prefix, nbParties);
        if (options.length === 0) {
            // we should have least one option available
            // if empty it means that no data was received
            this.errors = [{text: "This party has no data to share!"}];
            return [];
        }
        const fields = [];
        const partyNames = [];
        for (let i = 0; i < selectedShareTo.length; i++) {
            const partnerIndex = selectedShareTo[i];
            partyNames.push(getPartyName(partnerIndex, loops));
        }
        const label = "For cohort '" + cohortName + "', data types shared with '" + partyNames.join("', '") + "' by '" + partyNameI + "'";
        fields.push(
            {
                label,
                type: "dropdown",
                multiple: true,
                options
            }
        );
        return fields;
    },
    chooseNext: function () {
        return datasetsTransferredDatatypes10;
    },
});

const datasetsSecondarySharingDatatypesEach = new ItemDefinition({
    id: 'datasetsSecondarySharingDatatypesEach',
    title: 'Datatypes in secondary data sharing within the consortium',
    group: 'Cohort Secondary Shares',
    buildLoopName: function (states, globals, loops) {
        const processing10loopName = "dataset_info_loop#dataset_transfer_processing101112$" + loops['dataset_info_loop'].index;
        return processing10loopName + "#" + globals['WhereEarlyTerminationInner'] + "$" + loops[processing10loopName].index;
    },
    buildFields: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {
            cohortName,
            partyNameI,
            partyIndexI,
            datasetIndex,
            partyName
        } = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        const nbParties = getNumberOfParties(states);
        const prefix = "datasetsSecondarySharingDatatypes";
        const options = uniqueDatatypesReceivedByParty(globals, datasetIndex, partyIndexI, prefix, nbParties);
        if (options.length === 0) {
            // we should have least one option available
            // if empty it means that no data was received
            this.errors = [{text: "This party has no data to share!"}];
            return [];
        }
        const fields = [];
        const label = "For cohort '" + cohortName + "', data types transmitted to '" + partyName + "' by '" + partyNameI + "'";
        fields.push(
            {
                label,
                type: "dropdown",
                multiple: true,
                options
            }
        );
        return fields;
    },
    chooseNext: function () {
        return datasetsTransferredDatatypes10;
    },
});

const datasetsTransferredDatatypes10 = new ItemDefinition({
    id: 'datasetsTransferredDatatypes10',
    title: "Shared Datatypes 10",
    isDecisionNode: true,
    buildLoopName: function (states, globals, loops) {
        const processing10loopName = "dataset_info_loop#dataset_transfer_processing101112$" + loops['dataset_info_loop'].index;
        return processing10loopName + "#" + globals['WhereEarlyTerminationInner'] + "$" + loops[processing10loopName].index;
    },
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {partyIndexI, partnerIndex, datasetIndex} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        const sameDatatypes = states[datasetTransferProcessing10.id].values[1] === 1;
        let transferredDatatype;
        if (sameDatatypes) {
            transferredDatatype = states[datasetsSecondarySharingDatatypes.id].values[0];
        } else {
            transferredDatatype = states[datasetsSecondarySharingDatatypesEach.id].values[0];
        }
        if (globals['DataTypesShared'][datasetIndex][partyIndexI][partnerIndex] instanceof Array) {
            const extendedTypes = globals['DataTypesShared'][datasetIndex][partyIndexI][partnerIndex].concat(transferredDatatype);
            globals['DataTypesShared'][datasetIndex][partyIndexI][partnerIndex] = extendedTypes;
        } else {
            globals['DataTypesShared'][datasetIndex][partyIndexI][partnerIndex] = transferredDatatype;
        }
        globals['Sharing'][datasetIndex][partyIndexI][partnerIndex] = true;
        if (globals['CohortOwner'][datasetIndex] === partnerIndex) {
            globals['KeepData'][datasetIndex][partyIndexI][partnerIndex] = true;
            globals['BroadUse'][datasetIndex][partyIndexI][partnerIndex] = true;
            return afterEarlyTerminationDecision;
        }
        return datasetScopeUseDecision;
    },
});

const datasetTransfersProcessing101112EndLoop = new ItemDefinition({
    id: 'datasetTransfersProcessing101112EndLoop',
    isDecisionNode: true,
    buildLoopName: function (states, globals, loops) {
        return "dataset_info_loop#dataset_transfer_processing101112$" + loops['dataset_info_loop'].index;
    },
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        if (loopIndex < listOfTransferProcessing101112Parties(globals, states).length) {
            return this.nextLoopIteration(loops);
        } else {
            return datasetEndLoop;
        }
    }
});


// HERE

const partyShareWithTypes = new ItemDefinition({
    id: 'partyShareWithTypes',
    title: "Sharing with external parties as part of the defined processing",
    buildLoopName: function (states, globals, loops) {
        return "dataset_info_loop#dataset_transfer_processing101112$" + loops['dataset_info_loop'].index;
    },
    group: 'Cohort Secondary Shares',
    buildFields: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {cohortName, partyNameI} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        return [{
            label: "With which external parties is '" + partyNameI + "' allowed to share the data of cohort '" + cohortName + "' ?",
            type: "radio",
            options: [
                {key: "partyShareWithTypes-ap", text: "Any party", value: "AP"},
                {key: "partyShareWithTypes-ca", text: "Categories", value: "CA"},
                {key: "partyShareWithTypes-na", text: "Names", value: "NA"},
            ]
        }]
    },
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {partyIndexI, datasetIndex} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        globals['ExtPartyShared'][datasetIndex][partyIndexI] = '';
        const shareType = states[partyShareWithTypes.id].values[0];
        switch (shareType) {
            case 'AP':
                globals['ExtPartyShared'][datasetIndex][partyIndexI] = 'any external party';
                return partyShareWithTypesAP;
            case 'CA':
                return partyShareWithTypesCA;
            case 'NA':
                return partyShareWithTypesNA;
        }
    }
});

const partyShareWithTypesCA = new ItemDefinition({
    id: 'partyShareWithTypesCA',
    title: "Secondary data shares with external parties as part of the processing - categories of parties",
    group: 'Cohort Secondary Shares',
    buildLoopName: function (states, globals, loops) {
        return "dataset_info_loop#dataset_transfer_processing101112$" + loops['dataset_info_loop'].index;
    },
    buildFields: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {cohortName, partyNameI} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        return [{
            type: "dropdown",
            multiple: true,
            allowAdditions: true,
            label: "Select categories of the parties with which '" + partyNameI + "' can share data from the cohort '" + cohortName + "' .",
            options: []
        }]
    },
    chooseNext: function (states, globals, loops) {
        const categories = states[partyShareWithTypesCA.id].values[0].join();
        const loopIndex = this.getLoopIndex(loops);
        const {partyIndexI, datasetIndex} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        globals['ExtPartyShared'][datasetIndex][partyIndexI] = 'the following categories of external parties: ' + categories;
        return partyShareWithTypesAP;
    }
});

const partyShareWithTypesNA = new ItemDefinition({
    id: 'partyShareWithTypesNA',
    title: "Secondary data shares with external parties - number of parties",
    group: 'Cohort Secondary Shares',
    buildLoopName: function (states, globals, loops) {
        return "dataset_info_loop#dataset_transfer_processing101112$" + loops['dataset_info_loop'].index;
    },
    buildFields: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {cohortName, partyNameI} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        return [{
            type: "number",
            label: "How many parties are considered for the data share by '" + partyNameI + "' for the cohort '" + cohortName + "'?",
        }]
    },
    chooseNext: function () {
        return partyShareWithTypesNANames;
    }
});

const partyShareWithTypesNANames = new ItemDefinition({
    id: 'partyShareWithTypesNANames',
    title: "Secondary data shares with external parties - names of parties",
    group: 'Cohort Secondary Shares',
    buildLoopName: function (states, globals, loops) {
        return "dataset_info_loop#dataset_transfer_processing101112$" + loops['dataset_info_loop'].index;
    },
    buildFields: function (states) {
        const fields = [];
        const numberOfNames = states[partyShareWithTypesNA.id].values[0];
        for (let i = 0; i < numberOfNames; i++) {
            fields.push({
                label: "Name nº" + (i + 1),
                type: "text"
            });
        }
        return fields;
    },
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {datasetIndex, partyIndexI} = getNamesForTransfersLoop(loopIndex, states, globals, loops);

        globals['Recipients'][datasetIndex][partyIndexI] = [];
        for (let i = 0; i < states[partyShareWithTypesNANames.id].values.length; i++) {
            globals['Recipients'][datasetIndex][partyIndexI].push(states[partyShareWithTypesNANames.id].values[i])
        }
        const names = globals['Recipients'][datasetIndex][partyIndexI].join();
        globals['ExtPartyShared'][datasetIndex][partyIndexI] = 'the following external parties: ' + names;
        return partyShareWithTypesAP;
    }
});

const partyShareWithTypesAP = new ItemDefinition({
    id: 'partyShareWithTypesAP',
    title: "General conditions for secondary data sharing with externals",
    group: 'Cohort Secondary Shares',
    buildLoopName: function (states, globals, loops) {
        return "dataset_info_loop#dataset_transfer_processing101112$" + loops['dataset_info_loop'].index;
    },
    fields: [{
        type: "noInput",
        label: "General conditions for data sharing / data use conditions to be specified in separate annex"
    }],
    chooseNext: function () {
        return partyPurposeShare;
    }
});
const partyPurposeShare = new ItemDefinition({
    id: 'partyPurposeShare',
    title: "Purpose of secondary data shares with externals",
    group: 'Cohort Secondary Shares',
    buildLoopName: function (states, globals, loops) {
        return "dataset_info_loop#dataset_transfer_processing101112$" + loops['dataset_info_loop'].index;
    },
    buildFields: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {cohortName, partyNameI} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        return [{
            type: "text",
            label: "For which purpose can '" + partyNameI + "' share data of the cohort '" + cohortName + "' with external parties?",
            explanation: 'Please describe all purposes as a free text list.'
        }]
    },
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {partyIndexI, datasetIndex} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        globals['PurposeShare'][datasetIndex][partyIndexI] = states[partyPurposeShare.id].values[0];
        const pop = globals['POP'];
        if (pop === 'DH') {
            globals['AllShare'][datasetIndex][partyIndexI] = true;
            globals['DataToBeShared'][datasetIndex][partyIndexI] = ['all'];
            if (globals['SecondSharingExternalProcessor'][partyIndexI]) {
                return partyExternalSoleController;
            } else {
                return partyExtShareDAC;
            }
        }
        else {
            return partyAllShare;
        }
    }
});


const partyAllShare = new ItemDefinition({
    id: 'partyAllShare',
    title: "Scope of shared data within secondary data sharing with externals",
    group: 'Cohort Secondary Shares',
    buildLoopName: function (states, globals, loops) {
        return "dataset_info_loop#dataset_transfer_processing101112$" + loops['dataset_info_loop'].index;
    },
    buildFields: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {cohortName, partyNameI} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        return [{
            type: "yesno",
            label: "Will ALL data of the cohort '" + cohortName + "' deposited with '" + partyNameI + "' be shared with external parties?",
        }]
    },
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {partyIndexI, datasetIndex} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        const allShare = states[partyAllShare.id].values[0] === 1;
        globals['AllShare'][datasetIndex][partyIndexI] = allShare;
        if (allShare) {
            globals['DataToBeShared'][datasetIndex][partyIndexI] = ['all'];
            if (globals['SecondSharingExternalProcessor'][partyIndexI]) {
                return partyExternalSoleController;
            } else {
                return partyExtShareDAC;
            }
        }
        return partyExtShareData;
    }
});

const partyExtShareData = new ItemDefinition({
    id: 'partyExtShareData',
    title: "Secondary data sharing with externals - data types",
    group: 'Cohort Secondary Shares',
    buildLoopName: function (states, globals, loops) {
        return "dataset_info_loop#dataset_transfer_processing101112$" + loops['dataset_info_loop'].index;
    },
    buildFields: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {cohortName, partyNameI, datasetIndex} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        const allDatatypesCohort = new Set();
        getAllUniqueDatatypesForCohort(globals, datasetIndex, allDatatypesCohort);
        const options = Array.from(allDatatypesCohort).map((dataType, index) => {
            return {
                key: "partyExtShareData-" + index, value: dataType, text: dataType
            }
        });
        return [
            {
                label: "Which datatypes of cohort '" + cohortName + "' will be shared by '" + partyNameI + "' with external parties?",
                type: "dropdown",
                multiple: true,
                allowAdditions: true,
                options
            }
        ]
    },
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {partyIndexI, datasetIndex} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        globals['DataToBeShared'][datasetIndex][partyIndexI] = states[partyExtShareData.id].values[0];
        if (globals['SecondSharingExternalProcessor']) {
            return partyExternalSoleController;
        } else {
            return partyExtShareDAC;
        }
    }
});
const partyExternalSoleController = new ItemDefinition({
    id: 'partyExternalSoleController',
    title: "Sole controller of external sharing",
    group: 'Cohort Secondary Shares',
    buildLoopName: function (states, globals, loops) {
        return "dataset_info_loop#dataset_transfer_processing101112$" + loops['dataset_info_loop'].index;
    },
    buildFields: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {cohortName, datasetIndex} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        const cohortProvider = globals['CohortProvider'][datasetIndex];
        const partyName = getPartyName(cohortProvider, loops);
        return [{
            type: "yesno",
            label: "Is " + partyName + " sole controller of the external data sharing of the data of " + cohortName,
        }]
    },
    chooseNext(states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {datasetIndex, partyIndexI} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        const soleController = states[partyExternalSoleController.id].values[0] === 1;
        const cohortProvider = globals['CohortProvider'][datasetIndex];
        if (soleController) {
            globals["ExternalSharing"][datasetIndex][partyIndexI] = [cohortProvider];
            return partyExtShareDAC;
        } else {
            return partyExternalControllersList;
        }
    }
})

const partyExternalControllersList = new ItemDefinition({
    id: 'partyExternalControllersList',
    title: 'List of controllers - external sharing',
    group: 'Cohort Secondary Shares',
    buildLoopName: function (states, globals, loops) {
        return "dataset_info_loop#dataset_transfer_processing101112$" + loops['dataset_info_loop'].index;
    },
    buildFields: function (states, globals, loops) {
        const nbParties = getNumberOfParties(states);
        const options = [];
        for (let i = 0; i < nbParties; i++) {
            const partyName = getPartyName(i, loops);
            options.push({key: "partyExternalControllersList" + i, text: partyName, value: i});
        }
        return [{
            label: "Which partner(s) are controller for the external sharing?",
            type: "dropdown",
            options,
            multiple: true,
        }]
    },
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {datasetIndex, partyIndexI} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        globals["ExternalSharing"][datasetIndex][partyIndexI] = states[partyExternalControllersList.id].values[0];
        return partyExtShareDAC;
    }
});
const partyExtShareDAC = new ItemDefinition({
    id: 'partyExtShareDAC',
    title: "Conditions for secondary sharing with external parties as part of the defined processing",
    group: 'Cohort Secondary Shares',
    buildLoopName: function (states, globals, loops) {
        return "dataset_info_loop#dataset_transfer_processing101112$" + loops['dataset_info_loop'].index;
    },
    buildFields: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {cohortName, partyNameI} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        return [{
            type: "yesno",
            label: "Is a data access committee defined by the provider needed if '" + partyNameI + "' shares data of the cohort '" + cohortName + "' with external parties?",
        }]
    },
    chooseNext: function (states) {
        const dacNeeded = states[partyExtShareDAC.id].values[0] === 1;
        if (dacNeeded) {
            return partyExtShareDacDescription;
        } else {
            return partyInatShare;
        }
    }
});

const partyExtShareDacDescription = new ItemDefinition({
    id: 'partyExtShareDacDescription',
    title: "DAC description for secondary sharing with external parties",
    group: 'Cohort Secondary Shares',
    buildLoopName: function (states, globals, loops) {
        return "dataset_info_loop#dataset_transfer_processing101112$" + loops['dataset_info_loop'].index;
    },
    buildFields: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {cohortName, partyNameI} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        return [{
            type: "textarea",
            label: "Please describe the DAC and the procedures of DAC involvement for the secondary sharing of data from cohort '" + cohortName + "' by '" + partyNameI + "' with external parties",
        }]
    },
    chooseNext: function () {
        return partyInatShare;
    }
});

const partyInatShare = new ItemDefinition({
    id: 'partyInatShare',
    title: "Data sharing outside the EEA",
    group: 'Cohort Secondary Shares',
    buildLoopName: function (states, globals, loops) {
        return "dataset_info_loop#dataset_transfer_processing101112$" + loops['dataset_info_loop'].index;
    },
    buildFields: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {cohortName, partyNameI} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        const label = "Could data of cohort '" + cohortName + "' be shared by '" + partyNameI + "' with an external party outside the EEA, either by data transmission or access provision?";
        return [{
            label: label,
            type: "yesno"
        }];
    },
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {partyIndexI, datasetIndex} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        const inatShare = states[partyInatShare.id].values[0] === 1;
        globals['HasInatTransfer'][datasetIndex][partyIndexI] = inatShare;
        if (inatShare) {
            return partyAllSharedOutsideEu;
        } else {
            return partySamplesInvolved;
        }
    }
});

// all data are shared externally

const partyAllSharedOutsideEu = new ItemDefinition({
    id: 'partyAllSharedOutsideEu',
    title: "Scope of data sharing outside the EEA",
    group: 'Cohort Secondary Shares',
    buildLoopName: function (states, globals, loops) {
        return "dataset_info_loop#dataset_transfer_processing101112$" + loops['dataset_info_loop'].index;
    },
    buildFields: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {cohortName, partyNameI} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        return [{
            type: "yesno",
            label: "Will ALL data of cohort '" + cohortName + "' be shared by '" + partyNameI + "' with external parties outside the EEA?",
        }]
    },
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {partyIndexI, datasetIndex} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        const allSharedOutsideEu = states[partyAllSharedOutsideEu.id].values[0] === 1;
        if (allSharedOutsideEu) {
            globals['InatDataToBeShared'][datasetIndex][partyIndexI] = ['all'];
            return partyTransferWithoutLimitations;
        } else {
            return partyInatDataToBeShared;
        }
    }
});
const partyInatDataToBeShared = new ItemDefinition({
    id: 'partyInatDataToBeShared',
    title: "Data types to be shared outside the EEA",
    group: 'Cohort Secondary Shares',
    buildLoopName: function (states, globals, loops) {
        return "dataset_info_loop#dataset_transfer_processing101112$" + loops['dataset_info_loop'].index;
    },
    buildFields: function (states, globals, loops) {

        const loopIndex = this.getLoopIndex(loops);
        const {cohortName, partyNameI, datasetIndex} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        const allDatatypesCohort = new Set();
        getAllUniqueDatatypesForCohort(globals, datasetIndex, allDatatypesCohort);
        const options = Array.from(allDatatypesCohort).map((dataType, index) => {
            return {
                key: "partyInatDataToBeShared-" + index, value: dataType, text: dataType
            }
        });
        return [
            {
                label: "Please specify which data types of cohort '" + cohortName + "' will be shared by '" + partyNameI + "', with external parties outside the EEA?",
                type: "dropdown",
                multiple: true,
                allowAdditions: true,
                options
            }
        ]
    },
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {partyIndexI, datasetIndex} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        globals['InatDataToBeShared'][datasetIndex][partyIndexI] = states[partyInatDataToBeShared.id].values[0];
        return partyTransferWithoutLimitations;
    }
});


// limitations
const partyTransferWithoutLimitations = new ItemDefinition({
    id: 'partyTransferWithoutLimitations',
    title: "Limitation of countries outside the EEA",
    group: 'Cohort Secondary Shares',
    buildLoopName: function (states, globals, loops) {
        return "dataset_info_loop#dataset_transfer_processing101112$" + loops['dataset_info_loop'].index;
    },
    buildFields: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {cohortName, partyNameI} = getNamesForTransfersLoop(loopIndex, states, globals, loops);

        return [{
            type: "yesno",
            label: "Are the data shares of cohort '" + cohortName + "' by '" + partyNameI + "' with external parties outside the EEA without a limitation of countries?",
        }]
    },
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {datasetIndex, partyIndexI} = getNamesForTransfersLoop(loopIndex, states, globals, loops);

        const unlimited = states[partyTransferWithoutLimitations.id].values[0] === 1;
        globals['Unlimited'][datasetIndex][partyIndexI] = unlimited;
        if (unlimited) {
            globals['ExtCountries'][datasetIndex][partyIndexI][0] = 'any country or international organisation';
            return partySafeGuardsUnlimited;
        } else {
            return partyTransferLimited;
        }
    }
});


const partyTransferLimited = new ItemDefinition({
    id: 'partyTransferLimited',
    title: "Specification of limitations on countries outside the EEA",
    group: 'Cohort Secondary Shares',
    buildLoopName: function (states, globals, loops) {
        return "dataset_info_loop#dataset_transfer$" + loops['dataset_info_loop'].index;
    },
    buildFields: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {cohortName, partyNameI} = getNamesForTransfersLoop(loopIndex, states, globals, loops);

        return [{
            label: "Please specify the limitations for sharing of data from cohort '" + cohortName + "' by '" + partyNameI + "' .",
            type: "dropdown",
            options: [
                {
                    key: "partyTransferLimited-T",
                    text: "Categories of countries / international organisations",
                    value: "T"
                },
                {key: "partyTransferLimited-N", text: "Specific countries only", value: "N"},
            ]
        }]
    },
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {datasetIndex, partyIndexI} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        const type = states[partyTransferLimited.id].values[0];
        globals['TransferLimited'][datasetIndex][partyIndexI] = type;
        if (type === 'T') {
            return partyTypesOfCountries;
        } else {
            return partyCountriesConsidered;
        }
    }
});
const partyTypesOfCountries = new ItemDefinition({
    id: 'partyTypesOfCountries',
    title: "Categories of countries",
    group: 'Cohort Secondary Shares',
    buildLoopName: function (states, globals, loops) {
        return "dataset_info_loop#dataset_transfer_processing101112$" + loops['dataset_info_loop'].index;
    },
    buildFields: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {cohortName, partyNameI} = getNamesForTransfersLoop(loopIndex, states, globals, loops);

        return [{
            label: "Which categories of countries / international organisations can data of cohort '" + cohortName + "' be shared with by '" + partyNameI + "' ?",
            type: "dropdown",
            multiple: true,
            options: [
                {
                    key: "partyTypesOfCountries-AD",
                    text: "Countries or international organisations with adequacy decisions covering the processing",
                    value: "AD"
                },
                {
                    key: "partyTypesOfCountries-IO",
                    text: "International organisation(s) without adequacy decisions (IO)",
                    value: "IO"
                },
            ]
        }];
    },
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {datasetIndex, partyIndexI} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        const types = states[partyTypesOfCountries.id].values[0];
        if (types.indexOf('AD') !== -1) {
            const extCountries = 'Countries or international organisations';
            globals['ExtCountries'][datasetIndex][partyIndexI][1] = extCountries;
            globals['Safeguard'][datasetIndex][partyIndexI][extCountries] = 'AD';
        }
        if (types.indexOf('IO') !== -1) {
            globals['ExtCountries'][datasetIndex][partyIndexI][2] = 'International organisations';
            return partySafeGuardsIO;
        }
        return partySamplesInvolved;
    }
});

const partyCountriesConsidered = new ItemDefinition({
    id: 'partyCountriesConsidered',
    title: "Relevant countries for sharing outside the EEA",
    group: 'Cohort Secondary Shares',
    buildLoopName: function (states, globals, loops) {
        return "dataset_info_loop#dataset_transfer_processing101112$" + loops['dataset_info_loop'].index;
    },
    buildFields: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {cohortName, partyNameI} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        return [{
            label: "Please specify the countries outside the EEA that '" + partyNameI + "' can share data of cohort '" + cohortName + "' with",
            type: "dropdown",
            options: makeOptions("partyCountriesConsidered", COUNTRIES),
            multiple: true
        }];
    },
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const selectedCountries = states[partyCountriesConsidered.id].values[0];
        const {datasetIndex, partyIndexI} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        globals['ExtCountries'][datasetIndex][partyIndexI] = Array(selectedCountries.length);
        let next = partySamplesInvolved;
        for (let i = 0; i < selectedCountries.length; i++) {
            const selectedCountry = selectedCountries[i];
            globals['ExtCountries'][datasetIndex][partyIndexI][i] = selectedCountry;
            const isEu = COUNTRIES[selectedCountry].EEA;
            if (isEu) {
                globals['Safeguard'][datasetIndex][partyIndexI][selectedCountry] = "NN";
            } else {
                const isAD = COUNTRIES[selectedCountry].AD;
                if (isAD) {
                    globals['Safeguard'][datasetIndex][partyIndexI][selectedCountry] = "AD";
                } else {
                    next = partySafeGuardsPS;
                }
            }
        }
        return next;
    }
});
// safeguards

const partySafeGuardsUnlimited = new ItemDefinition({
    id: 'partySafeGuardsUnlimited',
    title: "Safeguards",
    group: 'Cohort Secondary Shares',
    buildLoopName: function (states, globals, loops) {
        return "dataset_info_loop#dataset_transfer_processing101112$" + loops['dataset_info_loop'].index;
    },
    buildFields: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {cohortName, partyNameI} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        return [{
            label: "What are the safeguards or derogations for the sharing of data from cohort '" + cohortName + "' by '" + partyNameI + "' with external parties outside the EEA?",
            type: "dropdown",
            options: buildSafeGuardsOptions("partySafeGuardsUnlimited")
        }]
    },
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const safeguards = states[partySafeGuardsUnlimited.id].values[0];
        const {datasetIndex, partyIndexI} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        globals['Safeguard'][datasetIndex][partyIndexI]['any country or international organisation'] = safeguards;
        return partySamplesInvolved;
    }
});

const partySafeGuardsIO = new ItemDefinition({
    id: 'partySafeGuardsIO',
    title: "Safeguards",
    group: 'Cohort Secondary Shares',
    buildLoopName: function (states, globals, loops) {
        return "dataset_info_loop#dataset_transfer_processing101112$" + loops['dataset_info_loop'].index;
    },
    buildFields: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {cohortName, partyNameI} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        return [{
            label: "What are the safeguards or derogations for the sharing of data from cohort '" + cohortName + "' by '" + partyNameI + "' outside the EEA?",
            type: "dropdown",
            options: buildSafeGuardsOptions("partySafeGuardsIO")
        }];
    },
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const safeguards = states[partySafeGuardsIO.id].values[0];
        const {datasetIndex, partyIndexI} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        globals['Safeguard'][datasetIndex][partyIndexI]['International organisations'] = safeguards;
        return partySamplesInvolved;
    }
});

const partySafeGuardsPS = new ItemDefinition({
    id: 'partySafeGuardsPS',
    title: "Safeguards",
    group: 'Cohort Secondary Shares',
    buildLoopName: function (states, globals, loops) {
        return "dataset_info_loop#dataset_transfer_processing101112$" + loops['dataset_info_loop'].index;
    },
    buildFields: function (states, globals, loops) {
        const fields = [];
        const loopIndex = this.getLoopIndex(loops);
        const {cohortName, partyNameI} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        const selectedCountries = states[partyCountriesConsidered.id].values[0];
        for (let i = 0; i < selectedCountries.length; i++) {
            const selectedCountry = selectedCountries[i];
            const isEu = COUNTRIES[selectedCountry].EEA;
            const isAD = COUNTRIES[selectedCountry].AD;
            if (isEu || isAD) {
                continue;
            }
            const countryLabel = COUNTRIES[selectedCountry].label;
            fields.push({
                label: "What are the chosen safeguards or derogations that '" + partyNameI + "' should use to share data of cohort '" + cohortName + "' with " + countryLabel,
                type: "dropdown",
                options: buildSafeGuardsOptions("partySafeGuardsPS" + i)
            });
        }
        return fields;
    },
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {datasetIndex, partyIndexI} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        const safeguards = states[partySafeGuardsPS.id].values;
        const selectedCountries = states[partyCountriesConsidered.id].values[0];
        let j = 0;
        for (let i = 0; i < selectedCountries.length; i++) {
            const selectedCountry = selectedCountries[i];
            const isEu = COUNTRIES[selectedCountry].EEA;
            const isAD = COUNTRIES[selectedCountry].AD;
            if (isEu || isAD) {
                continue;
            }
            globals['Safeguard'][datasetIndex][partyIndexI][selectedCountry] = safeguards[j];
            j++;
        }
        return partySamplesInvolved;
    }
});

// return datasetTransfersProcessing101112EndLoop
// samples
const partySamplesInvolved = new ItemDefinition({
    id: 'partySamplesInvolved',
    title: "partySamplesInvolved",
    group: 'Cohort Secondary Shares',
    buildLoopName: function (states, globals, loops) {
        return "dataset_info_loop#dataset_transfer_processing101112$" + loops['dataset_info_loop'].index;
    },
    isDecisionNode: true,
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {partyIndexI, datasetIndex} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        const samplesProviders = globals['SamplesProviders'][datasetIndex][partyIndexI];
        const samplesReceivers = globals['SamplesReceivers'][datasetIndex][partyIndexI];
        if (samplesProviders || samplesReceivers) {
            return partyInatSamplesShare;
        } else {
            return datasetTransfersProcessing101112EndLoop;
        }
    }
});
const partyInatSamplesShare = new ItemDefinition({
    id: 'partyInatSamplesShare',
    title: "Transmission of samples outside the EEA",
    group: 'Cohort Secondary Shares',
    buildLoopName: function (states, globals, loops) {
        return "dataset_info_loop#dataset_transfer_processing101112$" + loops['dataset_info_loop'].index;
    },
    buildFields: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {cohortName, partyNameI} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        return [{
            type: "yesno",
            label: "Will samples of cohort '" + cohortName + "' be shared by '" + partyNameI + "' with external parties outside the EEA?",
        }]
    },
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {partyIndexI, datasetIndex} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        const inatSamplesShare = states[partyInatSamplesShare.id].values[0] === 1;
        globals['InatSampleShare'][datasetIndex][partyIndexI] = inatSamplesShare;
        if (inatSamplesShare) {
            return partyAllSamplesOutsideEu;
        } else {
            return datasetTransfersProcessing101112EndLoop;
        }
    }
});

const partyAllSamplesOutsideEu = new ItemDefinition({
    id: 'partyAllSamplesOutsideEu',
    title: "Scope of sample transmission",
    group: 'Cohort Secondary Shares',
    buildLoopName: function (states, globals, loops) {
        return "dataset_info_loop#dataset_transfer_processing101112$" + loops['dataset_info_loop'].index;
    },
    buildFields: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {cohortName, partyNameI} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        return [{
            type: "yesno",
            label: "Will all samples rather than only a subset of samples of cohort '" + cohortName + "' be transmitted by '" + partyNameI + "' to external parties outside the EEA?",
        }]
    },
    chooseNext: function (states, globals, loops) {
        const allSamplesOutsideEu = states[partyAllSamplesOutsideEu.id].values[0] === 1;
        const loopIndex = this.getLoopIndex(loops);
        const {partyIndexI, datasetIndex} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        globals['AllSamplesOutsideEu'][datasetIndex][partyIndexI] = allSamplesOutsideEu;
        if (allSamplesOutsideEu) {
            globals['InatSamplesToBeShared'][datasetIndex][partyIndexI] = 'ALL';
            return partyInatShareDecision;
        } else {
            return partyAllSamplesOutsideEuSpecify;
        }
    }
});

const partyAllSamplesOutsideEuSpecify = new ItemDefinition({
    id: 'partyAllSamplesOutsideEuSpecify',
    title: "Samples to be transmitted outside the EEA",
    group: 'Cohort Secondary Shares',
    buildLoopName: function (states, globals, loops) {
        return "dataset_info_loop#dataset_transfer_processing101112$" + loops['dataset_info_loop'].index;
    },
    buildFields: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {cohortName, partyNameI} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        return [{
            type: "text",
            label: "Please specify which subset of samples from cohort '" + cohortName + "' will be transmitted by '" + partyNameI + "' to external parties outside the EEA?",
        }]
    },
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {datasetIndex, partyIndexI} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        globals['InatSamplesToBeShared'][datasetIndex][partyIndexI] = states[partyAllSamplesOutsideEuSpecify.id].values[0];
        return partyInatShareDecision;
    }
});

const partyInatShareDecision = new ItemDefinition({
    id: 'partyInatShareDecision',
    title: "partyInatShareDecision",
    group: 'Cohort Secondary Shares',
    buildLoopName: function (states, globals, loops) {
        return "dataset_info_loop#dataset_transfer_processing101112$" + loops['dataset_info_loop'].index;
    },
    isDecisionNode: true,
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {datasetIndex, partyIndexI} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        const hasInatTransfer = globals['HasInatTransfer'][datasetIndex][partyIndexI];
        if (hasInatTransfer) {
            return partyTransferSamplesSameData;
        } else {
            return datasetTransfersProcessing101112EndLoop;
        }
    }
});

const partyTransferSamplesSameData = new ItemDefinition({
    id: 'partyTransferSamplesSameData',
    title: "Samples outside the EEA, same as data?",
    group: 'Cohort Secondary Shares',
    buildLoopName: function (states, globals, loops) {
        return "dataset_info_loop#dataset_transfer_processing101112$" + loops['dataset_info_loop'].index;
    },
    buildFields: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {cohortName, partyNameI} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        return [{
            type: "yesno",
            label: "Will samples of cohort '" + cohortName + "' be shared by '" + partyNameI + "' with the same third countries outside the EEA as the data?",
        }]
    },
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {datasetIndex, partyIndexI} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        const sameShare = states[partyTransferSamplesSameData.id].values[0] === 1;
        globals['SampleSameShare'][datasetIndex][partyIndexI] = sameShare;
        if (sameShare) {
            globals['SampleUnlimited'][datasetIndex][partyIndexI] = globals['Unlimited'][datasetIndex][partyIndexI];
            globals['SampleExtCountries'][datasetIndex][partyIndexI] = globals['ExtCountries'][datasetIndex][partyIndexI];
            globals['SampleSafeguard'][datasetIndex][partyIndexI] = globals['Safeguard'][datasetIndex][partyIndexI];
            globals['SampleTransferLimited'][datasetIndex][partyIndexI] = globals['TransferLimited'][datasetIndex][partyIndexI];
            return datasetTransfersProcessing101112EndLoop;
            // set all variables for samples as for data
        } else {
            // ask the questions for samples
            return partySampleTransferWithoutLimitations;
        }
    }
});
// samples inat questions when different from data

const partySampleTransferWithoutLimitations = new ItemDefinition({
    id: 'partySampleTransferWithoutLimitations',
    title: "Samples - Limitation of countries outside the EEA",
    group: 'Cohort Secondary Shares',
    buildLoopName: function (states, globals, loops) {
        return "dataset_info_loop#dataset_transfer_processing101112$" + loops['dataset_info_loop'].index;
    },
    buildFields: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {cohortName, partyNameI} = getNamesForTransfersLoop(loopIndex, states, globals, loops);

        return [{
            type: "yesno",
            label: "Are the samples shares of cohort '" + cohortName + "' by '" + partyNameI + "' with external parties outside the EEA without a limitation of countries?",
        }]
    },
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {datasetIndex, partyIndexI} = getNamesForTransfersLoop(loopIndex, states, globals, loops);

        const unlimited = states[partySampleTransferWithoutLimitations.id].values[0] === 1;
        globals['SampleUnlimited'][datasetIndex][partyIndexI] = unlimited;
        if (unlimited) {
            globals['SampleExtCountries'][datasetIndex][partyIndexI] = Array(4);
            globals['SampleSafeguard'][datasetIndex][partyIndexI] = {};
            globals['SampleExtCountries'][datasetIndex][partyIndexI][0] = 'any country or international organisation';
            return partySampleSafeGuardsUnlimited;
        } else {
            return partySampleTransferLimited;
        }
    }
});


const partySampleTransferLimited = new ItemDefinition({
    id: 'partySampleTransferLimited',
    title: "Samples - Specification of limitations on countries outside the EEA",
    group: 'Cohort Secondary Shares',
    buildLoopName: function (states, globals, loops) {
        return "dataset_info_loop#dataset_transfer$" + loops['dataset_info_loop'].index;
    },
    buildFields: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {cohortName, partyNameI} = getNamesForTransfersLoop(loopIndex, states, globals, loops);

        return [{
            label: "Please specify the limitations for sharing of samples from cohort '" + cohortName + "' by '" + partyNameI + "' .",
            type: "dropdown",
            options: [
                {
                    key: "partySampleTransferLimited-T",
                    text: "Categories of countries / international organisations",
                    value: "T"
                },
                {key: "partySampleTransferLimited-N", text: "Specific countries only", value: "N"},
            ]
        }]
    },
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {datasetIndex, partyIndexI} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        const type = states[partySampleTransferLimited.id].values[0];
        globals['SampleTransferLimited'][datasetIndex][partyIndexI] = type;
        if (type === 'T') {
            return partySampleTypesOfCountries;
        } else {
            return partySampleCountriesConsidered;
        }
    }
});
const partySampleTypesOfCountries = new ItemDefinition({
    id: 'partySampleTypesOfCountries',
    title: "Samples - Categories of countries",
    group: 'Cohort Secondary Shares',
    buildLoopName: function (states, globals, loops) {
        return "dataset_info_loop#dataset_transfer_processing101112$" + loops['dataset_info_loop'].index;
    },
    buildFields: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {cohortName, partyNameI} = getNamesForTransfersLoop(loopIndex, states, globals, loops);

        return [{
            label: "Which categories of countries / international organisations can samples of cohort '" + cohortName + "' be shared with by '" + partyNameI + "' ?",
            type: "dropdown",
            multiple: true,
            options: [
                {
                    key: "partySampleTypesOfCountries-AD",
                    text: "Countries or international organisations with adequacy decisions covering the processing",
                    value: "AD"
                },
                {
                    key: "partySampleTypesOfCountries-IO",
                    text: "International organisation(s) without adequacy decisions (IO)",
                    value: "IO"
                },
            ]
        }];
    },
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {datasetIndex, partyIndexI} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        const types = states[partySampleTypesOfCountries.id].values[0];
        if (types.indexOf('AD') !== -1) {
            const extCountries = 'Countries or international organisations';
            globals['SampleExtCountries'][datasetIndex][partyIndexI][1] = extCountries;
            globals['SampleSafeguard'][datasetIndex][partyIndexI][extCountries] = 'AD';
        }
        if (types.indexOf('IO') !== -1) {
            globals['SampleExtCountries'][datasetIndex][partyIndexI][2] = 'International organisations';
            return partySampleSafeGuardsIO;
        }
        return datasetTransfersProcessing101112EndLoop;
    }
});

const partySampleCountriesConsidered = new ItemDefinition({
    id: 'partySampleCountriesConsidered',
    title: "Samples - Relevant countries for sharing outside the EEA",
    group: 'Cohort Secondary Shares',
    buildLoopName: function (states, globals, loops) {
        return "dataset_info_loop#dataset_transfer_processing101112$" + loops['dataset_info_loop'].index;
    },
    buildFields: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {cohortName, partyNameI} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        return [{
            label: "Please specify the countries outside the EEA that '" + partyNameI + "' can share samples of cohort '" + cohortName + "' with",
            type: "dropdown",
            options: makeOptions("partyCountriesConsidered", COUNTRIES),
            multiple: true
        }];
    },
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const selectedCountries = states[partySampleCountriesConsidered.id].values[0];
        const {datasetIndex, partyIndexI} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        globals['SampleExtCountries'][datasetIndex][partyIndexI] = Array(selectedCountries.length);
        globals['SampleSafeguard'][datasetIndex][partyIndexI] = {};
        let next = datasetTransfersProcessing101112EndLoop;
        for (let i = 0; i < selectedCountries.length; i++) {
            const selectedCountry = selectedCountries[i];
            globals['SampleExtCountries'][datasetIndex][partyIndexI][i] = selectedCountry;
            const isEu = COUNTRIES[selectedCountry].EEA;
            if (isEu) {
                globals['SampleSafeguard'][datasetIndex][partyIndexI][selectedCountry] = "NN";
            } else {
                const isAD = COUNTRIES[selectedCountry].AD;
                if (isAD) {
                    globals['SampleSafeguard'][datasetIndex][partyIndexI][selectedCountry] = "AD";
                } else {
                    next = partySampleSafeGuardsPS;
                }
            }
        }
        return next;
    }
});
// safeguards

const partySampleSafeGuardsUnlimited = new ItemDefinition({
    id: 'partySampleSafeGuardsUnlimited',
    title: "Samples - Safeguards",
    group: 'Cohort Secondary Shares',
    buildLoopName: function (states, globals, loops) {
        return "dataset_info_loop#dataset_transfer_processing101112$" + loops['dataset_info_loop'].index;
    },
    buildFields: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {cohortName, partyNameI} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        return [{
            label: "What are the safeguards or derogations for the sharing of samples from cohort '" + cohortName + "' by '" + partyNameI + "' with external parties outside the EEA?",
            type: "dropdown",
            options: buildSafeGuardsOptions("partySampleSafeGuardsUnlimited")
        }]
    },
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const safeguards = states[partySampleSafeGuardsUnlimited.id].values[0];
        const {datasetIndex, partyIndexI} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        globals['SampleSafeguard'][datasetIndex][partyIndexI]['any country or international organisation'] = safeguards;
        return datasetTransfersProcessing101112EndLoop;
    }
});

const partySampleSafeGuardsIO = new ItemDefinition({
    id: 'partySampleSafeGuardsIO',
    title: "Samples - Safeguards",
    group: 'Cohort Secondary Shares',
    buildLoopName: function (states, globals, loops) {
        return "dataset_info_loop#dataset_transfer_processing101112$" + loops['dataset_info_loop'].index;
    },
    buildFields: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {cohortName, partyNameI} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        return [{
            label: "What are the safeguards or derogations for the sharing of samples from cohort '" + cohortName + "' by '" + partyNameI + "' outside the EEA?",
            type: "dropdown",
            options: buildSafeGuardsOptions("partySampleSafeGuardsIO")
        }];
    },
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const safeguards = states[partySampleSafeGuardsIO.id].values[0];
        const {datasetIndex, partyIndexI} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        globals['SampleSafeguard'][datasetIndex][partyIndexI]['International organisations'] = safeguards;
        return datasetTransfersProcessing101112EndLoop;
    }
});

const partySampleSafeGuardsPS = new ItemDefinition({
    id: 'partySampleSafeGuardsPS',
    title: "Samples - Safeguards",
    group: 'Cohort Secondary Shares',
    buildLoopName: function (states, globals, loops) {
        return "dataset_info_loop#dataset_transfer_processing101112$" + loops['dataset_info_loop'].index;
    },
    buildFields: function (states, globals, loops) {
        const fields = [];
        const loopIndex = this.getLoopIndex(loops);
        const {cohortName, partyNameI} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        const selectedCountries = states[partySampleCountriesConsidered.id].values[0];
        for (let i = 0; i < selectedCountries.length; i++) {
            const selectedCountry = selectedCountries[i];
            const isEu = COUNTRIES[selectedCountry].EEA;
            const isAD = COUNTRIES[selectedCountry].AD;
            if (isEu || isAD) {
                continue;
            }
            const countryLabel = COUNTRIES[selectedCountry].label;
            fields.push({
                label: "What are the chosen safeguards or derogations that '" + partyNameI + "' should use to share samples of cohort '" + cohortName + "' with " + countryLabel,
                type: "dropdown",
                options: buildSafeGuardsOptions("partySafeGuardsPS" + i)
            });
        }
        return fields;
    },
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        const {datasetIndex, partyIndexI} = getNamesForTransfersLoop(loopIndex, states, globals, loops);
        const safeguards = states[partySampleSafeGuardsPS.id].values;
        const selectedCountries = states[partySampleCountriesConsidered.id].values[0];
        let j = 0;
        for (let i = 0; i < selectedCountries.length; i++) {
            const selectedCountry = selectedCountries[i];
            const isEu = COUNTRIES[selectedCountry].EEA;
            const isAD = COUNTRIES[selectedCountry].AD;
            if (isEu || isAD) {
                continue;
            }
            globals['SampleSafeguard'][datasetIndex][partyIndexI][selectedCountry] = safeguards[j];
            j++;
        }
        return datasetTransfersProcessing101112EndLoop;
    }
});

// end samples
const datasetEndLoop = new ItemDefinition({
    id: 'datasetEndLoop',
    title: "datasetEndLoop",
    loopName: "dataset_info_loop",
    isDecisionNode: true,
    chooseNext: function (states, globals, loops) {
        const loopIndex = this.getLoopIndex(loops);
        if (loopIndex === getNumberOfDatasets(states)) {
            return duration;
        } else {
            return this.nextLoopIteration(loops);
        }
    }
});


const duration = new ItemDefinition({
    id: 'duration',
    title: "Starting date and duration",
    group: 'Term and Termination',
    fields: [
        {
            label: "Please specify the duration of the agreement in months",
            type: "text"
        },
        {
            label: "At what date will be the earliest time of an access to or transmission of data or samples",
            type: "date"
        },
    ],
    chooseNext: function () {
        return extensionForeseen;
    }
});

const extensionForeseen = new ItemDefinition({
    id: 'extensionForeseen',
    title: "Extension",
    group: 'Term and Termination',
    fields: [
        {
            label: "Extension foreseen?",
            type: "radio",
            options: [
                {key: "extensionForeseen-automatic", value: 'automatic', text: "automatic"},
                {key: "extensionForeseen-optional", value: 'optional', text: "optional"},
                {key: "extensionForeseen-no", value: 'no', text: "no"},
            ]
        }
    ],
    chooseNext: function (states, globals) {
        const extension = states[extensionForeseen.id].values[0];
        globals['Extension'] = extension;
        if (extension === 'automatic') {
            return extensionForeseenAutomatic;
        } else {
            return jurisdiction;
        }
    }
});

const extensionForeseenAutomatic = new ItemDefinition({
    id: 'extensionForeseenAutomatic',
    title: "Extension details",
    group: 'Term and Termination',
    fields: [
        {
            label: "Duration of extension in months",
            type: "text",
        },
        {
            label: "Notification period in months in case of automatic extension is ended?",
            type: "text",
        }
    ],
    chooseNext: function (states, globals) {
        const {values} = states[extensionForeseenAutomatic.id];
        globals['Extension_duration'] = values[0];
        globals['Notification_period'] = values[1];
        return jurisdiction;
    }
});
const jurisdiction = new ItemDefinition({
    id: 'jurisdiction',
    title: 'Jurisdiction',
    group: 'Jurisdiction',
    fields: [
        {
            label: "By default, this contract is set to Belgium as applicable law and Brussels as the relevant court.\n" +
                "You can change these settings by specifying below your preferred option.",
            type: "noInput",
        }, {
            label: "Which law governs this agreement?",
            type: "text",
            defaultValue: "Belgium"
        },
        {
            label: "The applicable courts are the judicial districts of?",
            type: "text",
            defaultValue: "Brussels"
        }
    ],
    chooseNext: function (states, globals) {
        const jurisdictionValues = states[jurisdiction.id].values;
        globals['JurisdictionCountry'] = jurisdictionValues[1] || this.fields[1].defaultValue;
        globals['JurisdictionDistrict'] = jurisdictionValues[2] || this.fields[2].defaultValue;
        return annexes;
    }
});

const annexes = new ItemDefinition({
    id: 'annexes',
    title: "Annexes",
    group: 'Annexes',
    buildFields: function (states, globals, loops) {
        let defaultOptions = [
            {key: "annexes-c", text: "Consent templates", value: "AnnexConsentTemplate"},
            {key: "annexes-e", text: "Ethics approvals", value: "AnnexEthicsApproval"}
        ];
        const pop = globals['POP'];


        let {anySU, anyPROrMU} = anySUPRMU(states, globals);
        if (pop !== 'DH') {
            defaultOptions.push({
                key: "annexes-u",
                text: "List of data users (all people with access to the data)",
                value: "AnnexDataUsers"
            });
        }
        if (anySU) {
            defaultOptions.push({
                key: "annexes-ssp",
                text: "List of authorised sub-subprocessors",
                value: "AnnexSubprocessor"
            });
        } else if (anyPROrMU) {
            defaultOptions.push({
                key: "annexes-sp",
                text: "List of authorised subprocessors",
                value: "AnnexSubprocessor"
            });
        }
        let fields = [
            {
                label: "If any of the following documents are foreseen as annex, please select them.",
                type: "dropdown",
                multiple: true,
                optional: true,
                options: defaultOptions
            }
        ];
        const nbParties = getNumberOfParties(states);
        if (nbParties > 2 && pop !== 'DH') {
            fields = fields.concat([
            {
                type: "noInput",
                label: "Options for large projects:"
            },
            {
                label: "Include the description of the data types and the parties involved in the sharing " +
                    "of data in an annex instead of in the core text of the agreement",
                type: "checkbox"
            },
            {
                label: "Include the conditions applying to data and samples at contract termination in an annex" +
                    " instead of in the core text of the agreement",
                type: "checkbox"
            }]);
        }
        return fields;
    },
    chooseNext(states, globals) {
        const annexesValues = states[annexes.id].values[0];
        globals['AnnexConsentTemplate'] = 0;
        globals['AnnexEthicsApproval'] = 0;
        globals['AnnexDataUsers'] = 0;
        globals['AnnexSubprocessor'] = 0;
        const nbParties = getNumberOfParties(states);
        if (nbParties > 2) {
            globals['AnnexDataDefinitions'] = states[annexes.id].values[2];
            globals['AnnexTermination'] = states[annexes.id].values[3];
        } else {
            globals['AnnexDataDefinitions'] = false;
            globals['AnnexTermination'] = false;
        }
        if (annexesValues && annexesValues.indexOf('AnnexConsentTemplate') !== -1) {
            globals['AnnexConsentTemplate'] = 1;
        }
        if (annexesValues && annexesValues.indexOf('AnnexEthicsApproval') !== -1) {
            globals['AnnexEthicsApproval'] = 1;
        }
        if (annexesValues && annexesValues.indexOf('AnnexDataUsers') !== -1) {
            globals['AnnexDataUsers'] = 1;
        }
        if (annexesValues && annexesValues.indexOf('AnnexSubprocessor') !== -1) {
            globals['AnnexSubprocessor'] = 1;
        }
        return popAPDH;
    }
});

const popAPDH = new ItemDefinition({
    id: 'popAPDH',
    title: "popAPDH",
    isDecisionNode: true,
    chooseNext(states, globals) {
        if (globals['POP'] === 'AP' || globals['POP'] === 'DH') {
            return projectInfo;
        } else {
            return finalInformationProvision;
        }
    }
});

const projectInfo = new ItemDefinition({
    id: 'projectInfo',
    title: "Project Information",
    group: 'Confidentiality',
    fields: [
        {
            label: "Should information on the user of the data (Principal Investigator) be published by the data hub?",
            type: "yesno"
        }
    ],
    chooseNext() {
        return finalInformationProvision;
    }
});

const finalInformationProvision = new ItemDefinition({
    id: 'finalInformationProvision',
    title: 'Final information provision',
    group: 'Annexes',
    buildFields: function (states, globals, loops) {
        const fields = [];
        const annexes = [];
        const numberOfParties = getNumberOfParties(states);
        const numberOfDatasets = getNumberOfDatasets(states);
        const pop = globals['POP'];

        // ANNEX Contacts

        annexes.push({
            explanation: 'This annex lists the contact persons of the partners for legal, scientific and data' +
                ' protection issues related to the agreement. In particular, this annex lists the required ' +
                'unique contact point for data subjects',
            name: 'Contacts'
        })
        // ANNEX Cohorts definitions
        if (globals['AnnexDataDefinitions']) {
            annexes.push({
                explanation: 'This annex will be generated by Dawid and describes the data' +
                    ' types and the parties involved in the data sharing.',
                name: 'DATA definitions'
            });
        }
        // ANNEX Termination
        if (globals['AnnexTermination']) {
            annexes.push({
                explanation: 'This annex will be generated by Dawid and describes the conditions applying to data' +
                    ' and samples at the termination of the agreement.',
                name: 'Termination conditions'
            });
        }
        // ANNEX_Project
        if (['SP', 'DH'].indexOf(pop) === -1) {
            annexes.push({
                explanation: 'This annex needs to be provided by the partners to the agreement and describes the planned project.',
                name: 'Project description'
            });
        }
        let anyPR = false;
        let sccParties = [];
        for (let i = 0; i < numberOfParties; i++) {
            // ANNEX_DISH_character
            if (globals['Resp'][i]['P'] === 'PR' || globals['Resp'][i]['G'] === 'PR' || globals['Resp'][i]['A'] === 'PR' || globals['Resp'][i]['R'] === 'PR') {
                anyPR = true;
            }
            if (globals['SG'][i] && globals['SG'][i]['CC'] === true) {
                sccParties.push(getPartyName(i, loops));
            }
        }
        let cohort_or_cohorts = numberOfDatasets === 1 ? 'cohort' : 'cohorts. Each cohort needs to be described separately';
        annexes.push({
            explanation: 'This annex characterises the ' + cohort_or_cohorts + '. A template for such characterisation can be found under [this link](' + dish_template + ').',
            name: 'DATA information'
        });
        // ANNEX_Use-conditions
        annexes.push({
            explanation: 'This annex gives the general use conditions and data policy for the ' + cohort_or_cohorts + '. A template for such characterisation can be found under [this link](' + use_conditions_template + ')',
            name: 'Data use conditions'
        });
        // ANNEX_USERS
        if (globals['AnnexDataUsers'] === 1) {
            annexes.push({
                explanation: 'This annex lists the users with access to the data of the respective cohort.',
                name: 'Users'
            });
        }
        cohort_or_cohorts = numberOfDatasets === 1 ? 'cohort' : 'cohorts';
        if (globals['AnnexConsentTemplate'] === 1) {
            annexes.push({
                explanation: 'This annex provides the consent template used for the ' + cohort_or_cohorts + '.',
                name: 'Consent'
            });
        }
        // ANNEX_CONSENT
        if (globals['AnnexEthicsApproval'] === 1) {
            annexes.push({
                explanation: 'This annex comprises all relevant ethics approval documents.',
                name: 'Ethics'
            });
        }
        // ANNEX_Service
        let {anySU, anyPROrMU} = anySUPRMU(states, globals);
        if (['SP', 'DH'].indexOf(pop) !== -1 || anyPR) {
            const explanation = 'This annex needs to be provided by the partners to the agreement and describes' +
                ' all the processing of the data to be performed by a data processor.' +
                ' A template for such description can be found under [this link](' + service_template + ').'
            annexes.push({
                explanation,
                name: 'Services'
            });
        }
        // ANNEX_Subprocessing
        if (globals['AnnexSubprocessor'] === 1) {
            const explanationSubProcessor = 'This annex comprises the list of agreed sub-subprocessors that can be engaged by the subprocessor.';
            const explanationOtherCases = 'This annex comprises the list of agreed subprocessors that can be engaged by the processor.';
            const explanation = anySU ? explanationSubProcessor : explanationOtherCases;
            annexes.push({
                explanation,
                name: 'Subprocessing'
            });
        }
        // ANNEX_Security
        if (['SP', 'DH'].indexOf(pop) !== -1 || anyPROrMU) {
            const explanationSubProcessor = 'This annex needs to be provided by the Subprocessor and describes the organisational and technical measures that ensures an appropriate level of security for the data processing.';
            const explanationOtherCases = 'This annex needs to be provided by any partner acting as Processor and describes the organisational and technical measures that ensures an appropriate level of security for the data processing.';
            const explanation = anySU ? explanationSubProcessor : explanationOtherCases;
            annexes.push({
                explanation,
                name: 'Security'
            });
        }
        // ANNEX_StandardContractualClauses
        if (sccParties.length > 0) {
            const partyOrPartiesRequires = sccParties.length === 1 ? 'party requires' : 'parties require'
            const explanation = `This annex needs to be provided as the following ${partyOrPartiesRequires} standard contractual
             clauses: ${sccParties.join()}. Text to be compiled by the parties in compliance with the modular
              clauses provided by the European Commission`
            annexes.push({
                explanation,
                name: 'Standard Contractual Clauses'
            });
        }
        fields.push({
            label: 'The agreement will require the following annexes:',
            type: 'noInput'
        });
        for (let i = 0; i < annexes.length; i++) {
            fields.push({
                label: '- **' + annexes[i].name + '**: ' + annexes[i].explanation,
                type: 'noInput'
            })
        }
        return fields;
    },
    chooseNext: function () {
        return setFinalVariables;
    }
});


const setFinalVariables = new ItemDefinition({
    id: 'setFinalVariables',
    title: "some additional variables",
    isDecisionNode: true,
    chooseNext: function (states, globals) {
        const pop = globals['POP'];
        if (['OP', 'JC', 'CA'].indexOf(pop) === -1) {
            return theEnd;
        }
        const nbParties = getNumberOfParties(states);
        const nbDatasets = getNumberOfDatasets(states);
        [globals['HomogeneousSampleFate'], globals['HomSampleFateValue']] = computeHomogeneousSampleFate(globals, nbParties, nbDatasets);

        [globals['HomogeneousKeepData'], globals['HomDataKeepValue']] = computeHomogeneousKeepData(globals, nbParties, nbDatasets);
        if (globals['HomogeneousKeepData'] === true) {
            if (globals['HomDataKeepValue'] === true) {
                [globals['HomogeneousPurposeKeep'], globals['HomPurpKeepValue']] = computeHomogeneousVariable(globals, nbParties, nbDatasets, 'PurposeKeep');
                [globals['HomogeneousTerminateKeep'], globals['HomTermKeepValue']] = computeHomogeneousVariable(globals, nbParties, nbDatasets, 'TerminateKeep');
                if (globals['HomTermKeepValue'] === false) {
                    [globals['HomogeneousFateTermination'], globals['HomFateTermValue']] = computeHomogeneousVariable(globals, nbParties, nbDatasets, 'FateTermination');
                }
            } else {
                [globals['HomogeneousFate'], globals['HomFateValue']] = computeHomogeneousFate(globals, nbParties, nbDatasets);
            }
        }
        return theEnd;
    }
});


const theEnd = new ItemDefinition({
    id: 'theEnd',
    title: "THE END",
    group: 'Contract generation',
    fields: [],
    chooseNext: function () {
        return theEnd;
    }
});

const items = [
    pop,
    datasetDatatypes,
    datasetAccessDecision,
    datasetAccessNoDownload,
    datasetAccessNoDownloadPartners,
    datasetAccessNoDownloadPartnersTypes,
    datasetDetails,
    datasetEndLoop,
    datasetGeneration,
    datasetGenerationDecision,
    datasetGenerator,
    datasetGeneratorAndSharingDecision,
    datasetGenRespMU,
    datasetGenTypes,
    datasetKeepDataDecision,
    datasetParticipantRecruitedUnderAgreement,
    datasetPkAdditionalConditional,
    datasetPkAdditionalConditionalSpecify,
    datasetPkBackShare,
    datasetPkConsentPurposeKeep,
    datasetPkConsentPurposeKeepConditions,
    datasetPkConsentPurposeKeepConditionsSpecify,
    datasetPkConsentPurposeKeepSpecify,
    datasetPkFate,
    datasetPkIndependentSharing,
    datasetPkKeepMinimum,
    datasetPkNotGeneratorOrSharingFate,
    datasetPkPurposeKeep,
    datasetPkPurposeKeepConditions,
    datasetPkPurposeKeepConditionsSpecify,
    datasetPkPurposeKeepSpecify,
    datasetPkReducedDataset,
    datasetPkSampleFate,
    datasetPkUseConditions,
    datasetProvData,
    datasetProvDataDecision,
    datasetProvider,
    datasetProvRespMU,
    datasetProvTypes,
    datasetRecruitmentBy,
    datasetDatatypesRecruitersSame,
    datasetRecruitMU,
    datasetResponsiblePiAndInstitution,
    datasetSampleFateTrigger,
    datasetSampleTerminateFate,
    datasetSampleTerminate,
    datasetTransferProcessing10,
    datasetSamples,
    datasetSamplesAgreement,
    datasetSamplesAgreementSpecify,
    datasetSamplesProviders,
    datasetSamplesProvidersDec,
    datasetSamplesProvidersRoles,
    datasetSamplesReceivers,
    datasetSamplesReceiversRoles,
    datasetSamplesUsePartiesEndLoop,
    datasetsSecondarySharingDatatypes,
    datasetSharingController,
    datasetSharingToControllerKeep,
    datasetSharingToControllerLimited,
    datasetSharingToKeep,
    datasetSharingToLimited,
    datasetsSharingTo,
    datasetsTransferredDatatypes,
    datasetSecondarySharingConditions,
    datasetTransferNotProcessing10,
    datasetTransfersProcessing101112EndLoop,
    datasetTransfersNotProcessing10EndLoop,
    datasetTransfersDec,
    datasetTransfersNext,
    datasetScopeUseDecision,
    numberOfDatasets,
    numberOfParties,
    partyADCountry,
    partyAllSamplesOutsideEu,
    partyAllSamplesOutsideEuSpecify,
    partyAllShare,
    partyAllSharedOutsideEu,
    partyCountriesConsidered,
    datasetSecondarySharingDAC,
    partyDecisionAPOPDH,
    partyDecisionRoleRorGNotEU,
    partyDecisionSafeguards,
    partyDetails,
    partyDPODetails,
    partyDetailsNextDecision,
    partyRolesNextDecision,
    partyEndLoop,
    partyEuCountry,
    partyEuSubs,
    partyEuSubsCountriesList,
    partyExtShareDAC,
    partyExtShareDacDescription,
    partyExtShareData,
    partyInatDataToBeShared,
    partyInatSamplesShare,
    partyInatShare,
    partyInatShareDecision,
    partyIOAD,
    partyOrganisationOther,
    partyProcessingDecision,
    partyProcessingList,
    partyProcessingListDecision,
    partyProcessingListResp,
    partyProcessingListSpecify,
    partyPurposeShare,
    partyRolePGR,
    partyRolePGRR2,
    partyRolePGRR3,
    partyRolePGRR4,
    partyRolePGRRespG,
    partyRolePGRRespP,
    partyRolePGRRespR,
    partyRolePGRRespA,
    partyRolePR,
    partyRoleRorA,
    partySafeGuardsIO,
    partySafeGuardsPS,
    partySafeGuardsUnlimited,
    partySamplesInvolved,
    partySecondaryDecision,
    partySecondaryWithin,
    partyShareOutside,
    partyShareWithTypes,
    partyShareWithTypesAP,
    partyShareWithTypesCA,
    partyShareWithTypesNA,
    partyShareWithTypesNANames,
    partyTransferLimited,
    partyTransferWithoutLimitations,
    partyTypesOfCountries,
    partySampleSafeGuardsUnlimited,
    partySampleSafeGuardsIO,
    partySampleSafeGuardsPS,
    partySampleCountriesConsidered,
    partySampleTypesOfCountries,
    partySampleTransferLimited,
    partySampleTransferWithoutLimitations,
    partyTransferSamplesSameData,
    projectName,
    duration,
    extensionForeseen,
    extensionForeseenAutomatic,
    terminateKeep,
    terminateFateTermination,
    terminateKeepMinimum,
    terminateFateTermination2,
    terminateReducedDataset,
    annexes,
    popAPDH,
    projectInfo,
    setFinalVariables,
    finalInformationProvision,
    terminateKeepDecision,
    afterEarlyTerminationDecision,
    backShareDecision,
    theEnd,
    datasetProcessing10Decision,
    datasetTransferProcessing10Transfer,
    datasetTransfersIntro,
    datasetTransferProcessing101112,
    datasetTransferProcessing1112Decision,
    datasetsTransferredDatatypes10,
    datasetAllowedKeepDecision,
    datasetsSecondarySharingDatatypesEach,
    datasetPkFateArchiveDuration,
    datasetTransferAccessDecision,
    partyCAPipeda,
    datasetSoleController,
    datasetControllersList,
    partyExternalSoleController,
    partyExternalControllersList,
    jurisdiction
];


export default items;
